import { DownloadOutlined, MenuOutlined, SettingOutlined, UnorderedListOutlined } from "@ant-design/icons";
import { Button, Checkbox, Col, Empty, FormInstance, Input, Popover, Row, Table } from "antd/es";
import { SizeType } from "antd/es/config-provider/SizeContext";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { Guid } from "guid-typescript";
import _ from "lodash";
import moment from "moment";
import objectPath from "object-path";
import React, { PureComponent, ReactNode } from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import ReactDOM from "react-dom";
import isEqual from "react-fast-compare";
import shallowequal from "shallowequal";
import CarouselPlaceholder from "../../../../public/images/carousel-placeholder.svg";
import EmptyDataSvg from "../../../../public/images/empty-table.svg";
import FlexGridPlaceholder from "../../../../public/images/flex-grid-placeholder.svg";
import HorizontalGridPlaceholder from "../../../../public/images/horizontal-grid-placeholder.svg";
import { TableConfigService } from "../../../services/table-config-service";
import { formatDateValueByFormatter } from "../../../shared/utilty/date-value-formatter";
import DynamicStyleManager from "../../../shared/utilty/dynamic-style-manager";
import { KuikaAppManager } from "../../../shared/utilty/kuika-app-manager";
import { KMainFunctions } from "../../../shared/utilty/main-functions";
import { isAmapProvider } from "../../../shared/utilty/map-provider-config";
import { getCachedNumberFormat } from "../../../shared/utilty/number-format-cache";
import { CommonProps } from "../common/common-props";
import { Image } from "../image/image";
import { DashboardState, toCamelCase } from "../kuika-cl-model-runtimes";
import { AmapBodyWrapper } from "./body/amap/amap-body-wrapper";
import { CarouselBodyCell } from "./body/carousel/carousel-body-cell";
import { CarouselBodyWrapper, CarouselBodyWrapperProps } from "./body/carousel/carousel-body-wrapper";
import { CarouselItem } from "./body/carousel/carousel-item";
import { FlexGridBodyCell } from "./body/flex-grid/flex-grid-body-cell";
import { FlexGridBodyWrapper } from "./body/flex-grid/flex-grid-body-wrapper";
import { FlexGridItem } from "./body/flex-grid/flex-grid-item";
import { GalleryBodyCell } from "./body/gallery/gallery-body-cell";
import { GalleryBodyWrapper, GalleryBodyWrapperProps } from "./body/gallery/gallery-body-wrapper";
import { GalleryItem, GalleryItemProps } from "./body/gallery/gallery-item";
import { GoogleMapsBodyCell } from "./body/google-maps/google-maps-body-cell";
import { GoogleMapsBodyWrapper, GoogleMapsBodyWrapperProps } from "./body/google-maps/google-maps-body-wrapper";
import { GoogleMapsItem, ICoords, MarkerDetailPosition, MarkerDetailView } from "./body/google-maps/google-maps-item";
import { TableBodyCell } from "./body/table/table-body-cell";
import { TableBodyWrapper } from "./body/table/table-body-wrapper";
import { TableColumnProps } from "./body/table/table-column";
import CustomFilterDropdown from "./custom-table-filter-dropdown";
import { TableData, TableDataProps } from "./data/table-data";
import { TableHeaderCell } from "./header/table-header-cell";
import { TableHeaderRow } from "./header/table-header-row";
import { RefreshDelegate } from "./refresh-delegate";
import {
  CellBgColorFn,
  matchesColorFilter,
  matchesValueFilter,
  readRowField,
  TableColumnFilter
} from "./table-filter-helpers";
import "./table.scss";
import { CarouselViewWrapper } from "./wrapper/carousel-view-wrapper";
import { FlexGridViewWrapper } from "./wrapper/flex-grid-view-wrapper";
import { GalleryViewWrapper } from "./wrapper/gallery-view-wrapper";
import { GoogleMapsViewWrapper } from "./wrapper/google-maps-view-wrapper";
import { TableViewWrapper } from "./wrapper/table-view-wrapper";

export type ViewMode = "table" | "gallery" | "carousel" | "flex-grid" | "google-maps";

export type TableCellValue = string | number | boolean | Date | object | null | undefined;
export type TableRowRecord = Record<string, TableCellValue>;

// A built column config carrying the dynamic props setRow stamps on it that the
// editor-field sync (KOA-17691) reads: the bound field and, for editable columns, the
// edit-mode editor's Form.Item field name.
interface EditorFieldColumn {
  editorFieldName?: string;
  dataValue?: string;
}

// The edge a column is pinned to, as antd understands it.
export type FixedEdge = "left" | "right" | undefined;

// Minimal shape the pinned-column ordering rules need — satisfied by a plain column, a
// grouped child column and an antd column-group entry alike.
export interface IFixedEdgeColumn {
  fixed?: FixedEdge;
}

// One rendered cell of a row, as the pinned-offset measurement sees it.
export interface PinnedCellMeasurement {
  key: string | null;
  edge: FixedEdge;
  width: number;
}

// The sticky offset a pinned column must be placed at, in px from its edge.
export interface PinnedColumnOffset {
  key: string;
  edge: "left" | "right";
  offset: number;
}

// A grouped header cell and the rendered-column keys of its visible children, in order.
export interface GroupChildKeys {
  key: string;
  childKeys: string[];
}

// The insert row's record intentionally keeps its key field `undefined` (so generated insert
// actions receive a null id and let the database assign one), but antd derives `data-row-key`
// from that field — an undefined key would leave the <tr> without the attribute every
// DOM-to-record lookup depends on. This sentinel is what the insert row renders as its key.
export const KUIKA_INSERT_ROW_KEY = "kuika-insert-row";

// A `[datafield:X]` binding found on a column's child element together with the display
// formatter that element renders with. The Excel export replays the formatter on the raw
// record value so exported cells match the on-screen format (KOA-17951).
interface DataBindedFieldFormatter {
  binding: string;
  formatter?: string;
  customFormatter?: string;
}

// The subset of a data-bound child element's props the formatter collection reads.
interface DataBindedElementProps {
  formatter?: string;
  customFormatter?: string;
}

declare let window: any;

// A multiselect table nested inside another table's column is rendered once per parent row,
// so all of its instances share a single React ref and — because the outer table re-renders
// on unrelated state changes (e.g. a spinner toggle) — each instance is remounted, which would
// otherwise reset its checkbox selection to empty and make `*.selectedValues` read the wrong
// (last-mounted) instance. This module-level store keeps each nested instance's selection keyed
// by its parent row so it survives remounts, and tracks the most recently changed row so reads
// of `selectedValues` resolve to the instance the user actually interacted with.
interface NestedTableSelectionEntry {
  activeRowKey: string;
  selectionsByRowKey: Record<string, string>;
}
const nestedTableSelectionStore: Map<string, NestedTableSelectionEntry> = new Map();

export enum StringFilterTypes {
  Contains = "contains",
  DoesNotContain = "does_not_contain",
  Equals = "equals",
  DoesNotEqual = "does_not_equal",
  BeginsWith = "begins_with",
  EndsWith = "ends_with",
  Blank = "blank",
  NotBlank = "not_blank"
}

export enum NumberFilterTypes {
  Equals = "number_equals",
  DoesNotEqual = "number_does_not_equal",
  GreaterThan = "greater_than",
  LessThan = "less_than",
  GreaterThanOrEqual = "greater_than_or_equal",
  LessThanOrEqual = "less_than_or_equal",
  Between = "number_between",
  Blank = "number_blank",
  NotBlank = "number_not_blank"
}

export enum DateFilterTypes {
  Equals = "date_equals",
  DoesNotEqual = "date_does_not_equal",
  After = "after",
  Before = "before",
  Between = "date_between",
  Blank = "date_blank",
  NotBlank = "date_not_blank"
}

export enum BooleanFilterTypes {
  Equals = "boolean_equals",
  DoesNotEqual = "boolean_does_not_equal",
  Blank = "boolean_blank",
  NotBlank = "boolean_not_blank"
}

export enum ValueType {
  String = "string",
  Number = "number",
  Date = "date",
  Boolean = "boolean"
}

export enum FooterOptions {
  None = "none",
  Average = "average",
  Sum = "sum",
  Min = "min",
  Max = "max",
  Count = "count"
}

export interface ITableProps {
  children?: any;
  nodatafoundmessage?: string;
  rowBgColor?: (rowData: any, rowIndex?: number) => string;
  rowFontColor?: (rowData: any, rowIndex?: number) => string;
  dataSource?: any[];
  loading?: boolean;
  size?: SizeType;
  sorter?: boolean;
  pagination?: boolean;
  showHeader?: boolean;
  style?: any;
  mode?: ViewMode;
  striped?: boolean;
  stripedColor?: string;
  hideHeaderOnMobileResolution?: boolean;
  // Maps Props
  onChange?: (value?: any | undefined) => void;
  value?: any;
  latitude?: string;
  longitude?: string;
  zoom?: number; /// zoom /// 1-> World /// 5-> Landmass/continent 10 -> City /// 15-> Streets /// 20-> Buildings şeklinde tanımlanırsa veya açıklama yazılırsa low code için daha anlamlı olabilir.
  isMarkerPicker?: boolean;
  isPicker?: boolean;
  hasSearchBox?: boolean;
  disableDoubleClickZoom?: boolean;
  fullScreenControlV1?: "show" | "hide" | "user select" | boolean;
  streetViewControlV1?: "show" | "hide" | "user select" | boolean;
  scaleControlV1?: "show" | "hide" | "user select" | boolean;
  zoomControlV1?: "show" | "hide" | "user select" | boolean;
  panControl?: boolean;
  rotateControlV1?: "show" | "hide" | "user select" | boolean;
  cameraControl?: "show" | "hide" | "user select" | boolean;
  mapTypeControl?: boolean;
  autoCenter?: boolean;
  designClicked?: boolean;
  onClickDesign?: (value: boolean) => void;
  dataLatField?: string;
  dataLngField?: string;
  markerDetailView?: MarkerDetailView;
  markerDetailPosition?: MarkerDetailPosition;
  markerDetailHeight?: number;
  markerDetailWidth?: number;
  circleRadius?: number;
  groupPickerIcon?: string;
  groupPickerColor?: string;
  selectedLocationIcon?: string;
  selectedLocationColor?: string;
  currentLocationIcon?: string;
  currentLocationColor?: string;
  mapType?: "roadmap" | "terrain" | "satellite" | "hybrid";
  hideTrafficLayer?: boolean;
  hideTransitLayer?: boolean;
  enableLocationTracking?: boolean;
  waypoints?: any[];
  waypointLatField?: string;
  waypointLngField?: string;
  // Carousel Props
  setCurrentSlideNumber?: (val: number) => void;
  startingIndex?: number;
  hideTableOnEmpty?: boolean;
  rowHoverFontColor?: string;
  transformedOnMobileResolution?: boolean;
  rowHoverBgColor?: string;
  kuikacomponentname?: string;
  onRowEditFinished?: () => void;
  onInsertRowFinished?: () => void;
  form?: FormInstance<any>;
  insertRowActive?: boolean;
  insertRowPosition?: "top" | "bottom";
  noDataFoundImage?: string;
  filtering?: boolean;
  searchable?: boolean;
  tableWidthMode?: "fit" | "horizontal scroll" | "scroll";
  columnChooser?: boolean;
  editableAlwaysActive?: boolean;
  onConfigChange?: () => void;
  // Stable screen design id emitted by codegen; keys the per-user auto-persisted view config.
  // Auto-persist is gated on `columnChooser` (above) being enabled — no separate flag.
  screenId?: string;
  multiSelect?: boolean;
  selectedValues?: any;
  onSelectedValuesChange?: () => void;
  fixedHeader?: boolean;
  pageSize?: number;
  showSizeChanger?: boolean;
  options?: any[];
  onRowDoubleClick?: () => void;
  showNoDataFound?: boolean;
  resizableColumns?: boolean;
  selectedRowColor?: string;
  editable?: boolean;
  globalSearch?: boolean;
  export?: boolean;
  datavaluefield?: string;
  multiSelectScope?: "page" | "all";
  arrowColor?: string;
  shouldSearchOnChange?: boolean;
  searchDelay?: number;
  rowKey?: string;
  paginationUnselectedTextColor?: string;
  paginationSelectedTextColor?: string;
  showPageIndicator?: boolean;
  exportButtonBgColor?: string;
  exportFileName?: string;
  showRowNumbers?: boolean;
}

export interface IColumnGroup {
  title: string;
  children: TableColumnProps[];
  style?: React.CSSProperties;
  groupBackgroundColor?: string;
  groupFontColor?: string;
  groupFontSize?: number;
  groupFontWeight?: string;
}

export interface ITableState {
  columnTitles?: string[];
  selectedRowKeys: any;
  selectedRowKeysString: any;
  columns?: TableColumnProps & CommonProps[];
  columnGroups?: IColumnGroup[];
  headerStyle?: any;
  rowStyle?: any;
  footerStyle?: any;
  headerRenderer?: any;
  rowRenderer?: any;
  selectedIndexGuid: string;
  selectedColumnIndexGuid: string;
  selectedRowDataGuid: string;
  // Maps States:
  maps?: any;
  map?: any;
  isDataMarkerSelected: boolean;
  value?: any;
  uniqueKey?: Guid;
  activeEditableRowIndex?: number;
  randomGuid?: string;
  searchText: string;
  columnSearchTexts?: { [key: string]: string };
  carouselStartingIndex?: number;
  hiddenColumnList?: any[];
  columnOrder?: any[];
  filteringOptions?: any;
  sorterOptions?: any;
  pageSize?: any;
  resizingColumnIndex?: number;
  columnWidths?: any;
  current: number;
  hideHeaderOnMobileResolution?: boolean;
  excludedFromExportList?: any[];
  actualRowKey?: string;
}

// Runtime-only export metadata stamped onto a column by `setRow`. `dataValue` is the single
// resolved [datafield:] binding; `hasDataBoundChildren` records whether ANY [datafield:]
// binding exists among the column's children — a cell can mix several different bindings
// (multi-label columns) and still export data without resolving to a single `dataValue`.
// KOA-17950.
export interface IExportColumnMeta {
  dataValue?: string;
  hasDataBoundChildren?: boolean;
}

export type ExportableColumn = TableColumnProps & CommonProps & IExportColumnMeta;

// `rawValue` stays unformatted so `canColumnExportData` can probe emptiness on the raw value
// (KOA-17950). `formatter`/`customFormatter` report the display formatter of the binding that
// produced the value, letting the row loop render date cells like the screen does (KOA-17951).
export interface IExportCellResolution {
  rawValue: unknown;
  hasMultipleLabels: boolean;
  formatter?: string;
  customFormatter?: string;
}

// Loose view over a rendered React tree node used by the export cell resolution — rendered
// content is walked structurally (props/children), never through React's typed element API.
interface IRenderedNodeProps {
  value?: unknown;
  children?: unknown;
  direction?: string;
}

interface IRenderedNode {
  props?: IRenderedNodeProps;
}

let isEventListenerAdded = false;

export abstract class BaseTable<P, S> extends PureComponent<ITableProps & P & CommonProps, ITableState & S> {
  private static readonly DEFAULT_PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

  // Design-time only. A table column bound to a datasource-action / current-record
  // field is rendered on the canvas with an `actionField:<field>` / `current:<field>`
  // placeholder (DesignSideElementGenerator.getPropertyDisplayValue) instead of the
  // runtime `[datafield:X]` token that React_UIGenerator emits. The column field
  // inference below only recognises `[datafield:X]`, so without this the canvas can't
  // resolve the column's data field and silently drops header search / sort / filter
  // even though the generated app shows them. Capture group 1 is the bare field path.
  // KOA-16234.
  private static readonly DESIGN_TIME_DATAFIELD_PLACEHOLDER: RegExp = /^(?:actionField|current):(.+)$/i;

  private uniqueKey: Guid;

  private style: DynamicStyleManager;

  private pinnedOffsetStyle: DynamicStyleManager;

  private randomGuid: string;

  private cachedColumnWidths = new Map<string, number>();

  private syncWidthsTimerId: ReturnType<typeof setTimeout> | undefined;

  timeOutId = undefined;

  lastSelectedLat: any;

  lastSelectedLng: any;

  resizingColumnIndexRef = React.createRef<number>();

  startX: number | undefined;

  initialWidth: number | undefined;

  nextFocusTarget: { columnIndex: number; rowIndex: string } | null = null;

  tableElement: HTMLElement | null = null;

  constructor(props: ITableProps & P) {
    super(props);
    const cleanedValues = this.props.selectedValues?.replace(/^,|,$/g, "");
    this.uniqueKey = Guid.create();
    this.style = new DynamicStyleManager(`ktable_${this.uniqueKey}`);
    this.pinnedOffsetStyle = new DynamicStyleManager(`ktable_${this.uniqueKey}_pinned`);
    this.randomGuid = Guid.create().toString();
    // Restore a nested instance's previously selected rows so a remount (triggered by the outer
    // table re-rendering) does not wipe the checkbox selection. Top-level tables get null here and
    // fall back to the selectedValues prop, preserving existing behaviour.
    const restoredSelection: string | null = this.restoreNestedSelection();
    const initialSelectionString: string = restoredSelection !== null ? restoredSelection : cleanedValues || "";
    this.state = {
      uniqueKey: this.uniqueKey,
      columns: [],
      // Keep the original case of the keys; all matching sites compare case-insensitively, so
      // lowercasing the stored keys only corrupts the case reported by selectedValues.
      selectedRowKeys: initialSelectionString ? initialSelectionString.split(",") : [],
      selectedRowKeysString: initialSelectionString,
      selectedIndexGuid: Guid.create().toString(),
      selectedColumnIndexGuid: Guid.create().toString(),
      selectedRowDataGuid: Guid.create().toString(),
      activeEditableRowIndex: window.kuika.isDesignTime ? 0 : -1,
      searchText: "",
      columnSearchTexts: {},
      carouselStartingIndex: 0,
      hiddenColumnList: [],
      columnOrder: [],
      filteringOptions: {},
      sorterOptions: {},
      pageSize: this.props.pageSize && this.props.pageSize >= 1 ? this.props.pageSize : 10,
      resizingColumnIndex: null,
      columnWidths: undefined,
      current: 1,
      excludedFromExportList: [],
      actualRowKey: BaseTable.resolveActualRowKey(this.props.dataSource, this.props.rowKey)
    } as any;
  }

  // Stable identity for a nested-table instance: its component id plus the parent row index that
  // `withColumnProps` injects when the table is rendered inside another table's column cell.
  // Returns null for top-level tables (no parent row), so they are unaffected.
  private getNestedSelectionContext(): { id: string; rowKey: string } | null {
    const id: string | undefined = this.props.id;
    const rowIndex: number | undefined = this.props.rowIndex;
    if (id == null || rowIndex === undefined || rowIndex === null) {
      return null;
    }
    return { id, rowKey: rowIndex.toString() };
  }

  // Persists a nested instance's current selection and marks its row as the active one, so a later
  // read of `selectedValues` returns the row the user just interacted with.
  private persistNestedSelection(selectionString: string): void {
    const context = this.getNestedSelectionContext();
    if (!context) {
      return;
    }
    let entry: NestedTableSelectionEntry | undefined = nestedTableSelectionStore.get(context.id);
    if (!entry) {
      entry = { activeRowKey: context.rowKey, selectionsByRowKey: {} };
      nestedTableSelectionStore.set(context.id, entry);
    }
    entry.selectionsByRowKey[context.rowKey] = selectionString;
    entry.activeRowKey = context.rowKey;
  }

  private restoreNestedSelection(): string | null {
    const context = this.getNestedSelectionContext();
    if (!context) {
      return null;
    }
    const entry: NestedTableSelectionEntry | undefined = nestedTableSelectionStore.get(context.id);
    if (!entry || !Object.prototype.hasOwnProperty.call(entry.selectionsByRowKey, context.rowKey)) {
      return null;
    }
    return entry.selectionsByRowKey[context.rowKey];
  }

  // Selection of the most recently changed nested instance, used so `value(..., "selectedValues")`
  // reflects the parent row the user interacted with rather than the (shared) last-mounted ref.
  public getActiveNestedSelectedValues(): string | null {
    const context = this.getNestedSelectionContext();
    if (!context) {
      return null;
    }
    const entry: NestedTableSelectionEntry | undefined = nestedTableSelectionStore.get(context.id);
    if (!entry) {
      return null;
    }
    return entry.selectionsByRowKey[entry.activeRowKey] ?? null;
  }

  public static defaultProps = {
    mode: "table",
    designClicked: true
  };

  static withColumnProps(props: any, value: any, rowData: any, rowIndex: number): any {
    props.value = value;
    props.rowData = rowData;
    props.rowIndex = rowIndex;
    return props;
  }

  static resolveActualRowKey(dataSource: any[], rowKey: string): string {
    if (!dataSource || dataSource.length === 0 || !rowKey || typeof dataSource === "string") {
      return rowKey || "id";
    }

    const sampleRecord = dataSource?.find((record) => record && typeof record === "object");
    if (!sampleRecord) {
      return rowKey || "id";
    }

    if (sampleRecord.hasOwnProperty(rowKey)) {
      return rowKey;
    }

    const actualKey = Object.keys(sampleRecord).find((key) => key.toLowerCase() === rowKey.toLowerCase());

    return actualKey || rowKey;
  }

  static findRowByKey(dataSource: any[], searchValue: string, keyFieldProp?: string): any {
    if (!dataSource) {
      return undefined;
    }

    const rowKeyField = BaseTable.resolveActualRowKey(dataSource, keyFieldProp || "id");
    let result = dataSource.find((item) => item[rowKeyField]?.toString() === searchValue);

    if (!result && rowKeyField !== "id") {
      result = dataSource.find((item) => item.id?.toString() === searchValue);
    }

    return result;
  }

  // True when the click landed on the Combobox container/arrow/search
  // input, but NOT on a dropdown option. Those clicks open or focus the
  // Combobox; the value lands later through onChange when an option is
  // picked.
  static isInsideAntSelectShell(target: HTMLElement | null | undefined): boolean {
    if (!target) return false;
    const option = target.closest?.(".ant-select-item-option");
    if (option) return false;
    return !!target.closest?.(".ant-select");
  }

  static getDerivedStateFromProps(nextProps: ITableProps & CommonProps, prevState: ITableState) {
    const result: Partial<ITableState> = {
      selectedIndexGuid: prevState.selectedIndexGuid,
      selectedColumnIndexGuid: prevState.selectedColumnIndexGuid,
      selectedRowDataGuid: prevState.selectedRowDataGuid,
      maps: prevState.maps,
      map: prevState.map,
      isDataMarkerSelected: prevState.isDataMarkerSelected,
      activeEditableRowIndex: prevState.activeEditableRowIndex,
      columnSearchTexts: prevState.columnSearchTexts,
      searchText: prevState.searchText,
      hiddenColumnList: prevState.hiddenColumnList,
      filteringOptions: prevState.filteringOptions,
      sorterOptions: prevState.sorterOptions,
      selectedRowKeys: prevState.selectedRowKeys,
      selectedRowKeysString: prevState.selectedRowKeysString,
      columnWidths: prevState.columnWidths,
      actualRowKey: BaseTable.resolveActualRowKey(nextProps.dataSource, nextProps.rowKey)
    };
    if (nextProps.children) {
      result.columns = [] as CommonProps[];
      if (nextProps.mode === "carousel") {
        result.rowStyle = nextProps.style;
        BaseTable.setCarouselItem(result, nextProps.children);
      } else if (nextProps.mode === "gallery") {
        result.rowStyle = nextProps.style;
        BaseTable.setGalleryItem(result, nextProps.children);
      } else if (nextProps.mode === "flex-grid") {
        result.rowStyle = nextProps.style;
        BaseTable.setFlexGridItem(result, nextProps.children);
      } else if (nextProps.mode === "google-maps") {
        if (nextProps.value !== undefined) {
          result.value = nextProps.value;
        }
        React.Children.forEach(nextProps.children, (child, childIndex) => {
          switch (childIndex) {
            // GoogleMapsMarker
            case 0:
              BaseTable.setGoogleMapsItem(result, child, nextProps);
              break;
          }
        });
      } else {
        React.Children.forEach(nextProps.children, (child, childIndex) => {
          const isHeader = nextProps?.children?.length > 1 && childIndex === 0;
          const isRow =
            (nextProps?.children?.length > 1 && childIndex === 1) || typeof nextProps?.children === "object";
          switch (childIndex) {
            // Header
            case 0:
              if (
                child.type.name == "TableHeader" ||
                (child.props.kuikacomponentname && child.props.kuikacomponentname === "TableHeader") ||
                child.type.name == "ReportTableHeader" ||
                child.type.name == "StaticTableHeader" ||
                (child.props.kuikacomponentname && child.props.kuikacomponentname === "ReportTableHeader") ||
                isHeader
              ) {
                BaseTable.setHeader(result, child, prevState, nextProps);
              } else if (
                child.type.name == "TableRow" ||
                (child.props.kuikacomponentname && child.props.kuikacomponentname === "TableRow") ||
                child.type.name == "ReportTableRow" ||
                child.type.name == "StaticTableRow" ||
                (child.props.kuikacomponentname && child.props.kuikacomponentname === "ReportTableRow") ||
                isRow
              ) {
                BaseTable.setRow(
                  result,
                  child,
                  nextProps,
                  nextProps.sorter,
                  nextProps.filtering,
                  nextProps.dataSource,
                  nextProps.style
                );
                result.headerRenderer = (_props: any) => {
                  return React.createElement("div");
                };
              }
              break;
            // Row
            default:
              BaseTable.setRow(
                result,
                child,
                nextProps,
                nextProps.sorter,
                nextProps.filtering,
                nextProps.dataSource,
                nextProps.style
              );
              break;
          }
        });
      }

      if (prevState.columnOrder?.length > 0) {
        const orderedColumns = prevState.columnOrder
          .map((orderId) => result?.columns?.find((col) => col.id === orderId))
          .filter(Boolean);

        const remainingColumns = result?.columns?.filter((col) => !prevState.columnOrder.includes(col.id));

        if (remainingColumns?.length > 0) {
          result.columns = [...orderedColumns, ...remainingColumns];
        } else {
          result.columns = orderedColumns;
        }

        if (result.columnGroups && result.columnGroups.length > 0) {
          result.columnGroups = result.columnGroups.map((group) => {
            const orderedChildren = prevState.columnOrder
              .map((orderId) => group.children.find((child) => child.id === orderId))
              .filter(Boolean);

            const remainingChildren = group.children.filter((child) => !prevState.columnOrder.includes(child.id));

            if (remainingChildren.length > 0) {
              return {
                ...group,
                children: [...orderedChildren, ...remainingChildren]
              };
            }
            return { ...group, children: orderedChildren };
          });
        }
      }

      return result;
    }
  }

  static setHeader = (result: Partial<ITableState>, child: any, prevState: any, nextProps: any) => {
    let headerStyle: any = {};

    if (child.props.style) {
      headerStyle = child.props.style;
    }

    if (child.props.writingMode && child.props.writingMode === "vertical") {
      headerStyle.writingMode = "vertical-lr";
    }

    if (
      child.props.textDirection &&
      (child.props.textDirection === "Rotate Up" || child.props.textDirection === "Rotate Down")
    ) {
      headerStyle.writingMode = "vertical-lr";
      if (child.props.textDirection === "Rotate Up") {
        headerStyle.transform = "rotate(180deg)";
      }
    }

    result.headerStyle = headerStyle;
    result.headerRenderer = (props: any) => {
      let _props = { ...props };

      _props.wrapperClassname = `.ktable_${prevState.uniqueKey.toString().substring(0, 8)}`;
      _props.transformedOnMobileResolution = nextProps.transformedOnMobileResolution;

      return React.cloneElement(child, _props);
    };

    if (child.props.columnTitles) {
      result.columnTitles = child.props.columnTitles.split(";");
    }
  };

  static setRow = (
    result: Partial<ITableState>,
    child: any,
    _nextProps: ITableProps,
    sorter?: boolean,
    filtering?: boolean,
    datasource?: any,
    tableStyle?: any
  ) => {
    result.rowStyle = child.props.style;
    result.rowRenderer = (props: any) => {
      return React.cloneElement(child, props);
    };

    if (!result.columnGroups) {
      result.columnGroups = [];
    }
    if (!result.columns) {
      result.columns = [];
    }

    React.Children.forEach(child.props.children, (grandChild, grandChildIndex) => {
      const columnProps: TableColumnProps & CommonProps = _.clone(grandChild.props);
      const props = {};
      Object.keys(grandChild.props).forEach((key) => {
        if (key !== "children" || typeof grandChild.props[key] === "string") {
          props[key] = grandChild.props[key];
        }
      });

      columnProps.render = (value: any, rowData: any, rowIndex: number) => {
        let children = grandChild?.props?.children;
        if (grandChild?.props?.children?.length === 2 && grandChild.props.isEditableColumn) {
          children = [];
          if (grandChild.props.isEditableColumn) {
            if (_nextProps.editableAlwaysActive) {
              children.push(grandChild.props.children[1]);
            } else if (result.activeEditableRowIndex === rowIndex) {
              children.push(grandChild.props.children[1]);
            } else {
              children.push(grandChild.props.children[0]);
            }
          }
        }
        return (
          <>{React.cloneElement(grandChild, BaseTable.withColumnProps(props, value, rowData, rowIndex), children)}</>
        );
      };

      delete columnProps.children;
      if (
        columnProps &&
        result &&
        result.columnTitles &&
        result.columnTitles.length > grandChildIndex &&
        result.columnTitles[grandChildIndex]?.length > 0
      ) {
        columnProps.title = result.columnTitles[grandChildIndex];
      }

      let value =
        grandChild.props?.children?.props?.value ??
        grandChild.props?.children?.props?.children?.props?.value ??
        grandChild.props?.children?.props?.children?.props?.children?.props?.value;
      if (grandChild.props?.children?.props?.className === "kFormItem" && !value?.toString().includes("[datafield:")) {
        value = undefined;
      }
      if (value === undefined && grandChild.props?.children && grandChild.props?.children.length > 0) {
        value =
          grandChild.props?.children[0]?.props?.value ?? grandChild.props?.children[0]?.props?.children?.props?.value;
      }
      const dataBindedChilds = [];
      // Aligned with dataBindedChilds: remembers which display formatter the data-bound child
      // element carries, so the Excel export can format cells like the rendered Label (KOA-17951).
      const dataBindedChildFormatters: DataBindedFieldFormatter[] = [];

      const collectDataBindedProps = (childProps: DataBindedElementProps): void => {
        Object.keys(childProps).forEach((key) => {
          if (
            childProps[key]?.toString().includes("[datafield:") &&
            key !== "visExField1" &&
            key !== "visExField2" &&
            key !== "tooltip" &&
            // KuikaEditableCell (KOA-13928) carries `rowValue`/`rowId` datafield
            // tokens purely to drive per-row display; they are not column content
            // bindings. Counting them here mixes the bound field with `id`, so the
            // column's data field resolves to "ambiguous" (undefined) — which drops
            // the column's dataType (→ string) and breaks the inline boolean save
            // path in setFields. Exclude them like the other non-content tokens.
            key !== "rowValue" &&
            key !== "rowId"
          ) {
            dataBindedChilds.push(childProps[key]);
            dataBindedChildFormatters.push({
              binding: childProps[key].toString(),
              formatter: childProps.formatter,
              customFormatter: childProps.customFormatter
            });
          }
        });
      };

      const processChildren = (children) => {
        if (Array.isArray(children)) {
          children.forEach((child) => {
            if (child.props?.children) {
              processChildren(child.props.children);
            }
            collectDataBindedProps(child.props);
          });
        } else if (children) {
          if (children.props?.children) {
            processChildren(children.props.children);
          }
          if (children.props) {
            collectDataBindedProps(children.props);
          }
        }
      };

      if (grandChild.props?.children?.props?.className !== "kFormItem") {
        processChildren(grandChild.props?.children);
      }

      if (value === undefined && grandChild.props?.children?.props?.className !== "kFormItem") {
        processChildren(grandChild.props?.children?.props?.children);
      } else if (value === undefined && grandChild.props?.children?.props?.children?.props.className !== "kFormItem") {
        processChildren(grandChild.props?.children?.props?.children?.props.children);
      }

      // Whether any [datafield:] binding exists among this column's children. Action/button-only
      // columns have none: their export cells always resolve empty, so the Excel export drops
      // them (KOA-17950). Kept separate from `dataValue`, which stays undefined when several
      // different bindings share the cell even though the export can still join their values.
      (columnProps as ExportableColumn).hasDataBoundChildren = dataBindedChilds.length > 0;

      if (
        dataBindedChilds.length === 1 ||
        (dataBindedChilds.length > 1 &&
          dataBindedChilds.every((dataBindedChild) => dataBindedChild === dataBindedChilds[0]))
      ) {
        value = dataBindedChilds[0];
      } else if (
        dataBindedChilds.length > 1 &&
        !dataBindedChilds.every((dataBindedChild) => dataBindedChild === dataBindedChilds[0])
      ) {
        value = undefined;
      }

      if (
        dataBindedChilds.length > 1 &&
        !dataBindedChilds.every((dataBindedChild) => dataBindedChild === dataBindedChilds[0]) &&
        columnProps.defaultDataField
      ) {
        value = columnProps.defaultDataField?.toLowerCase();
      }
      // In design-time a bound column carries an `actionField:`/`current:` placeholder
      // rather than `[datafield:X]`; keep it so the column still resolves a data field
      // (otherwise the rule below strips it and the canvas loses header search/sort/filter).
      // Runtime is unaffected — these placeholders only exist at design-time. KOA-16234.
      const isDesignTimeDataFieldPlaceholder: boolean =
        window.kuika?.isDesignTime === true &&
        BaseTable.DESIGN_TIME_DATAFIELD_PLACEHOLDER.test(value?.toString() ?? "");
      if (
        value &&
        !value?.toString().includes("[datafield:") &&
        !columnProps.defaultDataField &&
        !isDesignTimeDataFieldPlaceholder
      ) {
        value = undefined;
      }
      if (grandChild.props?.children?.props?.children?.props?.checked) {
        value = grandChild.props?.children?.props?.children?.props?.checked;
      }

      if (
        !value &&
        grandChild.props?.children &&
        grandChild.props?.children.length > 1 &&
        grandChild.props?.children?.some(
          (child) =>
            child?.props?.visibility === "hidden" &&
            (grandChild.props?.children?.some((child) => !child?.props?.visibility) ||
              grandChild.props?.children?.some((child) => child?.props?.visibility === "visible"))
        )
      ) {
        const visibleColumnValue =
          grandChild.props?.children?.find((child) => !child?.props?.visibility)?.props?.value ||
          grandChild.props?.children?.find((child) => child?.props?.visibility === "visible")?.props?.value;
        value = visibleColumnValue;
      }
      if (value || window.kuika.isDesignTime) {
        value = value?.toString()?.replace("[datafield:", "")?.replace("]", "");
        // Design-time only: reduce the `actionField:`/`current:` placeholder to the bare
        // field name so dataType inference and header search treat it like a real datafield
        // column. Runtime never sees these placeholders, so it is left untouched. KOA-16234.
        if (window.kuika?.isDesignTime === true && typeof value === "string") {
          const placeholderMatch: RegExpMatchArray | null = value.match(BaseTable.DESIGN_TIME_DATAFIELD_PLACEHOLDER);
          if (placeholderMatch) {
            value = placeholderMatch[1].split(".").pop();
          }
        }
        let dataType = ValueType.String;

        function getDataTypeRecursively(datasource, value) {
          if (!datasource || datasource.length === 0) {
            return null;
          }

          if (datasource[0] !== null && datasource[0] !== undefined) {
            if (datasource[0][value] !== undefined && datasource[0][value] !== null) {
              return BaseTable.getValueType(datasource[0][value]);
            }

            const keys = Object.keys(datasource[0]);
            const matchingKey = keys.find((key) => key?.toLowerCase() === value?.toLowerCase());

            if (matchingKey && datasource[0][matchingKey] !== undefined && datasource[0][matchingKey] !== null) {
              return BaseTable.getValueType(datasource[0][matchingKey]);
            }
          }

          return getDataTypeRecursively(datasource?.slice(1), value);
        }
        dataType = getDataTypeRecursively(datasource?.slice(0, 10), value);

        if (sorter && columnProps?.hideSorting !== true) {
          columnProps.sorter = (a, b) => BaseTable.compare(value, a, b, _nextProps.rowKey);
        }
        if (filtering && columnProps?.hideFiltering !== true) {
          let initialFilters: any = {};

          if (result.filteringOptions) {
            Object.values(result.filteringOptions).forEach((filters: any) => {
              if (filters && filters.length > 0 && filters[0].columnId === columnProps.id) {
                initialFilters = filters[0];
              }
            });
          }

          columnProps.filteredValue = BaseTable.isColumnFilterActive(initialFilters) ? [initialFilters] : [];

          columnProps.filterDropdown = ({ setSelectedKeys, selectedKeys, confirm, clearFilters, visible }) =>
            filtering && (
              <CustomFilterDropdown
                selectedKeys={selectedKeys[0]}
                setSelectedKeys={(text) => setSelectedKeys([text])}
                confirm={confirm}
                clearFilters={clearFilters}
                dataType={dataType}
                visible={visible}
                initialFilters={initialFilters}
                columnId={columnProps.id}
                tableStyle={tableStyle}
                dateSearchFormat={(columnProps as any).dateSearchFormat}
                dataSource={datasource}
                dataValue={value}
                calculatedCellBgColor={columnProps.calculatedCellBgColor}
              />
            );
          columnProps.onFilter = (searchValue: any, record) => {
            if (dataType === ValueType.Date) {
              searchValue = {
                ...searchValue,
                dateSearchFormat: (columnProps as any).dateSearchFormat
              };
            }
            return BaseTable.applyColumnFilter(searchValue, record, value, dataType, columnProps.calculatedCellBgColor);
          };
        }
        (columnProps as any).dataType = dataType;
        (columnProps as any).dataValue = value;

        const resolvedFieldName: string = value?.toString()?.toLowerCase() ?? "";
        const formatterEntry: DataBindedFieldFormatter | undefined = dataBindedChildFormatters.find(
          (entry: DataBindedFieldFormatter) =>
            entry.formatter &&
            entry.binding.replace("[datafield:", "").replace("]", "").toLowerCase() === resolvedFieldName
        );
        if (formatterEntry) {
          columnProps.exportFormatter = formatterEntry.formatter;
          columnProps.exportCustomFormatter = formatterEntry.customFormatter;
        }
      }

      // KOA-17691 — capture the edit-mode editor's Form.Item field name so a commit can seed
      // it with the committing row's value. A Save Record param bound to DisplayItemProp
      // (Switch1.value / TextInput1.value) reads this field; in editableAlwaysActive every row
      // shares one field per column, so an editor the user never touched would otherwise
      // commit a stale/other-row value. Read from the editor slot (children[1]) of an editable
      // column; the name is on the generated Form.Item (className "kFormItem"), so this covers
      // Switch/Checkbox/text without depending on any editor's DOM id.
      if (grandChild.props?.isEditableColumn && Array.isArray(grandChild.props.children)) {
        const editorChild = grandChild.props.children[1];
        const editorClassName: string =
          typeof editorChild?.props?.className === "string" ? editorChild.props.className : "";
        if (editorClassName.includes("kFormItem") && typeof editorChild?.props?.name === "string") {
          (columnProps as any).editorFieldName = editorChild.props.name;
        }
      }

      if (columnProps.group) {
        let group = result.columnGroups?.find((g) => g.title === columnProps.group);
        if (!group) {
          group = {
            title: columnProps.group,
            children: [],
            style: {
              ...columnProps.style,
              background: columnProps.calculatedCellBgColor
            },
            groupBackgroundColor: columnProps.groupBackgroundColor,
            groupFontColor: columnProps.groupFontColor,
            groupFontSize: columnProps.groupFontSize,
            groupFontWeight: columnProps.groupFontWeight
          };
          result.columnGroups?.push(group);
        }
        columnProps.isGroup = true;
        group.children.push(columnProps);
        result.columns?.push(columnProps);
      } else {
        result.columns?.push(columnProps);
      }
    });
  };

  static applyFilter = (searchValue: any, record: any, value: any, dataType: any) => {
    const { type } = searchValue;
    const recordValue = record[value];
    const logicalOperator = searchValue?.logicalOperator;
    const secondType = searchValue?.secondType;
    const secondValue = searchValue?.secondValue;
    const secondValue2 = searchValue?.secondValue2;

    if (secondType) {
      const firstResult = BaseTable.applySingleFilter(
        {
          type,
          value: searchValue?.value,
          value2: searchValue?.value2
        },
        recordValue,
        dataType
      );

      const secondResult = BaseTable.applySingleFilter(
        {
          type: secondType,
          value: secondValue,
          value2: secondValue2
        },
        recordValue,
        dataType
      );

      return logicalOperator === "OR" ? firstResult || secondResult : firstResult && secondResult;
    }

    return BaseTable.applySingleFilter(searchValue, recordValue, dataType);
  };

  /**
   * Single dispatch point for ALL filter modes. Both filter paths — antd's
   * per-column `onFilter` (visible rows) and `getFilteredAndSortedDataSource`
   * (footer totals / counts / export) — call this so they stay identical.
   *   - "value": membership over the chosen unique values.
   *   - "color": membership over the chosen background colors (re-runs the
   *     column's `calculatedCellBgColor`, the same source render/export use).
   *   - otherwise: the existing condition filtering, unchanged.
   */
  static applyColumnFilter = (
    filter: TableColumnFilter,
    record: any,
    dataValue: string,
    dataType: any,
    colorFn?: CellBgColorFn
  ): boolean => {
    if (filter?.filterMode === "value") {
      return matchesValueFilter(readRowField(record, dataValue), filter.selectedValues);
    }
    if (filter?.filterMode === "color") {
      // rowIndex is intentionally undefined: it isn't reliably available in either filter
      // path (antd's onFilter has none; getFilteredAndSortedDataSource only has a post-filter
      // index). Kuika conditional-formatting color functions are rowData-driven, so this
      // matches the rendered color. A colorFn that keys off rowIndex (e.g. row striping)
      // would not filter precisely — documented as a known limitation.
      const cellColor: string | undefined = colorFn ? colorFn(undefined, record) : undefined;
      return matchesColorFilter(cellColor, filter.selectedColors);
    }
    return BaseTable.applyFilter(filter, record, dataValue, dataType);
  };

  /** True when a persisted column filter should mark the header as filtered/active. */
  static isColumnFilterActive = (filter: TableColumnFilter | undefined): boolean => {
    if (!filter) {
      return false;
    }
    if (filter.filterMode === "value") {
      return Boolean(filter.selectedValues && filter.selectedValues.length > 0);
    }
    if (filter.filterMode === "color") {
      return Boolean(filter.selectedColors && filter.selectedColors.length > 0);
    }
    const rawValue: unknown = filter.value;
    const blankTypes: string[] = [
      "blank",
      "not_blank",
      "number_blank",
      "number_not_blank",
      "date_blank",
      "date_not_blank",
      "boolean_blank",
      "boolean_not_blank"
    ];
    return (
      Boolean(rawValue) ||
      rawValue === false ||
      rawValue === 0 ||
      (filter.type !== undefined && blankTypes.includes(filter.type))
    );
  };

  static applySingleFilter = (searchValue: any, recordValue: any, dataType: any) => {
    const { type } = searchValue;
    const valueType = BaseTable.getValueType(recordValue, dataType);

    if (valueType === ValueType.String || !valueType) {
      const normalizedRecordValue = BaseTable.normalizeTurkishString(recordValue?.toString());
      const normalizedSearchValue =
        type === "blank" || type === "number_blank" || type === "date_blank" || type === "boolean_blank"
          ? null
          : BaseTable.normalizeTurkishString(searchValue?.value?.toString());

      switch (type) {
        case StringFilterTypes.Contains:
          return normalizedRecordValue?.includes(normalizedSearchValue);
        case StringFilterTypes.DoesNotContain:
          return !normalizedRecordValue?.includes(normalizedSearchValue);
        case StringFilterTypes.Equals:
          return normalizedRecordValue === normalizedSearchValue;
        case StringFilterTypes.DoesNotEqual:
          return normalizedRecordValue !== normalizedSearchValue;
        case StringFilterTypes.BeginsWith:
          return normalizedRecordValue?.startsWith(normalizedSearchValue);
        case StringFilterTypes.EndsWith:
          return normalizedRecordValue?.endsWith(normalizedSearchValue);
        case StringFilterTypes.Blank:
          return !recordValue;
        case StringFilterTypes.NotBlank:
          return !!recordValue;
        default:
          return normalizedRecordValue?.includes(normalizedSearchValue);
      }
    } else if (valueType === ValueType.Number) {
      const recordValueNumber = Number(recordValue);

      switch (type) {
        case NumberFilterTypes.Equals:
          return recordValueNumber === Number(searchValue?.value);
        case NumberFilterTypes.DoesNotEqual:
          return recordValueNumber !== Number(searchValue?.value);
        case NumberFilterTypes.GreaterThan:
          return recordValueNumber > Number(searchValue?.value);
        case NumberFilterTypes.LessThan:
          return recordValueNumber < Number(searchValue?.value);
        case NumberFilterTypes.GreaterThanOrEqual:
          return recordValueNumber >= Number(searchValue?.value);
        case NumberFilterTypes.LessThanOrEqual:
          return recordValueNumber <= Number(searchValue?.value);
        case NumberFilterTypes.Between:
          if (searchValue?.value !== undefined && searchValue?.value2 !== undefined) {
            const min = Number(searchValue.value);
            const max = Number(searchValue.value2);
            return !isNaN(min) && !isNaN(max) && recordValueNumber >= min && recordValueNumber <= max;
          }
          return recordValueNumber === Number(searchValue?.value);
        case NumberFilterTypes.Blank:
          return !recordValue && recordValue !== 0;
        case NumberFilterTypes.NotBlank:
          return !!recordValue || recordValue === 0;
        default:
          return recordValueNumber === Number(searchValue?.value);
      }
    } else if (valueType === ValueType.Date) {
      if (!recordValue || recordValue === "") {
        if (type === DateFilterTypes.Blank) return true;
        if (type === DateFilterTypes.NotBlank) return false;
        if (type === DateFilterTypes.DoesNotEqual) return true;
        return false;
      }

      const recordValueDate = BaseTable.parseDateWithFormat(recordValue, searchValue?.dateSearchFormat, ValueType.Date);
      if (!recordValueDate.isValid()) {
        if (type === DateFilterTypes.Blank) return true;
        if (type === DateFilterTypes.NotBlank) return false;
        if (type === DateFilterTypes.DoesNotEqual) return true;
        return false;
      }

      const searchValueDate = searchValue?.value
        ? BaseTable.parseDateWithFormat(searchValue.value, searchValue?.dateSearchFormat, ValueType.Date)
        : null;
      const hasValidSearchDate = searchValueDate && searchValueDate.isValid();

      const searchValueDate2 = searchValue?.value2
        ? BaseTable.parseDateWithFormat(searchValue.value2, searchValue?.dateSearchFormat, ValueType.Date)
        : null;
      const hasValidSearchDate2 = searchValueDate2 && searchValueDate2.isValid();

      const comparisonUnit = BaseTable.getDateComparisonUnit(searchValue?.dateSearchFormat);

      switch (type) {
        case DateFilterTypes.Equals:
          return hasValidSearchDate && recordValueDate.isSame(searchValueDate, comparisonUnit);
        case DateFilterTypes.DoesNotEqual:
          return hasValidSearchDate && !recordValueDate.isSame(searchValueDate, comparisonUnit);
        case DateFilterTypes.After:
          return hasValidSearchDate && recordValueDate.isAfter(searchValueDate, comparisonUnit);
        case DateFilterTypes.Before:
          return hasValidSearchDate && recordValueDate.isBefore(searchValueDate, comparisonUnit);
        case DateFilterTypes.Between:
          if (hasValidSearchDate && hasValidSearchDate2) {
            return recordValueDate.isBetween(searchValueDate, searchValueDate2, comparisonUnit, "[]");
          }
          return hasValidSearchDate && recordValueDate.isSame(searchValueDate, comparisonUnit);
        case DateFilterTypes.Blank:
          return !recordValue;
        case DateFilterTypes.NotBlank:
          return !!recordValue;
        default:
          return hasValidSearchDate && recordValueDate.isSame(searchValueDate, comparisonUnit);
      }
    } else if (valueType === ValueType.Boolean) {
      const normalizedRecordValue = recordValue;
      const normalizedSearchValue =
        type === "blank" || type === "number_blank" || type === "date_blank" || type === "boolean_blank"
          ? null
          : searchValue?.value;

      switch (type) {
        case BooleanFilterTypes.Equals:
          return normalizedRecordValue === normalizedSearchValue;
        case BooleanFilterTypes.DoesNotEqual:
          return normalizedRecordValue !== normalizedSearchValue;
        case BooleanFilterTypes.Blank:
          return !recordValue && recordValue !== false;
        case BooleanFilterTypes.NotBlank:
          return !!recordValue || recordValue === false;
        default:
          return normalizedRecordValue === normalizedSearchValue;
      }
    } else {
      return false;
    }
  };

  static getDefaultDateFormats = (): string[] => {
    return [
      "YYYY-MM-DDTHH:mm:ss",
      "YYYY-MM-DDTHH:mm",
      "YYYY-MM-DD HH:mm:ss",
      "YYYY-MM-DD HH:mm",
      "DD.MM.YYYY HH:mm:ss",
      "D.MM.YYYY HH:mm:ss",
      "DD.M.YYYY HH:mm:ss",
      "D.M.YYYY HH:mm:ss",
      "MM/DD/YYYY HH:mm:ss",
      "DD/MM/YYYY HH:mm:ss",
      "M/DD/YYYY HH:mm:ss",
      "DD/M/YYYY HH:mm:ss",
      "DD.MM.YYYY HH:mm",
      "D.MM.YYYY HH:mm",
      "MM/DD/YYYY HH:mm",
      "DD/MM/YYYY HH:mm",
      "DD.MM.YYYY",
      "D.MM.YYYY",
      "MM/DD/YYYY",
      "DD/MM/YYYY",
      "YYYY-MM-DD"
    ];
  };

  static parseDateWithFormat = (value: any, dateSearchFormat?: string, columnType?: ValueType) => {
    if (columnType && columnType !== ValueType.Date) return moment.invalid();
    if (!value) return moment.invalid();
    const formats: string[] = [];
    if (dateSearchFormat) {
      formats.push(dateSearchFormat);
      if (dateSearchFormat.includes(":ss")) formats.push(dateSearchFormat.replace(":ss", ""));
      if (dateSearchFormat.includes("HH:mm") && !dateSearchFormat.includes(":ss"))
        formats.push(`${dateSearchFormat}:ss`);
    }
    formats.push(...BaseTable.getDefaultDateFormats());
    const m = moment(value, formats, true);
    if (m.isValid()) return m;
    return moment(value);
  };

  static getDateComparisonUnit = (dateSearchFormat?: string): moment.unitOfTime.StartOf => {
    if (!dateSearchFormat) return "day";
    if (dateSearchFormat.includes("ss")) return "second";
    if (dateSearchFormat.includes("HH") || dateSearchFormat.includes("mm")) return "minute";
    return "day";
  };

  static isNotNull = (value: any) => {
    if (value === undefined) return false;
    if (value === null) return false;
    return true;
  };

  static mapFontWeight = (fontWeight: string): string | number => {
    const fontWeightMap: { [key: string]: string | number } = {
      Thin: 100,
      "Extra Light": 200,
      Light: 300,
      Regular: 400,
      Medium: 500,
      "Semi Bold": 600,
      Bold: 700,
      "Extra Bold": 800,
      Black: 900
    };
    return fontWeightMap[fontWeight] || fontWeight;
  };

  static compare = (value, a, b, _rowKey?: string) => {
    let x = "";
    const rowKey = _rowKey || "id";
    if (a?.id != null && Object.keys(a).every((key) => key === rowKey || a[key] == null)) {
      return 0;
    }
    if (b?.id != null && Object.keys(b).every((key) => key === rowKey || b[key] == null)) {
      return 0;
    }
    if (BaseTable.isNotNull(a) && BaseTable.isNotNull(value) && BaseTable.isNotNull(a[value])) {
      x = a[value];
      if (typeof a[value] === "string") {
        x = a[value].trim();
      }
    }
    if (!x) {
      const match = value.match(/\[(.*?)\.(.*?)\]/);
      let extractedKey = null;

      if (match && match[2]) {
        extractedKey = match[2];
      }
      if (extractedKey) {
        x = a[extractedKey];
      }
    }
    if (!BaseTable.isNotNull(x)) {
      x = "";
    }

    let y = "";
    if (BaseTable.isNotNull(b) && BaseTable.isNotNull(value) && BaseTable.isNotNull(b[value])) {
      y = b[value];
      if (typeof b[value] === "string") {
        y = b[value].trim();
      }
    }
    if (!y) {
      const match = value.match(/\[(.*?)\.(.*?)\]/);
      let extractedKey = null;

      if (match && match[2]) {
        extractedKey = match[2];
      }
      if (extractedKey) {
        y = b[extractedKey];
      }
    }

    if (!BaseTable.isNotNull(y)) {
      y = "";
    }
    const specialSortingFormats = [
      "DD/MM/YYYY HH:mm",
      "DD/MM/YYYY HH:mm:ss",
      "DD/MM/YYYY",
      "MM/DD/YYYY HH:mm",
      "MM/DD/YYYY HH:mm:ss",
      "MM/DD/YYYY",
      "HH:mm:ss",
      "HH:mm",
      "YYYY-MM-DDTHH:mm:ss",
      "YYYY-MM-DDTHH:mm:ss.SSSZ",
      "YYYY-MM-DDTHH:mm:ss.SSSSSSSZ"
    ];

    const isValidDate = BaseTable.isDate(x, specialSortingFormats);

    if (isValidDate) {
      const specialSortingFormat = specialSortingFormats.find((format) => moment(x, format, true).isValid());
      if (specialSortingFormat) {
        if (!x || x === "") {
          return 0;
        }
        if (!y || y === "") {
          return 1;
        }
        const xDate = moment(x, specialSortingFormat);
        const yDate = moment(y, specialSortingFormat);
        return xDate.diff(yDate);
      }
    }

    if (
      (BaseTable.isNumber(x, true) || x === "" || x === undefined || x === null) &&
      (BaseTable.isNumber(y, true) || y === "" || y === undefined || y === null)
    ) {
      if ((!x && parseInt(x, 10) !== 0) || x === "") {
        return -1;
      }
      if ((!y && parseInt(y, 10) !== 0) || y === "") {
        return 1;
      }
      return Number(x) - Number(y);
    }

    if (typeof x === "string" && typeof y === "string" && x.localeCompare) {
      return x.localeCompare(y);
    }

    if (typeof x === "boolean" && typeof y === "boolean") {
      return x === y ? 0 : x ? 1 : -1;
    }

    return 0;
  };

  static isDate = (value: any, formats: any[]) => {
    if (value === undefined || value === null) return false;

    if (typeof value === "string") {
      for (const format of formats) {
        if (moment(value, format, true).isValid()) {
          return true;
        }
      }
    }

    return false;
  };

  static isNumber(r, isSort) {
    if (typeof r === "string" && !isSort) return false;
    return /^-?\d+(\.\d+)?$/.test(r);
  }

  // Strips thousand separators from a formatted number string so parseFloat/parseInt can read it.
  // Handles both US ("1,234.56") and European ("1.234,56") conventions. The hard case is a lone
  // comma or dot with exactly three digits after it ("1,000" / "1.000") — we prefer the
  // thousand-separator reading so NumberInput's default (decimalSeparator ".", thousandSeparator ",")
  // round-trips user input like 1000 correctly. See KOA-16988.
  static parseLocalizedNumberString(value: string): string {
    if (!value || typeof value !== "string") return value;
    const commaCount = (value.match(/,/g) || []).length;
    const dotCount = (value.match(/\./g) || []).length;
    if (commaCount === 0 && dotCount === 0) return value;

    if (commaCount > 0 && dotCount > 0) {
      if (value.lastIndexOf(",") > value.lastIndexOf(".")) {
        return value.replace(/\./g, "").replace(/,/g, ".");
      }
      return value.replace(/,/g, "");
    }

    if (commaCount > 0) {
      if (commaCount > 1) return value.replace(/,/g, "");
      const afterComma = value.substring(value.lastIndexOf(",") + 1);
      if (/^\d{3}$/.test(afterComma)) return value.replace(/,/g, "");
      return value.replace(/,/g, ".");
    }

    if (dotCount > 1) return value.replace(/\./g, "");
    return value;
  }

  static getValueType(value: any, dataType?: ValueType) {
    const specialSortingFormats = [
      "DD/MM/YYYY HH:mm",
      "DD/MM/YYYY HH:mm:ss",
      "DD/MM/YYYY",
      "MM/DD/YYYY HH:mm",
      "MM/DD/YYYY HH:mm:ss",
      "MM/DD/YYYY",
      "HH:mm:ss",
      "HH:mm",
      "YYYY-MM-DDTHH:mm:ss",
      "YYYY-MM-DDTHH:mm:ss.SSSZ",
      "YYYY-MM-DDTHH:mm:ss.SSSSSSSZ"
    ];
    if (typeof value === "boolean" || value === "true" || value === "false") {
      return ValueType.Boolean;
    }
    if (BaseTable.isDate(value, specialSortingFormats)) {
      return ValueType.Date;
    }
    if (BaseTable.isNumber(value, false)) {
      return ValueType.Number;
    }
    if (!value || value === "" || value === null || value === undefined) {
      return dataType;
    }
    return ValueType.String;
  }

  static setGalleryItem = (result: Partial<ITableState>, children: any) => {
    result.rowRenderer = (props: any) => {
      return <GalleryItem {...props}></GalleryItem>;
    };
    const columnProps: TableDataProps & CommonProps = {};
    columnProps.render = (_value: any, rowData: any, rowIndex: number) => {
      return (
        <TableData rowIndex={rowIndex} rowData={rowData}>
          {children}
        </TableData>
      );
    };
    result?.columns?.push(columnProps);
  };

  static setCarouselItem = (result: Partial<ITableState>, children: any) => {
    result.rowRenderer = (props: any) => {
      return <CarouselItem {...props}></CarouselItem>;
    };
    const columnProps: TableDataProps & CommonProps = {};

    columnProps.render = (_value: any, rowData: any, rowIndex: number) => (
      <TableData rowIndex={rowIndex} rowData={rowData}>
        {children}
      </TableData>
    );
    result?.columns?.push(columnProps);
  };

  static setGoogleMapsItem = (result: Partial<ITableState>, children: any, nextProps: ITableProps) => {
    let rowPopoverRenderer: any | undefined;

    React.Children.forEach(nextProps.children, (child, childIndex) => {
      switch (childIndex) {
        // GoogleMapsMarkerPopup
        case 1:
          rowPopoverRenderer = (_value: any, rowData: any, rowIndex: number) => {
            return (
              <TableData
                rowIndex={rowIndex}
                rowData={rowData}
                markerDetailHeight={nextProps.markerDetailHeight}
                markerDetailWidth={nextProps.markerDetailWidth}
              >
                {React.cloneElement(
                  child,
                  {
                    ...child.props,
                    markerDetailWidth: nextProps.markerDetailWidth,
                    markerDetailHeight: nextProps.markerDetailHeight
                  },
                  child.props.children
                )}
              </TableData>
            );
          };
      }
    });

    result.rowRenderer = (props: any) => {
      return <GoogleMapsItem {...props} rowPopoverRenderer={rowPopoverRenderer}></GoogleMapsItem>;
    };
    const columnProps: TableDataProps & CommonProps = {};

    columnProps.render = (_value: any, rowData: any, rowIndex: number) => (
      <TableData rowIndex={rowIndex} rowData={rowData}>
        {children}
      </TableData>
    );
    result?.columns?.push(columnProps);
  };

  static refreshGoogleMapsItem: ((nextProps?: any, callBack?: any) => void) | undefined;

  static setFlexGridItem = (result: Partial<ITableState>, children: any) => {
    result.rowRenderer = (props: any) => {
      return <FlexGridItem {...props}></FlexGridItem>;
    };
    const columnProps: TableDataProps & CommonProps = {};

    columnProps.render = (_value: any, rowData: any, rowIndex: number) => (
      <TableData rowIndex={rowIndex} rowData={rowData}>
        {children}
      </TableData>
    );
    result?.columns?.push(columnProps);
  };

  setStyle = (_props?: ITableProps) => {
    const node: Element | null | Text = ReactDOM.findDOMNode(this);
    if (!node) {
      return;
    }
    const table = (node as Element).querySelectorAll("div > div > div > div > div > table");
    this.setBackgroundColor(table);
    const tbody = (node as Element).querySelectorAll("div > div > div > div > div > tbody");
    this.setBackgroundColor(tbody);
    const tdList = (node as Element).querySelectorAll("div > div > div > div > div > table > tbody > tr > td");
    tdList.forEach((td: any, index: number) => {
      this.setBackgroundColor(td);
      if (this.props.mode === "table") {
        this.setAlignments(td, index);
      }
    });
    if (this.props.mode === "table") {
      this.setDynamicStyle();
    }
  };

  // antd/rc-table owns the sticky offsets of pinned cells: it measures every column
  // (hidden ResizeObserver measure row) and derives `left`/`right` for leaf cells AND for
  // grouped (colspan) header cells from those measurements. Its model requires the pinned
  // columns to be the *leading* and *trailing* entries of the column list — a pinned column
  // in the middle is offset by everything before it, which is why a grouped fixed column
  // ended up overlapping its neighbours (KOA-17746): header groups render before the
  // ungrouped columns, so a pinned group was neither leading nor trailing.
  //
  // This restores rc-table's contract with a stable partition: pinned-left entries first,
  // unpinned entries in their existing order, pinned-right entries last. Only pinned columns
  // move, and their previous rendering position was already wrong.
  static orderColumnsByFixedEdge<T extends IFixedEdgeColumn>(columnEntries: T[]): T[] {
    const left: T[] = [];
    const middle: T[] = [];
    const right: T[] = [];
    columnEntries.forEach((entry: T) => {
      if (entry?.fixed === "left") {
        left.push(entry);
      } else if (entry?.fixed === "right") {
        right.push(entry);
      } else {
        middle.push(entry);
      }
    });
    return [...left, ...middle, ...right];
  }

  // A grouped header cell spans its children, so it can only be pinned as a whole. rc-table
  // reads the group cell's edge from its first child (left) and its last child (right) — this
  // mirrors that rule so the group travels in the same bucket that antd will pin it to.
  // A partially pinned group therefore follows its leading/trailing child, never a child in
  // the middle of the span.
  static resolveGroupFixedEdge(visibleChildren: IFixedEdgeColumn[]): FixedEdge {
    if (!visibleChildren?.length) {
      return undefined;
    }
    if (visibleChildren[0]?.fixed === "left") {
      return "left";
    }
    if (visibleChildren[visibleChildren.length - 1]?.fixed === "right") {
      return "right";
    }
    return undefined;
  }

  // True when the table renders as a horizontally scrollable antd table (scroll={{ x }}):
  // explicit horizontal width modes, or at least one visible pinned column.
  hasHorizontalOverflowMode = (): boolean => {
    const hasVisibleFixedColumn: boolean =
      this.state.columns?.some(
        (col: any) =>
          (col.fixed === "left" || col.fixed === "right") &&
          col.visibility !== "hidden" &&
          !this.state.hiddenColumnList?.some((id) => id.toLowerCase() === col.id.toLowerCase())
      ) === true;
    return (
      this.props.tableWidthMode === "scroll" ||
      this.props.tableWidthMode === "horizontal scroll" ||
      hasVisibleFixedColumn
    );
  };

  // A pinned cell paints nothing of its own borders once it detaches from its natural
  // position. The table body renders with `border-collapse: collapse` (TableViewWrapper —
  // the collapsed model is what lets a Table Row's border draw the row separators), and in
  // the collapsed model a border belongs to the *table*, not to the cell: the browser paints
  // it where the cell's column sits, so a sticky cell slides away from its own borders and
  // the separators inside the pinned block vanish as soon as the table is scrolled sideways
  // (KOA-17746). Re-drawing them as inset box-shadows keeps them with the cell, because a
  // shadow paints with the element instead of with the table's collapsed-border layer.
  //
  // Only the sides the column actually configures are drawn, so a column without borders
  // stays borderless.
  static buildStickyBorderShadow(style: any, includeHorizontalSides: boolean): string | undefined {
    if (!style || style.borderStyle === "none") {
      return undefined;
    }
    const resolveWidth = (side?: string | number): number => {
      const raw: string | number | undefined = side ?? style.borderWidth;
      if (typeof raw === "number") {
        return raw;
      }
      if (typeof raw === "string") {
        return parseFloat(raw) || 0;
      }
      return 0;
    };
    const resolveColor = (side?: string): string => side ?? style.borderColor ?? "rgb(218, 218, 218)";
    const shadows: string[] = [];
    const left: number = resolveWidth(style.borderLeftWidth);
    if (left > 0) {
      shadows.push(`inset ${left}px 0 0 0 ${resolveColor(style.borderLeftColor)}`);
    }
    const right: number = resolveWidth(style.borderRightWidth);
    if (right > 0) {
      shadows.push(`inset -${right}px 0 0 0 ${resolveColor(style.borderRightColor)}`);
    }
    if (includeHorizontalSides) {
      const top: number = resolveWidth(style.borderTopWidth);
      if (top > 0) {
        shadows.push(`inset 0 ${top}px 0 0 ${resolveColor(style.borderTopColor)}`);
      }
      const bottom: number = resolveWidth(style.borderBottomWidth);
      if (bottom > 0) {
        shadows.push(`inset 0 -${bottom}px 0 0 ${resolveColor(style.borderBottomColor)}`);
      }
    }
    return shadows.length > 0 ? shadows.join(", ") : undefined;
  }

  // The full shadow a pinned cell shows while it is detached: whatever the styling panel
  // configured, plus the border replacement. The styling shadow has to be carried along
  // because the rule that applies this wins over the cell's inline box-shadow.
  static composePinnedCellShadow(
    configuredShadow: string | undefined,
    borderShadow: string | undefined
  ): string | undefined {
    const parts: string[] = [configuredShadow, borderShadow].filter((part): part is string => Boolean(part));
    return parts.length > 0 ? parts.join(", ") : undefined;
  }

  // A pinned cell only loses its borders while it is actually detached from its column — at
  // rest the collapsed border is painted exactly where it belongs, and drawing the shadow
  // too would show a doubled 2px line. antd already tracks that state: it marks the table
  // `ping-left` while the body is scrolled away from its left edge and `ping-right` while it
  // is scrolled away from its right edge, which is precisely when the left/right pinned
  // cells are detached. Gating on those classes means the replacement is painted only when
  // the real border is missing.
  static buildPinnedCellBorderCss(className: string, includeHeaderCells: boolean): string {
    const targets: string[] = includeHeaderCells ? ["td", "th"] : ["td"];
    const rules: string[] = [];
    (["left", "right"] as const).forEach((edge: "left" | "right") => {
      const selector: string = targets
        .map((tag: string) => `.${className} .ant-table-ping-${edge} ${tag}.ant-table-cell-fix-${edge}`)
        .join(",\n");
      rules.push(`${selector} {\n  box-shadow: var(--kuika-pinned-shadow, none) !important;\n}`);
    });
    return rules.join("\n");
  }

  // True when the Table Row itself draws a horizontal separator. The `<tr>` never sticks, so
  // its collapsed border keeps spanning the pinned columns — re-drawing the cell's own
  // top/bottom border on top of it would add a second line that is not there today.
  static rowStylePaintsSeparators(rowStyle: any): boolean {
    if (!rowStyle || rowStyle.borderStyle === "none") {
      return false;
    }
    const width = (side?: string | number): number => {
      const raw: string | number | undefined = side ?? rowStyle.borderWidth;
      if (typeof raw === "number") {
        return raw;
      }
      if (typeof raw === "string") {
        return parseFloat(raw) || 0;
      }
      return 0;
    };
    return width(rowStyle.borderTopWidth) > 0 || width(rowStyle.borderBottomWidth) > 0;
  }

  // antd derives the sticky offset of every pinned cell from a hidden measuring row
  // (`.ant-table-measure-row`) that `styles/general.scss` has hidden with `display: none`
  // since 2022. A `display: none` row reports `offsetWidth === 0` for every column, so antd
  // pins *all* pinned columns at offset 0 and they stack on top of each other instead of
  // sitting side by side — the "iç içe girme" of KOA-17746. Kuika therefore has to measure
  // the offsets itself; this is the pure core of that, kept DOM-free so it can be tested.
  //
  // The pinned columns are the leading and trailing runs of the rendered row (guaranteed by
  // orderColumnsByFixedEdge), and a column's offset is the total width of the pinned columns
  // between it and its edge.
  static computePinnedOffsets(cells: PinnedCellMeasurement[]): PinnedColumnOffset[] {
    const offsets: PinnedColumnOffset[] = [];
    let left = 0;
    for (const cell of cells) {
      if (cell.edge !== "left") {
        break;
      }
      if (cell.key !== null) {
        offsets.push({ key: cell.key, edge: "left", offset: left });
      }
      left += cell.width;
    }
    let right = 0;
    for (let i = cells.length - 1; i >= 0; i--) {
      const cell = cells[i];
      if (cell.edge !== "right") {
        break;
      }
      if (cell.key !== null) {
        offsets.push({ key: cell.key, edge: "right", offset: right });
      }
      right += cell.width;
    }
    return offsets;
  }

  // A grouped header cell spans its children, so it sticks at the offset of whichever child
  // is on its pinned edge.
  static computeGroupPinnedOffsets(groups: GroupChildKeys[], leafOffsets: PinnedColumnOffset[]): PinnedColumnOffset[] {
    const byKey = new Map<string, PinnedColumnOffset>();
    leafOffsets.forEach((offset: PinnedColumnOffset) => byKey.set(`${offset.edge}:${offset.key}`, offset));
    const offsets: PinnedColumnOffset[] = [];
    groups.forEach((group: GroupChildKeys) => {
      if (group.childKeys.length === 0) {
        return;
      }
      const first: PinnedColumnOffset | undefined = byKey.get(`left:${group.childKeys[0]}`);
      if (first) {
        offsets.push({ key: group.key, edge: "left", offset: first.offset });
        return;
      }
      const last: PinnedColumnOffset | undefined = byKey.get(`right:${group.childKeys[group.childKeys.length - 1]}`);
      if (last) {
        offsets.push({ key: group.key, edge: "right", offset: last.offset });
      }
    });
    return offsets;
  }

  // One rule per pinned column, matched on the column identity every cell of that column
  // carries. A single rule therefore positions the leaf header cell, the grouped header cell
  // above it and all of its body cells — and, unlike the inline styles this replaces, the
  // rules survive antd rebuilding its rows (KOA-17505) without a per-row DOM pass.
  static buildPinnedOffsetCss(className: string, offsets: PinnedColumnOffset[]): string {
    return offsets
      .map((offset: PinnedColumnOffset) => {
        const target = `.ant-table-cell-fix-${offset.edge}[index="${offset.key}"]`;
        return `.${className} th${target}, .${className} td${target} { ${offset.edge}: ${offset.offset}px !important; }`;
      })
      .join("\n");
  }

  private static readonly MIN_SYNCED_COLUMN_WIDTH_PX: number = 90;

  // True when the column carries a width of its own — set through the styling panel, by a
  // column resize, or by Kuika itself (the row-number column is 50px wide). The horizontal
  // width modes assign every ungrouped column `width: "min-content"`, which is a sizing
  // keyword rather than a width, so it deliberately does not count.
  static hasExplicitColumnWidth(column: any): boolean {
    const width: number | string | undefined = column?.width ?? column?.style?.width;
    if (typeof width === "number") {
      return Number.isFinite(width) && width > 0;
    }
    return typeof width === "string" && /^\d+(\.\d+)?px$/.test(width.trim());
  }

  // The width to hand a column when the header and body are synced.
  //
  // A column that is not rendered yet measures 0 and would collapse both tables, so it falls
  // back to a minimum. Columns that size themselves keep that minimum as a floor, which is
  // the long-standing behaviour of fixed-header tables with pinned columns.
  //
  // A column with a width of its own must NOT be floored: the body cell renders at the width
  // its `<col>` prescribes (antd re-renders the colgroup from the column config), so
  // widening only the header cell drifts the two apart by the difference and pushes every
  // following column out of line — a 39px drift on the row-number column (KOA-17746).
  static resolveSyncedColumnWidth(measuredWidth: number, columnHasExplicitWidth: boolean): number {
    if (measuredWidth < 1) {
      return BaseTable.MIN_SYNCED_COLUMN_WIDTH_PX;
    }
    return columnHasExplicitWidth ? measuredWidth : Math.max(measuredWidth, BaseTable.MIN_SYNCED_COLUMN_WIDTH_PX);
  }

  // The marker `hasExplicitColumnWidth` leaves on a rendered cell so the width sync can tell
  // the two cases apart without re-resolving the column config.
  static readonly EXPLICIT_WIDTH_ATTRIBUTE: string = "data-kuika-explicit-width";

  private static cellHasExplicitWidth(cell: HTMLTableCellElement): boolean {
    return cell.getAttribute(BaseTable.EXPLICIT_WIDTH_ATTRIBUTE) === "true";
  }

  static readonly DEFAULT_GROUP_BORDER_COLOR: string = "rgb(218, 218, 218)";

  // The border colour grouped header cells are drawn with. `state.headerStyle` is only filled
  // in by setHeader, which runs when the Table's first child is a Table Header — a table
  // built without one leaves it undefined, and dereferencing it while building the grouped
  // columns took the whole screen down with "Cannot read properties of undefined (reading
  // 'borderColor')".
  static resolveGroupBorderColor(headerStyle: any): string {
    return headerStyle?.borderColor || BaseTable.DEFAULT_GROUP_BORDER_COLOR;
  }

  private getGroupBorderColor = (): string => {
    return BaseTable.resolveGroupBorderColor(this.state.headerStyle);
  };

  // rc-table lifts the header rows into their own <table> when a vertical scroll height is
  // set, and that table keeps the default separate border model — its cells paint their own
  // borders, sticky or not. Only a header that shares the body table needs the shadow
  // fallback above.
  private isHeaderInBodyTable = (): boolean => {
    const hasFixedColumns: boolean =
      this.state.columns?.some((col: any) => col.fixed === "left" || col.fixed === "right") === true;
    return !(this.props.fixedHeader && hasFixedColumns);
  };

  // KOA-13599: on long tables the horizontal scrollbar lives at the very bottom of the
  // table, below the viewport. A self-managed proxy scrollbar (the kuika-table-pinned-hscroll
  // element in render) is pinned to the viewport's bottom edge while the table crosses it, so
  // horizontal scrolling stays reachable from any row. antd's `sticky` prop is deliberately
  // NOT used for this: it moves the header rows into a separate sticky-holder <table>, which
  // desyncs grouped (colspan) header titles from the body columns and re-broke the
  // KOA-16782 header-alignment family (KOA-18006). Design time is excluded to keep the
  // canvas DOM stable for selection/measurement, and the report designer is excluded
  // because its output is paginated print, not a scrolling page.
  shouldPinHorizontalScrollbar = (): boolean => {
    if (window?.kuika?.isDesignTime === true) {
      return false;
    }
    if (window?.kuika?.dashboardState === DashboardState.reportDesigner) {
      return false;
    }
    return this.hasHorizontalOverflowMode();
  };

  private static readonly STICKY_SCROLL_VISIBLE_CLASS: string = "kuika-sticky-scroll-visible";

  private static readonly STICKY_SCROLL_HIDE_DELAY_MS: number = 1000;

  // Hide the pinned bar before the table's own scrollbar enters the viewport so the two are
  // never visible together. rc-table compensates by the native scrollbar size, but macOS
  // overlay scrollbars report size 0, which is how both bars ended up on screen at once.
  private static readonly STICKY_SCROLL_BOTTOM_MARGIN_PX: number = 24;

  // The pinned bar is only useful while the viewport's bottom edge cuts through the table:
  // above it the table has not been reached, below it the table's own scrollbar is in reach.
  static shouldShowStickyScrollBar(wrapperTop: number, wrapperBottom: number, viewportHeight: number): boolean {
    if (wrapperTop >= viewportHeight) {
      return false;
    }
    return wrapperBottom > viewportHeight + BaseTable.STICKY_SCROLL_BOTTOM_MARGIN_PX;
  }

  private stickyScrollHideTimerId: ReturnType<typeof setTimeout> | undefined;

  private stickyScrollRafId: number | undefined;

  private stickyScrollBarHovered: boolean = false;

  private pinnedScrollbarEl: HTMLDivElement | null = null;

  private pinnedScrollbarSpacerEl: HTMLDivElement | null = null;

  // Callback ref: the proxy bar's mount/unmount IS the pinned-scrollbar feature's
  // lifecycle, so the window listeners attach and detach here. Attaching once from
  // componentDidMount raced against async column loading — a table whose horizontal
  // overflow comes from fixed columns still has empty state.columns at mount+100ms, so
  // the listeners never attached and the bar could never be revealed (QA fixedheader
  // screen, KOA-18006).
  private handlePinnedScrollbarRef = (node: HTMLDivElement | null): void => {
    this.pinnedScrollbarEl = node;
    if (node) {
      this.attachStickyScrollListeners();
    } else {
      this.detachStickyScrollListeners();
    }
  };

  private handlePinnedScrollbarSpacerRef = (node: HTMLDivElement | null): void => {
    this.pinnedScrollbarSpacerEl = node;
  };

  private getWrapperElement = (): HTMLElement | null => {
    const className: string = this.getClassName().trim();
    if (!className) {
      return null;
    }
    return document.querySelector<HTMLElement>(`.${className}`);
  };

  // macOS-style overlay behavior for the pinned bar: any scroll (page, inner container, or
  // the table body itself — the listener is capture-phase) reveals it, and it fades out
  // shortly after the activity stops unless the pointer is on it.
  handleStickyScrollActivity = (): void => {
    if (this.stickyScrollRafId !== undefined) {
      return;
    }
    this.stickyScrollRafId = window.requestAnimationFrame(() => {
      this.stickyScrollRafId = undefined;
      this.updateStickyScrollVisibility();
    });
  };

  updateStickyScrollVisibility = (): void => {
    const wrapper: HTMLElement | null = this.getWrapperElement();
    if (!wrapper) {
      return;
    }
    const proxyBar: HTMLDivElement | null = this.pinnedScrollbarEl;
    if (!proxyBar) {
      wrapper.classList.remove(BaseTable.STICKY_SCROLL_VISIBLE_CLASS);
      return;
    }
    this.requestSyncIfFixedColumnsDesynced(wrapper);
    // Geometry gate first: a table whose bottom edge is not below the viewport does no
    // scroll-mirroring work at all, so per activity tick an off-screen table costs one
    // rect read and never touches scrollWidth or styles (avoids per-frame layout thrash
    // on pages with several tables).
    const wrapperRect: DOMRect = wrapper.getBoundingClientRect();
    const isInViewportBand: boolean = BaseTable.shouldShowStickyScrollBar(
      wrapperRect.top,
      wrapperRect.bottom,
      window.innerHeight
    );
    if (!isInViewportBand) {
      wrapper.classList.remove(BaseTable.STICKY_SCROLL_VISIBLE_CLASS);
      return;
    }
    const isScrollable: boolean = this.syncPinnedScrollbarFromTable(wrapper, proxyBar);
    if (isScrollable) {
      wrapper.classList.add(BaseTable.STICKY_SCROLL_VISIBLE_CLASS);
      this.scheduleStickyScrollHide(wrapper);
    } else {
      wrapper.classList.remove(BaseTable.STICKY_SCROLL_VISIBLE_CLASS);
    }
  };

  scheduleStickyScrollHide = (wrapper: HTMLElement): void => {
    if (this.stickyScrollHideTimerId !== undefined) {
      clearTimeout(this.stickyScrollHideTimerId);
    }
    this.stickyScrollHideTimerId = setTimeout(() => {
      this.stickyScrollHideTimerId = undefined;
      if (this.stickyScrollBarHovered) {
        this.scheduleStickyScrollHide(wrapper);
        return;
      }
      wrapper.classList.remove(BaseTable.STICKY_SCROLL_VISIBLE_CLASS);
    }, BaseTable.STICKY_SCROLL_HIDE_DELAY_MS);
  };

  // The single element that horizontally scrolls this table's body: .ant-table-content when
  // header and body share one <table>, .ant-table-body when a fixed header splits them. The
  // wrapper's first match in document order belongs to this table, not to a nested one.
  private getHorizontalScrollContainer = (wrapper: HTMLElement): HTMLElement | null => {
    return wrapper.querySelector<HTMLElement>(".ant-table-content, .ant-table-body");
  };

  // Both inner tables of a split (fixedHeader) render share one column grid, so their
  // rendered total widths agree whenever the columns are in sync (±1px of border
  // rounding); a body rebuilt without its inline widths immediately diverges by whole
  // column deltas. Comparing the totals is cell-layout agnostic, so it works for grouped
  // headers too, at the cost of two rect reads.
  private static readonly FIXED_DESYNC_TOLERANCE_PX: number = 2;

  // KOA-17505 completion: an overlay (Modal / Drawer / Float Panel) can make antd rebuild
  // the body rows and reset their widths without changing any layout-relevant prop, so
  // shouldResyncColumnWidths never schedules a resync and a split (fixedHeader) table
  // stays desynced until some unrelated change. The scroll-activity tick is the missing
  // trigger: a cheap total-width probe schedules the full resync, whose
  // fixedColumnsMisaligned check then defeats the width cache and re-applies the widths.
  requestSyncIfFixedColumnsDesynced = (wrapper: HTMLElement): void => {
    if (!this.props.fixedHeader) {
      return;
    }
    const headerTable: HTMLElement | null = wrapper.querySelector<HTMLElement>(".ant-table-header table");
    const bodyTable: HTMLElement | null = wrapper.querySelector<HTMLElement>(".ant-table-body table");
    if (!headerTable || !bodyTable) {
      return;
    }
    const headerWidth: number = headerTable.getBoundingClientRect().width;
    const bodyWidth: number = bodyTable.getBoundingClientRect().width;
    if (Math.abs(headerWidth - bodyWidth) > BaseTable.FIXED_DESYNC_TOLERANCE_PX) {
      this.requestSyncColumnWidths();
    }
  };

  // Mirror the table's scroll geometry onto the pinned proxy bar: the spacer reproduces the
  // body's scrollWidth so the proxy thumb has the right proportions, and the two scroll
  // offsets are kept equal. The <1px guard breaks the mirror/counter-mirror cycle between
  // the proxy's onScroll and the capture-phase window scroll listener. Returns false when
  // the table has no horizontal overflow, so the bar never shows for a table that fits.
  syncPinnedScrollbarFromTable = (wrapper: HTMLElement, proxyBar: HTMLDivElement): boolean => {
    const scrollContainer: HTMLElement | null = this.getHorizontalScrollContainer(wrapper);
    const spacer: HTMLDivElement | null = this.pinnedScrollbarSpacerEl;
    if (!scrollContainer || !spacer) {
      return false;
    }
    const hasHorizontalOverflow: boolean = scrollContainer.scrollWidth - scrollContainer.clientWidth > 1;
    if (!hasHorizontalOverflow) {
      return false;
    }
    // Write-guard: assigning an unchanged width every tick would dirty style state between
    // the layout reads above and the rect reads of sibling tables in the same frame.
    const spacerWidth: string = `${scrollContainer.scrollWidth}px`;
    if (spacer.style.width !== spacerWidth) {
      spacer.style.width = spacerWidth;
    }
    if (Math.abs(proxyBar.scrollLeft - scrollContainer.scrollLeft) >= 1) {
      proxyBar.scrollLeft = scrollContainer.scrollLeft;
    }
    return true;
  };

  // Dragging the proxy bar drives the real scroll container. The resulting container scroll
  // event reaches the capture-phase window listener, which re-runs the mirror above and
  // finds the offsets already equal — no feedback loop.
  handlePinnedScrollbarScroll = (): void => {
    const wrapper: HTMLElement | null = this.getWrapperElement();
    const proxyBar: HTMLDivElement | null = this.pinnedScrollbarEl;
    if (!wrapper || !proxyBar) {
      return;
    }
    const scrollContainer: HTMLElement | null = this.getHorizontalScrollContainer(wrapper);
    if (!scrollContainer) {
      return;
    }
    if (Math.abs(scrollContainer.scrollLeft - proxyBar.scrollLeft) >= 1) {
      scrollContainer.scrollLeft = proxyBar.scrollLeft;
    }
  };

  handleStickyScrollBarPointer = (event: MouseEvent): void => {
    const target: HTMLElement | null = event.target instanceof HTMLElement ? event.target : null;
    this.stickyScrollBarHovered = target?.closest?.(".kuika-table-pinned-hscroll") != null;
  };

  // Height of the hover band above the viewport's bottom edge that reveals the pinned bar.
  private static readonly STICKY_SCROLL_HOVER_BAND_PX: number = 64;

  // Scroll activity alone leaves mouse users stranded: after the fade there is nothing to
  // grab and the invisible bar ignores the pointer. Moving the pointer into the bottom band
  // of the viewport (where the bar lives) reveals it without scrolling — the mouse-user
  // counterpart of macOS showing scrollbars permanently when a mouse is connected.
  handleStickyScrollPointerMove = (event: MouseEvent): void => {
    if (event.clientY < window.innerHeight - BaseTable.STICKY_SCROLL_HOVER_BAND_PX) {
      return;
    }
    this.handleStickyScrollActivity();
  };

  // Called from the proxy bar's callback ref, so the render-time
  // shouldPinHorizontalScrollbar decision is already embodied by the element existing.
  // addEventListener with an identical (type, handler, capture) tuple is idempotent, so a
  // re-fired ref cannot double-register.
  attachStickyScrollListeners = (): void => {
    window.addEventListener("scroll", this.handleStickyScrollActivity, true);
    window.addEventListener("resize", this.handleStickyScrollActivity);
    window.addEventListener("mouseover", this.handleStickyScrollBarPointer, true);
    window.addEventListener("mousemove", this.handleStickyScrollPointerMove, true);
  };

  detachStickyScrollListeners = (): void => {
    window.removeEventListener("scroll", this.handleStickyScrollActivity, true);
    window.removeEventListener("resize", this.handleStickyScrollActivity);
    window.removeEventListener("mouseover", this.handleStickyScrollBarPointer, true);
    window.removeEventListener("mousemove", this.handleStickyScrollPointerMove, true);
    if (this.stickyScrollHideTimerId !== undefined) {
      clearTimeout(this.stickyScrollHideTimerId);
      this.stickyScrollHideTimerId = undefined;
    }
    if (this.stickyScrollRafId !== undefined) {
      window.cancelAnimationFrame(this.stickyScrollRafId);
      this.stickyScrollRafId = undefined;
    }
  };

  getDynamicCss = (): string => {
    const className: string = this.getClassName();
    if (!className || className.length === 0) {
      return "";
    }
    let result = "";
    const { maxHeight, boxShadow, height, minHeight, padding, paddingRight, paddingLeft, paddingTop, paddingBottom } =
      this.props.style;
    const hasFixedColumns = this.state.columns?.some((col: any) => col.fixed === "left" || col.fixed === "right");

    if (hasFixedColumns) {
      result += BaseTable.buildPinnedCellBorderCss(className.trim(), this.isHeaderInBodyTable());
    }

    if (window.kuika?.dashboardState === 17) {
      const { borderWidth, borderColor, borderLeftWidth, borderRightWidth, borderTopWidth, borderBottomWidth } =
        this.props.style;
      if (borderWidth || borderColor) {
        result += `.${className.trim()} .ant-table-content {
              border-left-width: ${borderLeftWidth}${borderLeftWidth ? "px" : ""} !important;
              border-right-width: ${borderRightWidth}${borderRightWidth ? "px" : ""} !important;
              border-top-width: ${borderTopWidth}${borderTopWidth ? "px" : ""} !important;
              border-bottom-width: ${borderBottomWidth}${borderBottomWidth ? "px" : ""} !important;
              border-color: ${borderColor} !important;
              border-style: solid !important;
            }`;
        result += `.${className.trim()} .ant-table-content table {
              border: none !important;
            }`;
      }
    }

    if (maxHeight || height || minHeight) {
      if (height) {
        result += `.${className.trim()} .ant-table-wrapper {
              height: ${height} !important;
            }`;
        result += `.${className.trim()} .ant-table-content table {
              min-height: ${height} !important;
            }`;
      }
      if (minHeight) {
        result += `.${className.trim()} .ant-table-wrapper {
              min-height: ${minHeight} !important;
            }`;
      }
      if (maxHeight) {
        result += `.${className.trim()} .ant-table-wrapper {
                max-height: ${maxHeight} !important;
                overflow-y: scroll !important;
              }`;
      }
      if (height || maxHeight) {
        if (this.props.pagination) {
          result += `.${className.trim()} .ant-pagination {
              background: white;
              position: sticky;
              bottom: 0;
              margin-bottom: 0 !important;
              height: 36px;
              align-items: center;
              margin-right: 2px !important;
                }`;
        }
      }
    }

    result += `.${className.trim()} table {
        border-radius: ${this.props.style?.borderRadius ?? 0}px !important;
      }`;

    if (this.props.fixedHeader && !hasFixedColumns) {
      result += `.${className.trim()} .ant-table-thead {
            position: sticky;
            top: 0;
            background: #fff;
            z-index: 5;
        }`;
    } else if (this.props.fixedHeader && hasFixedColumns) {
      result += `.${className.trim()} .ant-table-header table {
            table-layout: auto !important;
        }`;

      if (maxHeight || height || minHeight) {
        if (height) {
          result += `.${className.trim()} .ant-table-wrapper {
              height: auto !important;
            }`;
        }
        if (maxHeight) {
          result += `.${className.trim()} .ant-table-wrapper {
                max-height: none !important;
                overflow-y: hidden !important;
              }`;
        }
      }
    }

    if (this.props.searchable && this.props.sorter && !this.props.filtering) {
      result += `.${className.trim()} .ant-table-thead > tr > th > .ant-table-column-sorters {
            padding-bottom: 36px;
        }`;
    }

    if (boxShadow && !this.props.pagination) {
      result += `.${className.trim()} {
          box-shadow: ${boxShadow} !important;
        }`;
    }

    if (this.props?.striped) {
      result += `.${className.trim()} table tr:nth-child(2n) {
            background-color: #F5F7FA;
        }`;
    }

    if (this.props?.striped && this.props?.stripedColor) {
      result += `.${className.trim()} table tr:nth-child(2n) {
            background-color: ${this.props?.stripedColor};
        }`;
    }

    if (
      (padding || paddingRight || paddingLeft || paddingTop || paddingBottom) &&
      window?.kuika?.dashboardState === DashboardState.reportDesigner
    ) {
      result += `.${className.trim()} .ant-table {
          padding: ${paddingTop ?? 0}px ${paddingRight ?? 0}px ${paddingBottom ?? 0}px ${paddingLeft ?? 0}px !important;
        }`;
    }

    if (window?.kuika?.dashboardState !== DashboardState.reportDesigner) {
      if (this.props.tableWidthMode === "horizontal scroll" || this.props.tableWidthMode === "scroll") {
        result += `
        .${className.trim()} .ant-table-wrapper,
        .${className.trim()} .ant-spin-nested-loading,
        .${className.trim()} .ant-spin-container,
        .${className.trim()} .ant-table,
        .${className.trim()} .ant-table-container {
          width: 100% !important;
          max-width: 100% !important;
          overflow: visible !important;
        }
        .${className.trim()} .ant-table-content {
          width: 100% !important;
          max-width: 100% !important;
          overflow-x: auto !important;
          overflow-y: hidden !important;
          -webkit-overflow-scrolling: touch !important;
        }
      `;
      } else {
        result += `.${className.trim()} .ant-table-wrapper {
        overflow-x: hidden !important;
        overflow-y: auto !important;
      }
      .${className.trim()} .ant-table-content {
        overflow-x: auto !important;
      }`;
      }
    }

    if (this.props.paginationUnselectedTextColor) {
      result += `.${className.trim()} .ant-pagination .ant-pagination-item {
        color: ${this.props.paginationUnselectedTextColor} !important;
      }`;
      result += `.${className.trim()} .ant-pagination .ant-pagination-item a {
        color: ${this.props.paginationUnselectedTextColor} !important;
      }`;
      result += `.${className.trim()} .ant-pagination .ant-pagination-prev .ant-pagination-item-link,
      .${className.trim()} .ant-pagination .ant-pagination-next .ant-pagination-item-link {
        color: ${this.props.paginationUnselectedTextColor} !important;
      }`;
      result += `.${className.trim()} .ant-pagination .ant-pagination-total-text {
        color: ${this.props.paginationUnselectedTextColor} !important;
      }`;
      result += `.${className.trim()} .ant-pagination .ant-pagination-options .ant-pagination-options-size-changer {
        color: ${this.props.paginationUnselectedTextColor} !important;
      }`;

      result += `.${className.trim()} .ant-pagination .ant-pagination-options-size-changer .ant-select-selector .ant-select-selection-item {
        color: ${this.props.paginationUnselectedTextColor} !important;
      }`;
      result += `.${className.trim()} .ant-pagination .ant-pagination-options-size-changer .ant-select-arrow {
        color: ${this.props.paginationUnselectedTextColor} !important;
      }`;
    }

    if (this.props.paginationSelectedTextColor) {
      result += `.${className.trim()} .ant-pagination .ant-pagination-item-active {
        color: ${this.props.paginationSelectedTextColor} !important;
        border-color: ${this.props.paginationSelectedTextColor} !important;
      }`;
      result += `.${className.trim()} .ant-pagination .ant-pagination-item-active a {
        color: ${this.props.paginationSelectedTextColor} !important;
      }`;
      result += `.${className.trim()} .ant-pagination .ant-pagination-item:hover {
        color: ${this.props.paginationSelectedTextColor} !important;
      }`;
      result += `.${className.trim()} .ant-pagination .ant-pagination-item:hover a {
        color: ${this.props.paginationSelectedTextColor} !important;
      }`;
      result += `.${className.trim()} .ant-pagination .ant-pagination-prev:hover .ant-pagination-item-link,
      .${className.trim()} .ant-pagination .ant-pagination-next:hover .ant-pagination-item-link {
        color: ${this.props.paginationSelectedTextColor} !important;
      }`;
      result += `.${className.trim()} .ant-pagination .ant-pagination-item-link-icon {
        color: ${this.props.paginationSelectedTextColor} !important;
      }`;
      result += `.${className.trim()} .ant-pagination .ant-pagination-options-size-changer .ant-select-selector:hover,
      .${className.trim()} .ant-pagination .ant-pagination-options-size-changer .ant-select-selector:focus,
      .${className.trim()} .ant-pagination .ant-pagination-options-size-changer .ant-select-selector:active,
      .${className.trim()} .ant-pagination .ant-pagination-options-size-changer.ant-select-focused .ant-select-selector {
        border-color: ${this.props.paginationSelectedTextColor} !important;
        box-shadow: none !important;
      }`;
    }

    if (
      this.state.headerStyle?.borderWidth ||
      this.state.headerStyle?.borderBottomWidth ||
      this.state.headerStyle?.borderTopWidth ||
      this.state.headerStyle?.borderLeftWidth ||
      this.state.headerStyle?.borderRightWidth
    ) {
      result += `.${className.trim()} .ant-table-thead > tr > th::before {
        content: none !important;
      }`;
    }

    if (window.kuika?.dashboardState === DashboardState.reportDesigner && this.state.headerStyle?.borderColor) {
      result += `.${className.trim()} .ant-table-thead > tr > th::before {
        background-color: ${this.state.headerStyle.borderColor} !important;
      }`;
    }

    if (window.kuika?.dashboardState === DashboardState.reportDesigner && this.state.headerStyle) {
      const headerStyle = this.state.headerStyle;
      const fallbackWidth: number | string | undefined = headerStyle.borderWidth;
      const topWidth: number | string | undefined = headerStyle.borderTopWidth ?? fallbackWidth;
      const bottomWidth: number | string | undefined = headerStyle.borderBottomWidth ?? fallbackWidth;
      const leftWidth: number | string | undefined = headerStyle.borderLeftWidth ?? fallbackWidth;
      const rightWidth: number | string | undefined = headerStyle.borderRightWidth ?? fallbackWidth;

      if (topWidth || bottomWidth || leftWidth || rightWidth) {
        const rawBorderStyle: string = headerStyle.borderStyle;
        const headerBorderStyle: string = rawBorderStyle && rawBorderStyle !== "none" ? rawBorderStyle : "solid";
        const headerBorderColor: string = headerStyle.borderColor || "rgba(0, 0, 0, 1)";
        const toBorderCss = (width: number | string | undefined, side: string): string => {
          if (!width) {
            return "";
          }
          const widthPx: string = typeof width === "number" ? `${width}px` : width;
          return `border-${side}: ${widthPx} ${headerBorderStyle} ${headerBorderColor} !important;`;
        };

        // Canvas parity with the iText PDF generator: frame the header ROW, not each cell.
        // The configured borderWidth is otherwise dropped because borders set on the <thead>
        // (table-header.tsx) are never painted by the browser. So paint them on the antd cells:
        // top/bottom on every header cell (continuous lines), left only on the first cell and
        // right only on the last cell — matching ApplyColumnBorders' suppressLeft/suppressRight.
        result += `.${className.trim()} .ant-table-thead > tr > th {
          ${toBorderCss(topWidth, "top")}
          ${toBorderCss(bottomWidth, "bottom")}
        }`;
        if (leftWidth) {
          result += `.${className.trim()} .ant-table-thead > tr > th:first-child {
            ${toBorderCss(leftWidth, "left")}
          }`;
        }
        if (rightWidth) {
          result += `.${className.trim()} .ant-table-thead > tr > th:last-child {
            ${toBorderCss(rightWidth, "right")}
          }`;
        }

        // Full-height vertical divider between adjacent header columns — matches the PDF
        // generator's {Name}leftBorder (a left border on every cell except the first).
        // The antd ::before separator is removed once a header border width is set
        // (see the th::before content:none block above), so re-draw it as a real border.
        const dividerWidth: number | string | undefined = leftWidth ?? rightWidth ?? topWidth ?? bottomWidth;
        result += `.${className.trim()} .ant-table-thead > tr > th:not(:first-child) {
          ${toBorderCss(dividerWidth, "left")}
        }`;
      }
    }

    if (window.kuika?.dashboardState === DashboardState.reportDesigner) {
      this.state.columns?.forEach((col: any, index: number) => {
        const colPaddingLeft = col?.style?.paddingLeft ?? 0;
        result += `.${className.trim()} .ant-table-thead > tr > th:nth-child(${index + 1}) {
          padding-left: ${colPaddingLeft}px !important;
        }`;
      });
    }

    const dataSourceLength = this.getFilteredAndSortedDataSource()?.length || 0;
    const totalPages = Math.ceil(dataSourceLength / this.state.pageSize);

    if (totalPages <= 1 && !window.kuika?.isDesignTime) {
      result += `.${className.trim()} .ant-pagination-prev, .${className.trim()} .ant-pagination-next, .${className.trim()} .ant-pagination-item {
        display: none;
      }`;
    }

    return result;
  };

  setDynamicStyle = () => {
    this.style.setDynamicCss(this.getDynamicCss());
  };

  getClassName = () => {
    let result = "";
    if (!this.state.uniqueKey) {
      return result;
    }
    result = `${result} ktable_${this.state.uniqueKey.toString().substring(0, 8)}`;
    return result;
  };

  hideNoDataIfNotNeeded = (props: any) => {
    if (!props.showNoDataFound) {
      const node = document.getElementById(props.id);
      if (node) {
        const empty: HTMLCollectionOf<Element> = node.getElementsByClassName("ant-empty");
        if (empty.length > 0) {
          (empty[0] as HTMLElement).style.display = "none";
        }
      }
    }
  };

  setBackgroundColor = (element: any, props?: any) => {
    if (props && props.style && props.style.backgroundColor) {
      element.style["background-color"] = props.style.backgroundColor;
    }
  };

  setAlignments = (element: any, index: number) => {
    const isDesignTime = window.kuika?.isDesignTime;
    let columnStyles: React.CSSProperties = {};
    let elementsDirection: "horizontal" | "vertical" | undefined;
    if (this.props?.children[1]?.props?.children?.length > 0) {
      const columnIndex = index % this.props?.children[1]?.props?.children.length;
      const columnProps = this.props?.children[1]?.props?.children[columnIndex]?.props;
      columnStyles = columnProps?.style;
      elementsDirection = columnProps?.elementsDirection;
    }
    let node: any = element.firstChild;
    if (!node) {
      return;
    }
    if (!isDesignTime) {
      node = element;
    }
    KMainFunctions.handleAlignments(node, columnStyles, false, elementsDirection);
    if (!isDesignTime) {
      node.style.display = "table-cell";
      node.style.verticalAlign =
        columnStyles.alignItems === "center"
          ? "middle"
          : columnStyles.alignItems === "flex-end"
            ? "bottom"
            : columnStyles.alignItems === "flex-start"
              ? "top"
              : "center";
    }
  };

  // Decide whether a re-render should trigger a fixed-header column-width resync.
  // Only inputs that actually affect column geometry count. Previously this used
  // `prevProps !== this.props`, which is a new object reference on essentially every
  // parent render, so the table re-measured (and visibly reshuffled) its columns on
  // every unrelated change — the "widths don't hold on the smallest change" half of
  // KOA-16782. Layout-relevant props are compared by value so unrelated prop churn
  // (event-handler identity, selectedValues, hover state, etc.) no longer resyncs.
  static shouldResyncColumnWidths(
    prevProps: ITableProps,
    nextProps: ITableProps,
    prevState: ITableState | undefined,
    nextState: ITableState
  ): boolean {
    const stateChanged: boolean =
      prevState?.columns !== nextState.columns ||
      prevState?.hiddenColumnList !== nextState.hiddenColumnList ||
      prevState?.columnOrder !== nextState.columnOrder ||
      prevState?.columnWidths !== nextState.columnWidths ||
      prevState?.pageSize !== nextState.pageSize ||
      prevState?.current !== nextState.current;
    if (stateChanged) {
      return true;
    }

    const propChanged: boolean =
      prevProps.fixedHeader !== nextProps.fixedHeader ||
      prevProps.mode !== nextProps.mode ||
      prevProps.tableWidthMode !== nextProps.tableWidthMode ||
      prevProps.showRowNumbers !== nextProps.showRowNumbers ||
      prevProps.multiSelect !== nextProps.multiSelect ||
      prevProps.size !== nextProps.size ||
      prevProps.pagination !== nextProps.pagination ||
      prevProps.pageSize !== nextProps.pageSize ||
      prevProps.dataSource !== nextProps.dataSource;
    if (propChanged) {
      return true;
    }

    // Container dimensions drive the width available for column sizing.
    const prevStyle = prevProps.style ?? {};
    const nextStyle = nextProps.style ?? {};
    return (
      prevStyle.width !== nextStyle.width ||
      prevStyle.height !== nextStyle.height ||
      prevStyle.minHeight !== nextStyle.minHeight ||
      prevStyle.maxHeight !== nextStyle.maxHeight
    );
  }

  componentDidUpdate(prevProps: ITableProps, prevState: any) {
    this.setStyle(prevProps);
    this.hideNoDataIfNotNeeded(prevProps);

    if (prevProps.selectedValues !== this.props.selectedValues) {
      const selectedRowKeys =
        this.props.selectedValues && (this.props.selectedValues as any) !== ""
          ? this.props.selectedValues.split(",")
          : [];
      this.setState({
        selectedRowKeys,
        selectedRowKeysString: this.props.selectedValues || ""
      });
    }

    const isEql = isEqual(this.props.dataSource, prevProps.dataSource);
    if (this.state.activeEditableRowIndex != -1 && !isEql && !window.kuika?.isDesignTime) {
      let formValues = {};
      const filteredList = this.getFilteredAndSortedDataSource();
      let rowValue = filteredList[this.state.activeEditableRowIndex];
      if (rowValue) {
        Object.keys(rowValue).forEach((cellfieldName) => {
          let key = `Table_${this.props.id}_${cellfieldName}`;
          formValues[key] = rowValue[cellfieldName];
        });
        this.props.form?.setFieldsValue(formValues);
        this.randomGuid = Guid.create().toString();
        this.setState({ activeEditableRowIndex: -1 } as any);
      }
    } else if (
      this.state.activeEditableRowIndex != -1 &&
      isEql &&
      prevState?.activeEditableRowIndex != -1 &&
      !window.kuika.isDesignTime
    ) {
      this.randomGuid = Guid.create().toString();
      this.setState({ activeEditableRowIndex: -1 } as any);
    }

    const shouldSyncColumnWidths: boolean = BaseTable.shouldResyncColumnWidths(
      prevProps,
      this.props,
      prevState,
      this.state
    );

    if (shouldSyncColumnWidths) {
      this.requestSyncColumnWidths();
    }

    // Persist any change to the 7 view-config fields (order/visibility/width/filter/sort/search).
    // Reference comparison is reliable: each mutation replaces the field with a new object/array.
    if (this.isAutoPersistEnabled() && !this.isApplyingLoadedConfig) {
      const configChanged: boolean =
        prevState?.columnOrder !== this.state.columnOrder ||
        prevState?.hiddenColumnList !== this.state.hiddenColumnList ||
        prevState?.columnWidths !== this.state.columnWidths ||
        prevState?.filteringOptions !== this.state.filteringOptions ||
        prevState?.sorterOptions !== this.state.sorterOptions ||
        prevState?.searchText !== this.state.searchText ||
        prevState?.columnSearchTexts !== this.state.columnSearchTexts;
      if (configChanged) {
        this.persistTableConfigDebounced();
      }
    }
  }

  componentWillUnmount(): void {
    // Flush any pending save so a fast navigate-away still captures the last change.
    // Optional-chained: real lodash debounce exposes flush(); test mocks may omit it.
    this.persistTableConfigDebounced.flush?.();
    this.detachStickyScrollListeners();
    this.pinnedOffsetStyle.removeStyle();
    if (this.syncWidthsTimerId !== undefined) {
      clearTimeout(this.syncWidthsTimerId);
    }
    if (this.tableElement) {
      this.tableElement.removeEventListener("keydown", this.handleKeyDown);
      this.tableElement.removeEventListener("click", this.handleClick);
      this.tableElement.removeEventListener("keydown", this.handleTableKeyDown);
      this.tableElement = null;
    }

    window.removeEventListener("mousemove", this.resizeListener);
  }

  // ── Auto-persist of table view config ─────────────────────────────────────────────
  // Trigger = any interactive view feature is enabled, so a resize-only / sort-only / filter-only
  // table persists too — not just ColumnChooser. Each feature drives one of the 7 saved fields
  // (columnChooser→order/visibility, resizableColumns→widths, sorter→sort, filtering→filter,
  // globalSearch→search, searchable→column-search).
  private isAutoPersistEnabled = (): boolean =>
    this.props.columnChooser === true ||
    this.props.resizableColumns === true ||
    this.props.filtering === true ||
    this.props.sorter === true ||
    this.props.globalSearch === true ||
    this.props.searchable === true;

  // Guards the echo write when we apply a freshly loaded config on mount.
  private isApplyingLoadedConfig = false;

  // Debounced so rapid changes (column resize drags, successive toggles) collapse to one write.
  private persistTableConfigDebounced = _.debounce(() => this.autoPersistSaveConfig(), 600);

  // Logged-in → DB (per-user, cross-device); anonymous → localStorage (per-device). Single source
  // at a time: the DB path never reads localStorage and vice-versa, so the two never cross-contaminate.
  private isUserLoggedIn = (): boolean => KuikaAppManager.readLoginStateCookie("is_logged_in") === "1";

  private getTableConfigStorageKey = (): string => `kuika_tblcfg_${this.props.screenId || ""}_${this.props.id || ""}`;

  private loadPersistedTableConfig = async (): Promise<void> => {
    if (!this.isAutoPersistEnabled() || !this.props.id) return;

    let configJson = "";
    if (this.isUserLoggedIn()) {
      configJson = await TableConfigService.getTableConfig(this.props.screenId || "", this.props.id);
    } else {
      try {
        configJson = window.localStorage.getItem(this.getTableConfigStorageKey()) || "";
      } catch {
        configJson = "";
      }
    }

    // setTableConfigJson sets the echo-suppression guard itself, so applying the loaded
    // config here does not trigger a save-back.
    if (configJson) {
      this.setTableConfigJson(configJson);
    }
  };

  private autoPersistSaveConfig = (): void => {
    if (!this.isAutoPersistEnabled() || !this.props.id) return;

    const configJson = this.getTableConfigJson();
    if (this.isUserLoggedIn()) {
      void TableConfigService.saveTableConfig(this.props.screenId || "", this.props.id, configJson);
    } else {
      try {
        window.localStorage.setItem(this.getTableConfigStorageKey(), configJson);
      } catch {
        // storage unavailable (private mode / quota exceeded); best-effort
      }
    }
  };

  componentDidMount = () => {
    this.setStyle(this.props);
    this.hideNoDataIfNotNeeded(this.props);
    this.setState({
      pageSize: this.props.pageSize && this.props.pageSize >= 1 ? this.props.pageSize : 10
    });

    setTimeout(() => {
      const tableElement = document.getElementById(this.props.id);
      if (tableElement) {
        tableElement.addEventListener("keydown", this.handleKeyDown);
        tableElement.addEventListener("click", this.handleClick);
        tableElement.addEventListener("keydown", this.handleTableKeyDown);

        this.tableElement = tableElement;
      }

      this.requestSyncColumnWidths();
    }, 100);

    if (this.isAutoPersistEnabled()) {
      void this.loadPersistedTableConfig();
    }
  };

  getRowBgColor = (rowData: any, rowIndex?: number): string | undefined => {
    if (!rowData) return undefined;
    if (
      this.state.selectedRowKeys?.some((k) => k?.toString()?.toLowerCase() === rowData.id?.toString()?.toLowerCase()) &&
      this.props.selectedRowColor
    ) {
      return this.props.selectedRowColor;
    }
    if (this.props.rowBgColor) {
      return this.props.rowBgColor(rowData, rowIndex);
    }
    return undefined;
  };

  getRowFontColor = (rowData: any, rowIndex?: number): string | undefined => {
    if (this.props.rowFontColor) {
      return this.props.rowFontColor(rowData, rowIndex);
    }
    return undefined;
  };

  getRowStyle = (rowValue: any, index: number | undefined): any | undefined => {
    let style: any | undefined = _.clone(this.state.rowStyle);
    if (!style) {
      style = {};
    }
    const color = this.getRowFontColor(rowValue, index);
    if (color) {
      style.color = color;
    }
    const backgroundColor = this.getRowBgColor(rowValue, index);
    if (backgroundColor) {
      style.backgroundColor = backgroundColor;
    }
    return style;
  };

  getViewWrapper = () => {
    switch (this.props.mode) {
      case "table":
        return (props) => (
          <TableViewWrapper
            {...props}
            style={this.props.style}
            scrollable={this.props.tableWidthMode === "horizontal scroll" || this.props.tableWidthMode === "scroll"}
          ></TableViewWrapper>
        );
      case "carousel":
        return (props) => <CarouselViewWrapper {...props} style={this.props.style}></CarouselViewWrapper>;
      case "google-maps":
        return (props) => <GoogleMapsViewWrapper {...props} style={this.props.style}></GoogleMapsViewWrapper>;
      case "gallery":
        return (props) => <GalleryViewWrapper {...props} style={this.props.style}></GalleryViewWrapper>;
      case "flex-grid":
        return (props) => <FlexGridViewWrapper {...props} style={this.props.style} />;
    }
  };

  refreshGoogleMapsBodyWrapper: ((nextProps?: any, callBack?: any) => void) | undefined;

  getBodyWrapper = () => {
    switch (this.props.mode) {
      case "table":
        return TableBodyWrapper;
      case "carousel":
        return (props) => (
          <CarouselBodyWrapper
            {...props}
            {...this.getCarouselBodyWrapperProps()}
            setCurrentSlideNumber={(val: any) => {
              this.setState({ carouselStartingIndex: val });
            }}
            startingIndex={this.state.carouselStartingIndex}
          />
        );
      case "google-maps":
        return (props) => (
          <RefreshDelegate
            {...props}
            setRefreshDelegate={(refreshMethod) => {
              this.refreshGoogleMapsBodyWrapper = refreshMethod;
            }}
            {...this.getGoogleMapsBodyWrapperProps()}
            Component={isAmapProvider() ? AmapBodyWrapper : GoogleMapsBodyWrapper}
          />
        );
      case "gallery":
        return (props) => <GalleryBodyWrapper {...props} {...this.getGalleryBodyWrapperProps()}></GalleryBodyWrapper>;
      case "flex-grid":
        return (props) => <FlexGridBodyWrapper {...props} {...this.getGalleryBodyWrapperProps()} />;
    }
  };

  getBodyCell = () => {
    switch (this.props.mode) {
      case "table":
        return (props) => <TableBodyCell {...props} parentTable={this} />;
      case "carousel":
        return CarouselBodyCell;
      case "google-maps":
        return GoogleMapsBodyCell;
      case "gallery":
        return GalleryBodyCell;
      case "flex-grid":
        return FlexGridBodyCell;
    }
  };

  getCarouselBodyWrapperProps = () => {
    const result: CarouselBodyWrapperProps = {};
    const p = this.props as CarouselBodyWrapperProps;
    if (p.autoplaySpeed) result.autoplaySpeed = p.autoplaySpeed;
    if (p.autoplay) result.autoplay = p.autoplay;
    if (p.fade) result.fade = p.fade;
    if (p.pauseOnDotsHover) result.pauseOnDotsHover = p.pauseOnDotsHover;
    // pauseOnHover defaults to true and would mask pauseOnDotsHover; force it off
    // so pauseOnDotsHover alone controls hover-pause.
    result.pauseOnHover = false;
    if (p.slidesToScroll) result.slidesToScroll = p.slidesToScroll;
    if (p.dots === false) {
      result.dots = false;
    }
    if (p.dots === undefined || p.dots === true) {
      result.dots = true;
    }
    if (p.arrows === false) {
      result.arrows = false;
    }
    if (p.arrows === undefined || p.arrows === true) {
      result.arrows = true;
    }
    if (p.slidesToShow) result.slidesToShow = p.slidesToShow;
    if (p.speed) result.speed = p.speed;
    if (p.swipe) result.swipe = p.swipe;
    if (p.cssEase) result.cssEase = p.cssEase;
    if (p.vertical) result.vertical = p.vertical;
    if (p.onChange) result.onChange = p.onChange;
    if (p.startingIndex && p.startingIndex > 0) {
      result.startingIndex = p.startingIndex;
      this.setState({ carouselStartingIndex: p.startingIndex as any });
    }
    if (p.hoverBgColor) result.hoverBgColor = p.hoverBgColor;
    if (p.hoverFontColor) result.hoverFontColor = p.hoverFontColor;
    if (p.arrowColor) result.arrowColor = p.arrowColor;
    if (p.showPageIndicator !== undefined) result.showPageIndicator = p.showPageIndicator;
    if (this.props.dataSource) result.totalSlides = this.props.dataSource.length;
    return result;
  };

  getLatValue = () => {
    if (this.lastSelectedLat !== undefined) {
      return this.lastSelectedLat;
    }
    return "";
  };

  getLngValue = () => {
    if (this.lastSelectedLng !== undefined) {
      return this.lastSelectedLng;
    }
    return "";
  };

  getGoogleMapsBodyWrapperProps = () => {
    const result: GoogleMapsBodyWrapperProps = {} as any;
    const p = this.props;
    result.value = this.state.value;
    if (p.style) result.style = p.style;
    if (p.latitude) result.latitude = p.latitude;
    if (p.longitude) result.longitude = p.longitude;
    if (p.zoom) result.zoom = p.zoom;
    if (p.dataLatField) result.dataLatField = p.dataLatField;
    if (p.dataLngField) result.dataLngField = p.dataLngField;
    if (p.markerDetailView) result.markerDetailView = p.markerDetailView;
    if (p.markerDetailPosition) result.markerDetailPosition = p.markerDetailPosition;
    if (p.markerDetailHeight) result.markerDetailHeight = p.markerDetailHeight;
    if (p.markerDetailWidth) result.markerDetailWidth = p.markerDetailWidth;
    if (p.designClicked) result.designClicked = p.designClicked;
    result.isPicker = p.isPicker;
    result.isMarkerPicker = p.isMarkerPicker;
    result.hasSearchBox = p.hasSearchBox;
    result.disableDoubleClickZoom = p.disableDoubleClickZoom;
    result.fullScreenControlV1 = p.fullScreenControlV1;
    result.streetViewControlV1 = p.streetViewControlV1;
    result.scaleControlV1 = p.scaleControlV1;
    result.zoomControlV1 = p.zoomControlV1;
    result.panControl = p.panControl;
    result.rotateControlV1 = p.rotateControlV1;
    result.cameraControl = p.cameraControl;
    result.mapTypeControl = p.mapTypeControl;
    result.autoCenter = p.autoCenter;
    result.circleRadius = p.circleRadius;
    result.mapType = p.mapType;
    result.hideTrafficLayer = p.hideTrafficLayer;
    result.hideTransitLayer = p.hideTransitLayer;
    result.enableLocationTracking = p.enableLocationTracking;
    result.waypointLatField = p.waypointLatField;
    result.waypointLngField = p.waypointLngField;
    result.waypoints = p.waypoints;
    result.onClusterElementClicked = (e, rowValue, rowData) => {
      this.setState({ isDataMarkerSelected: true as any }, () => {
        this.handleRowClick(e, rowValue, rowData);
      });
    };

    result.clearActiveContent = () => {
      if (this.refreshGoogleMapsBodyWrapper) {
        this.refreshGoogleMapsBodyWrapper({
          activeContent: undefined,
          designClicked: false
        });
      }
    };
    result.onChange = (isDataMarkerSelected: boolean, value?: ICoords) => {
      this.setState({ value, isDataMarkerSelected } as any, () => {
        if (this.refreshGoogleMapsBodyWrapper) {
          let wrapperProps: any = { value, isDataMarkerSelected };
          if (isDataMarkerSelected !== true) {
            wrapperProps = {
              value,
              isDataMarkerSelected,
              activeContent: undefined
            };
          }
          this.refreshGoogleMapsBodyWrapper(wrapperProps, () => {
            if (p.onChange) {
              this.lastSelectedLat = value?.latitude;
              this.lastSelectedLng = value?.longitude;
              p.onChange(value);
            }
          });
        }
      });
    };
    result.onGoogleApiLoaded = (map: any, maps: any) => {
      this.setState({ map, maps }, () => {
        if (this.refreshGoogleMapsBodyWrapper) {
          this.refreshGoogleMapsBodyWrapper({ map, maps }, () => {});
        }
      });
    };
    result.maps = this.state.maps;
    result.map = this.state.map;
    result.isDataMarkerSelected = this.state.isDataMarkerSelected;
    result.groupPickerIcon = p.groupPickerIcon;
    result.groupPickerColor = p.groupPickerColor;
    result.currentLocationIcon = p.currentLocationIcon;
    result.currentLocationColor = p.currentLocationColor;
    return result;
  };

  getGalleryBodyWrapperProps = () => {
    const result: GalleryBodyWrapperProps = {};
    const p = this.props as GalleryBodyWrapperProps;
    if (p.horizontalGutter) result.horizontalGutter = p.horizontalGutter;
    if (p.verticalGutter) result.verticalGutter = p.verticalGutter;
    return result;
  };

  public getSelectedIndex = () => {
    const selectedIndexHiddenInput = this.getSelectedRowIndexHiddenInput();

    if (selectedIndexHiddenInput) {
      return selectedIndexHiddenInput.value;
    }
  };

  public getSelectedValues = () => {
    // For a nested table the shared ref points at the last-mounted instance, so prefer the
    // selection of the parent row the user most recently changed. Top-level tables return null
    // here and fall back to their own state.
    const activeNestedSelection: string | null = this.getActiveNestedSelectedValues();
    if (activeNestedSelection !== null) {
      return activeNestedSelection;
    }
    return this.state.selectedRowKeysString;
  };

  public getSelectedCount = () => {
    return this.state.selectedRowKeys?.length || 0;
  };

  getNestedTableSelectedIndex = (parentTableDomEl: HTMLElement, nestedTableId?: string) => {
    const selectedIndex = this.getSelectedIndex();

    const tbody = parentTableDomEl.querySelector(".ant-table-tbody");

    if (!tbody) {
      return;
    }

    const rows = tbody.querySelectorAll(`:scope > [tableclassname="${this.getClassName().trim()}"]`);

    if (rows.length === 0) {
      return;
    }

    if (selectedIndex === undefined || selectedIndex === "") {
      return;
    }

    const selectedRow = rows[selectedIndex];

    if (!selectedRow) {
      return;
    }

    const selectedIndexDom = this.findNestedSelectedIndexInput(selectedRow, nestedTableId);

    if (!(selectedIndexDom instanceof HTMLInputElement)) {
      return;
    }

    return selectedIndexDom.value ?? "";
  };

  // A single parent row can host more than one nested table (one per column). Each nested table
  // renders its own `.selected-index-guid` input, so a plain `querySelector` returns the first one
  // in DOM order — i.e. the first column that contains data. Actions fired from any other column
  // would then resolve their data against that first column's selected row instead of their own.
  // Scope the lookup to the wrapper of the nested table that actually triggered the action so each
  // column reads its own selection. (KOA-17573)
  private findNestedSelectedIndexInput = (selectedRow: Element, nestedTableId?: string): Element | null => {
    if (nestedTableId) {
      try {
        const nestedTableEl = selectedRow.querySelector(`[id="${nestedTableId}"]`);
        const nestedWrapper = nestedTableEl?.closest(".kuika_table__wrapper");
        const scopedInput = nestedWrapper?.querySelector(":scope > .selected-index-guid");

        if (scopedInput) {
          return scopedInput;
        }
      } catch {
        // A malformed id would make the attribute selector throw; fall back to the row-wide lookup
        // below rather than breaking the whole action binding.
      }
    }

    return selectedRow.querySelector(".selected-index-guid");
  };

  getTableWrapperFromHtmlElement = (htmlElementNode: HTMLElement): HTMLElement | undefined => {
    while (htmlElementNode) {
      htmlElementNode = htmlElementNode.parentNode as HTMLElement;

      if (htmlElementNode?.classList?.contains("ant-table")) {
        return htmlElementNode;
      }
    }

    return undefined;
  };

  getNearestTableAncestor = (htmlElementNode: HTMLElement): HTMLElement | undefined => {
    const baseTableViewWrapperElementClasses = [
      "kuika-table-view-wrapper",
      "kuika-galleryview-view-wrapper",
      "kuika-flexgrid-view-wrapper",
      "kuika-carousel-view-wrapper"
    ];

    while (htmlElementNode) {
      htmlElementNode = htmlElementNode?.parentNode as HTMLElement;

      if (!htmlElementNode) break;
      const classList = Array.from(htmlElementNode.classList ?? []);

      const hasMatchingClass = classList.some((cls) => baseTableViewWrapperElementClasses.includes(cls));

      if (hasMatchingClass) return htmlElementNode;
    }

    return undefined;
  };

  public getParentTableIdIfExist = () => {
    const currentTableElement = document.getElementById(this.props.id);
    const nearestTableAncestor = this.getNearestTableAncestor(currentTableElement);

    if (!nearestTableAncestor) {
      return;
    }

    const tableWrapper = this.getTableWrapperFromHtmlElement(nearestTableAncestor);

    return tableWrapper?.id;
  };

  getSelectedColumnIndex = () => {
    const selectedIndexHiddenInput = this.getSelectedColumnIndexHiddenInput();
    if (selectedIndexHiddenInput) {
      return selectedIndexHiddenInput.value;
    }
  };

  public getSelectedRowData = () => {
    const selectedRowDataHiddenInput = this.getSelectedRowDataHiddenInput();
    if (!this.props.dataSource || !selectedRowDataHiddenInput || !selectedRowDataHiddenInput.value) {
      return;
    }

    if (this.props.mode === "google-maps" && !this.state.isDataMarkerSelected) return;

    return JSON.parse(selectedRowDataHiddenInput.value);
  };

  public getCarouselCurrentData = () => {
    if (this.props.dataSource && this.props.mode === "carousel") {
      return this.props.dataSource[this.state.carouselStartingIndex];
    }
    return {} as any;
  };

  public getTableConfigJson = () => {
    const json = {
      searchText: this.state.searchText,
      columnSearchTexts: this.state.columnSearchTexts,
      hiddenColumnList: this.state.hiddenColumnList,
      columnOrder: this.state.columnOrder,
      filteringOptions: this.state.filteringOptions,
      sorterOptions: this.state.sorterOptions,
      columnWidths: this.state.columnWidths
    };
    return JSON.stringify(json);
  };

  public setTableConfigJson = (json: string) => {
    if (!json || json === "") return;
    const obj = JSON.parse(json);
    // Applying a config programmatically (our auto-load OR the manual Import Table Configs action)
    // must not be mistaken for a user change → suppress the componentDidUpdate auto-save echo.
    // Reset/Clear go through their own setState (not here), so a user "reset" still persists.
    this.isApplyingLoadedConfig = true;
    this.setState({
      searchText: obj.searchText,
      columnSearchTexts: obj.columnSearchTexts,
      hiddenColumnList: obj.hiddenColumnList,
      columnOrder: obj.columnOrder,
      filteringOptions: obj.filteringOptions,
      sorterOptions: obj.sorterOptions,
      columnWidths: obj.columnWidths
    });
    setTimeout(() => {
      this.isApplyingLoadedConfig = false;
    }, 0);
  };

  public clearFilterConfigs = () => {
    this.setState({
      searchText: "" as any,
      columnSearchTexts: {} as any,
      filteringOptions: {},
      sorterOptions: {}
    });
  };

  public resetTableConfigs = () => {
    this.setState({
      searchText: "" as any,
      columnSearchTexts: {} as any,
      hiddenColumnList: [] as any,
      columnOrder: [] as any,
      filteringOptions: {} as any,
      sorterOptions: {} as any,
      columnWidths: {} as any
    });
  };

  public searchTable = (searchText: string) => {
    this.setState((prevState) => ({
      ...prevState,
      searchText
    }));
  };

  public exportTable = () => {
    this.onExport();
  };

  public setSelectedValues = (selectedValues: string) => {
    const cleanedValues = selectedValues?.replace(/^,|,$/g, "");
    this.persistNestedSelection(cleanedValues || "");
    this.setState({
      selectedRowKeysString: cleanedValues,
      selectedRowKeys: cleanedValues ? cleanedValues.split(",") : []
    });
  };

  private getSelectedRowKeysForAntd = (): any[] => {
    if (!this.state.selectedRowKeys || this.state.selectedRowKeys.length === 0) return [];
    if (!this.props.dataSource) return this.state.selectedRowKeys;
    const actualRowKey = this.state.actualRowKey || "id";
    return this.props.dataSource
      .filter((row) =>
        this.state.selectedRowKeys.some(
          (k) => k?.toString()?.toLowerCase() === row[actualRowKey]?.toString()?.toLowerCase()
        )
      )
      .map((row) => row[actualRowKey]);
  };

  getSelectedRowIndexHiddenInput = () => {
    const selectedIndexHiddenInput = window.document.getElementById(this.state.selectedIndexGuid);
    return selectedIndexHiddenInput;
  };

  getSelectedColumnIndexHiddenInput = () => {
    const selectedColumnIndexHiddenInput = window.document.getElementById(this.state.selectedColumnIndexGuid);
    return selectedColumnIndexHiddenInput;
  };

  getSelectedRowDataHiddenInput = () => {
    const selectedColumnIndexHiddenInput = window.document.getElementById(this.state.selectedRowDataGuid);
    return selectedColumnIndexHiddenInput;
  };

  findAttributeInTree = (element: HTMLElement, attributeName: string, maxDepth: number = 10): string | null => {
    let currentElement = element;
    let depth = 0;

    while (currentElement && depth < maxDepth) {
      const attrValue = currentElement.getAttribute(attributeName);
      if (attrValue) {
        return attrValue;
      }

      if (currentElement.parentElement) {
        currentElement = currentElement.parentElement;
        depth++;
      } else {
        break;
      }
    }

    return null;
  };

  findRowElement = (element: HTMLElement): HTMLElement | null => {
    let currentElement = element;
    let depth = 0;
    const maxDepth = 20;

    while (currentElement && depth < maxDepth) {
      if (currentElement.tagName?.toLowerCase() === "tr") {
        return currentElement;
      }

      if (currentElement.parentElement) {
        currentElement = currentElement.parentElement;
        depth++;
      } else {
        break;
      }
    }

    return null;
  };

  getRowDataFromElement = (element: HTMLElement): any | null => {
    const rowElement = this.findRowElement(element);
    if (!rowElement) return null;

    const rowKey = rowElement.getAttribute("data-row-key");
    if (!rowKey) return null;

    const dataSource = this.getDataSource();
    const result = this.resolveRowDataByRenderKey(dataSource, rowKey);
    return result || null;
  };

  isInsertRow = (rowData: any): boolean => {
    if (!rowData) return false;
    const rowKey = this.props.rowKey?.toString()?.toLowerCase() || "id";
    const otherFields = Object.keys(rowData).filter((key) => key?.toString()?.toLowerCase() !== rowKey);
    return otherFields.every((field) => rowData[field] === undefined);
  };

  getRowRenderKey = (record: TableRowRecord): React.Key => {
    const keyField: string = this.state.actualRowKey || "id";
    const keyValue: TableCellValue = record?.[keyField];
    if (keyValue === undefined && this.props.insertRowActive && this.isInsertRow(record)) {
      return KUIKA_INSERT_ROW_KEY;
    }
    return keyValue as React.Key;
  };

  getInsertRowRecord = (): TableRowRecord | undefined => {
    if (!this.props.insertRowActive) return undefined;
    const dataSource: TableRowRecord[] = this.getDataSource();
    return dataSource?.find?.((row: TableRowRecord) => this.isInsertRow(row));
  };

  resolveRowDataByRenderKey = (dataSource: TableRowRecord[], renderKey: string): TableRowRecord | undefined => {
    if (renderKey === KUIKA_INSERT_ROW_KEY) {
      return this.getInsertRowRecord();
    }
    return BaseTable.findRowByKey(dataSource, renderKey, this.props.rowKey?.toString() || "id");
  };

  getFormValues = (rowData: any, isInsertRow?: boolean): { [key: string]: any } => {
    if (!rowData) return {};

    const dataToUse = isInsertRow ? this.getCurrentFirstRowInputValues() || rowData : rowData;

    const formValues: { [key: string]: any } = {};
    Object.keys(dataToUse).forEach((fieldName) => {
      const key = `Table_${this.props.id}_${fieldName}`;
      formValues[key] = dataToUse[fieldName];
    });

    return formValues;
  };

  focusNextCell = (nextColumnIndex: number, currentRowIndex: string): void => {
    setTimeout(() => {
      const tableElement = document.querySelector(`.${this.getClassName().trim()}`);
      if (!tableElement) return;

      const nextCell = tableElement.querySelector(`[rowindex="${currentRowIndex}"][columnindex="${nextColumnIndex}"]`);

      if (nextCell) {
        const input = nextCell.querySelector("input, textarea, select, .ant-select-selection-search-input");
        if (input) {
          (input as HTMLElement).focus();
        }
      }
    }, 50);
  };

  compareFormValues = (rowData: any): { isEqual: boolean; oldValues: any; newValues: any } => {
    const oldValues = {};
    const newValues = {};

    if (!rowData || !this.props.form) {
      return { isEqual: true, oldValues, newValues };
    }

    Object.keys(rowData).forEach((fieldName) => {
      const key = `Table_${this.props.id}_${fieldName}`;
      oldValues[key] = rowData[fieldName];
      newValues[key] = this.props.form?.getFieldValue(key);
    });

    return {
      isEqual: isEqual(oldValues, newValues),
      oldValues,
      newValues
    };
  };

  handleKeyDown = async (event: KeyboardEvent): Promise<void> => {
    const target = event.target as HTMLElement;
    const rowData = this.getRowDataFromElement(target);
    const isCurrentInsertRow = rowData ? this.isInsertRow(rowData) : false;
    const currentColumnIndex = this.findAttributeInTree(target, "columnindex");
    const currentRowIndex = this.findAttributeInTree(target, "rowindex");

    if (event?.key === "Escape") {
      if (this.props.editableAlwaysActive || this.state.activeEditableRowIndex !== -1) {
        this.setState({ activeEditableRowIndex: -1 } as any);
        return;
      }
    }

    if (
      event?.key === "Enter" &&
      this.state.activeEditableRowIndex !== -1 &&
      (this.props.onRowEditFinished || this.props.onInsertRowFinished)
    ) {
      event.preventDefault();
      await this.handleOnRowEditFinished(rowData);
      return;
    }

    if (!this.props.editableAlwaysActive) return;

    if (event?.key === "Tab" && isCurrentInsertRow) {
      const tableElement = document.querySelector(`.${this.getClassName().trim()}`);
      if (!tableElement || !currentColumnIndex) return;

      const nextColumnIndex = parseInt(currentColumnIndex) + 1;
      const nextCell = tableElement.querySelector(`[rowindex="0"][columnindex="${nextColumnIndex}"]`);

      if (nextCell) {
        const input = nextCell.querySelector("input, textarea, select, .ant-select-selection-search-input");
        if (input) {
          event.preventDefault();
          (input as HTMLElement).focus();
        }
      }
      return;
    }

    if (event?.key === "Enter" && isCurrentInsertRow) {
      event.preventDefault();
      // handleTableKeyDown is registered on the same element and would commit the
      // insert row a second time for this very keystroke.
      event.stopImmediatePropagation();
      await this.commitInsertRow();
      return;
    }

    if (event?.key === "Tab" || (event?.key === "Enter" && !isCurrentInsertRow)) {
      if (!rowData) {
        await this.handleOnRowEditFinished(rowData);
        return;
      }

      this.setFields(event, false, rowData);

      const { isEqual } = this.compareFormValues(rowData);

      if (!isEqual) {
        await this.handleOnRowEditFinished(rowData, false, event);
        if (currentColumnIndex && currentRowIndex) {
          this.handleFocusNextCell(event, currentColumnIndex, currentRowIndex, false);
        }
      }
    }
  };

  handleFocusNextCell = (
    event: KeyboardEvent,
    currentColumnIndex: string,
    currentRowIndex: string,
    isEqual: boolean
  ) => {
    if (event && event?.key === "Tab") {
      event.preventDefault();

      const tableElement = document.querySelector(`.${this.getClassName().trim()}`) as HTMLElement;
      if (!tableElement || !currentColumnIndex || !currentRowIndex) return;

      const currentRow = tableElement.querySelector(`tr[rowindex="${currentRowIndex}"]`) as HTMLElement;
      if (!currentRow) return;

      const cells = currentRow.querySelectorAll("td");
      const lastColumnIndex = cells.length - 1;

      const findNextCellWithDataValue = (startColumnIndex: number, currentRowIndex: number) => {
        const dataSource = this.getDataSource();
        if (!dataSource || dataSource.length <= currentRowIndex) {
          return { columnIndex: startColumnIndex, rowIndex: currentRowIndex };
        }

        const rowData = dataSource[currentRowIndex];
        if (!rowData) return { columnIndex: startColumnIndex, rowIndex: currentRowIndex };

        const hasInteractiveElements = (cell: HTMLElement): boolean => {
          return !!(
            cell.querySelector("input") ||
            cell.querySelector(".ant-switch") ||
            cell.querySelector(".ant-select") ||
            cell.querySelector(".ant-checkbox") ||
            cell.querySelector(".ant-radio")
          );
        };

        const row =
          currentRowIndex === parseInt(currentRowIndex.toString())
            ? currentRow
            : (tableElement.querySelector(`tr[rowindex="${currentRowIndex}"]`) as HTMLElement);

        if (!row) return { columnIndex: startColumnIndex, rowIndex: currentRowIndex };

        for (let i = startColumnIndex; i <= lastColumnIndex; i++) {
          const cellElement = row.querySelector(`td[columnindex="${i}"]`) as HTMLElement;
          if (!cellElement) continue;

          const dataValueAttr = cellElement.getAttribute("datavalue");
          const hasDataValue = dataValueAttr && rowData[dataValueAttr] !== undefined;
          const hasInteractive = hasInteractiveElements(cellElement);

          if (hasDataValue && hasInteractive) {
            return { columnIndex: i, rowIndex: currentRowIndex };
          }
        }

        const nextRowIndex = currentRowIndex + 1;
        if (dataSource.length > nextRowIndex) {
          const nextRow = tableElement.querySelector(`tr[rowindex="${nextRowIndex}"]`) as HTMLElement;
          if (nextRow) {
            for (let i = 0; i <= lastColumnIndex; i++) {
              const cellElement = nextRow.querySelector(`td[columnindex="${i}"]`) as HTMLElement;
              if (!cellElement) continue;

              const dataValueAttr = cellElement.getAttribute("datavalue");
              const hasDataValue = dataValueAttr && dataSource[nextRowIndex][dataValueAttr] !== undefined;
              const hasInteractive = hasInteractiveElements(cellElement);

              if (hasDataValue && hasInteractive) {
                return { columnIndex: i, rowIndex: nextRowIndex };
              }
            }
          }
        }

        return { columnIndex: 0, rowIndex: nextRowIndex };
      };

      const currentColumnIndexNum = parseInt(currentColumnIndex);
      const currentRowIndexNum = parseInt(currentRowIndex);

      if (isEqual) {
        const nextRowIndex = currentRowIndexNum + 1;
        const nextRow = tableElement.querySelector(`tr[rowindex="${nextRowIndex}"]`);

        if (nextRow) {
          const nextCell = findNextCellWithDataValue(0, nextRowIndex);
          const dataSource = this.getDataSource();
          const nextRowData = dataSource && dataSource.length > nextRowIndex ? dataSource[nextRowIndex] : null;

          window.nextCellToFocus = {
            tableId: this.props.id || "",
            columnIndex: nextCell.columnIndex,
            rowIndex: nextCell.rowIndex.toString()
          };

          if (nextRowData) {
            const targetCell = tableElement.querySelector(
              `td[columnindex="${nextCell.columnIndex}"][rowindex="${nextCell.rowIndex}"]`
            ) as HTMLElement;
            if (targetCell) {
              let inputElement = targetCell.querySelector("input") as HTMLInputElement;
              if (inputElement && document.activeElement !== inputElement) {
                inputElement.focus();

                if (!window.handlingCellClick) {
                  window.handlingCellClick = true;

                  const syntheticEvent = {
                    currentTarget: targetCell,
                    target: inputElement,
                    preventDefault: () => {},
                    stopPropagation: () => {}
                  };
                  this.handleCellClick(syntheticEvent as any, nextCell.columnIndex, nextRowData, nextCell.rowIndex);
                  setTimeout(() => {
                    window.handlingCellClick = false;
                    window.nextCellToFocus = null;
                  }, 500);
                }
              }
            }
          }
          return;
        }
      }

      if (currentColumnIndexNum >= lastColumnIndex) {
        const nextRowIndex = currentRowIndexNum + 1;
        const nextRow = tableElement.querySelector(`tr[rowindex="${nextRowIndex}"]`);

        if (nextRow) {
          const nextCell = findNextCellWithDataValue(0, nextRowIndex);

          window.nextCellToFocus = {
            tableId: this.props.id || "",
            columnIndex: nextCell.columnIndex,
            rowIndex: nextCell.rowIndex.toString()
          };

          const dataSource = this.getDataSource();
          if (dataSource && dataSource.length > nextCell.rowIndex) {
            const nextRowData = dataSource[nextCell.rowIndex];

            if (nextRowData) {
              setTimeout(() => {
                const targetRow = tableElement.querySelector(`tr[rowindex="${nextCell.rowIndex}"]`);
                const targetCell = targetRow?.querySelector(`td[columnindex="${nextCell.columnIndex}"]`);
                if (targetCell) {
                  const syntheticEvent = {
                    target: targetCell,
                    currentTarget: targetCell,
                    preventDefault: () => {},
                    stopPropagation: () => {}
                  };

                  setTimeout(() => {
                    this.handleCellClick(syntheticEvent as any, nextCell.columnIndex, nextRowData, nextCell.rowIndex);

                    const selectedRowDataHiddenInput = this.getSelectedRowDataHiddenInput();
                    if (selectedRowDataHiddenInput) {
                      selectedRowDataHiddenInput.value = JSON.stringify(nextRowData);
                    }

                    const selectedIndexHiddenInput = this.getSelectedRowIndexHiddenInput();
                    if (selectedIndexHiddenInput) {
                      selectedIndexHiddenInput.value = nextCell.rowIndex.toString();
                    }
                  }, 100);
                }
              }, 1000);
            }
          }
        }
      } else {
        const nextCell = findNextCellWithDataValue(currentColumnIndexNum + 1, currentRowIndexNum);

        window.nextCellToFocus = {
          tableId: this.props.id || "",
          columnIndex: nextCell.columnIndex,
          rowIndex: nextCell.rowIndex.toString()
        };

        if (nextCell.rowIndex !== currentRowIndexNum) {
          const dataSource = this.getDataSource();
          if (dataSource && dataSource.length > nextCell.rowIndex) {
            const nextRowData = dataSource[nextCell.rowIndex];
            const nextRow = tableElement.querySelector(`tr[rowindex="${nextCell.rowIndex}"]`);

            if (nextRowData && nextRow) {
              const targetCell = nextRow.querySelector(`td[columnindex="${nextCell.columnIndex}"]`);
              if (targetCell) {
                const syntheticEvent = {
                  target: targetCell,
                  currentTarget: targetCell,
                  preventDefault: () => {},
                  stopPropagation: () => {}
                };

                setTimeout(() => {
                  this.handleCellClick(syntheticEvent as any, nextCell.columnIndex, nextRowData, nextCell.rowIndex);

                  const selectedRowDataHiddenInput = this.getSelectedRowDataHiddenInput();
                  if (selectedRowDataHiddenInput) {
                    selectedRowDataHiddenInput.value = JSON.stringify(nextRowData);
                  }

                  const selectedIndexHiddenInput = this.getSelectedRowIndexHiddenInput();
                  if (selectedIndexHiddenInput) {
                    selectedIndexHiddenInput.value = nextCell.rowIndex.toString();
                  }
                }, 1000);
              }
            }
          }
        }
      }
    }
  };

  handleClick = (event: MouseEvent): void => {
    const targetId = (event?.target as HTMLElement)?.id;

    if (targetId.includes("body") || targetId.includes("header") || targetId.includes("footer")) {
      if (
        this.state.activeEditableRowIndex !== -1 &&
        (this.props.onRowEditFinished || this.props.onInsertRowFinished)
      ) {
        const rowData = this.getRowDataFromElement(event?.target as HTMLElement);
        this.handleOnRowEditFinished(rowData);
      }
    }
  };

  handleTableKeyDown = async (event: KeyboardEvent): Promise<void> => {
    if (event?.key === "Enter") {
      const rowData = this.getRowDataFromElement(event?.target as HTMLElement);
      if (
        this.state.activeEditableRowIndex !== -1 &&
        (this.props.onRowEditFinished || this.props.onInsertRowFinished)
      ) {
        event.preventDefault();
        event.stopPropagation();
        await this.handleOnRowEditFinished(rowData);
      } else if (this.props.insertRowActive) {
        await this.handleOnRowEditFinished(rowData);
      }
    }
  };

  commitInsertRow = async (): Promise<void> => {
    const updatedRowData: TableRowRecord | null = this.getCurrentFirstRowInputValues();
    if (!updatedRowData) return;
    // In editableAlwaysActive mode the insert row's inputs are not form-bound; the form only
    // learns their values here, and both the change-detection compare and the generated
    // event handler read from the form.
    const formValues = this.getFormValues(updatedRowData);
    this.props.form?.setFieldsValue(formValues);
    await this.handleOnRowEditFinished(updatedRowData, true);
  };

  handleOnRowEditFinished = async (rowData?: any, _isInsertRow?: boolean, event?: KeyboardEvent): Promise<void> => {
    const { pageSize = 10, current = 1 } = this.state;
    const offset = (current - 1) * pageSize;

    let rowValue: any;

    if (!this.props.editableAlwaysActive) {
      const filteredList = this.getFilteredAndSortedDataSource();
      const adjustedIndex = offset + (this.state.activeEditableRowIndex || 0);
      rowValue = filteredList?.[adjustedIndex];
    } else if (rowData) {
      rowValue = rowData;
    }

    if (!rowValue) {
      this.setState({ activeEditableRowIndex: -1 } as any);
      return;
    }

    const isInsertRow = _isInsertRow || (this.props.insertRowActive && this.isInsertRow(rowValue));

    let { oldValues, newValues } = this.compareFormValues(rowValue);

    const idColumnKey = `Table_${this.props.rowKey?.toString() || "id"}_id`;
    if (isInsertRow) {
      // A row being inserted has no previous values. The keys must mirror
      // compareFormValues' `Table_{id}_{field}` form keys — with raw field names the
      // compare below can never report equality and an untouched insert row would
      // still fire the event.
      oldValues = {
        ...Object.keys(rowValue).reduce((acc: Record<string, undefined>, key: string) => {
          acc[`Table_${this.props.id}_${key}`] = undefined;
          return acc;
        }, {})
      };
    }

    const _isEqual = isEqual(oldValues, newValues);

    if (_isEqual) {
      this.setState({ activeEditableRowIndex: -1 } as any);
      return;
    }

    if (oldValues[idColumnKey] !== newValues[idColumnKey] && !isInsertRow) {
      console.warn("Row ID should not be changed during edit");

      if (event?.key === "Tab") {
        const target = event.target as HTMLElement;
        const currentColumnIndex = this.findAttributeInTree(target, "columnindex");
        const currentRowIndex = this.findAttributeInTree(target, "rowindex");
        this.handleFocusNextCell(event, currentColumnIndex, currentRowIndex, true);
      }
      return;
    }

    // The committed row = the row's own values overlaid with the edits made this commit
    // (newValues, keyed Table_<id>_<field>). Both the editor-field sync and the selected-row
    // publish below use THIS merged row: a column the user actually edited (e.g. a toggled
    // Switch) carries the new value; an untouched column carries the row's own value.
    const committedRow: TableRowRecord = this.buildCommittedRow(rowValue, newValues);

    // Seed every editable column's editor field with the committed row's value before the
    // action reads them via DisplayItemProp. In editableAlwaysActive all rows share one field
    // per column, so committing row R by editing a different cell would otherwise read another
    // row's value for the untouched editors (KOA-17691: a true boolean flipping to false).
    this.syncEditorFieldsForCommittedRow(committedRow);

    if (isInsertRow && this.props.onInsertRowFinished) {
      this.publishCommittedRow(committedRow);
      this.props.onInsertRowFinished();
    } else if (this.props.onRowEditFinished) {
      // Committing an edit by clicking another row records THAT row as "selected" on
      // mousedown; publish the edited row so the action's `dataSource.selected.Id` targets
      // the edited row, not the just-clicked one (KOA-17691). Enter-commit was already fine.
      this.publishCommittedRow(committedRow);
      this.props.onRowEditFinished();
    }
  };

  // KOA-17691 — the row's own values overlaid with the edits made this commit. `newValues`
  // is keyed `Table_<id>_<field>` (compareFormValues) and, for whitelisted Switch/Checkbox/
  // text editors, already reflects the user's toggle/type. A column the user did NOT touch is
  // absent from newValues, so the row's own value is kept. The result is the correct final row.
  buildCommittedRow = (rowValue: TableRowRecord, newValues: TableRowRecord): TableRowRecord => {
    const committedRow: TableRowRecord = { ...rowValue };
    Object.keys(rowValue).forEach((fieldName: string) => {
      const formValue: TableCellValue = newValues[`Table_${this.props.id}_${fieldName}`];
      if (formValue !== undefined) {
        committedRow[fieldName] = formValue;
      }
    });
    return committedRow;
  };

  // KOA-17691 — publish the committed row's cell values into each editable column's editor
  // Form.Item field (captured as `editorFieldName` during setRow). A Save Record param bound
  // to DisplayItemProp reads that field, and in editableAlwaysActive it is shared across rows,
  // so without this an editor the user did not touch this commit would persist a stale value.
  // MUST use the merged committed row, not the raw pre-edit snapshot: seeding the raw snapshot
  // would overwrite the value the user just toggled with the old one (covers Switch/Checkbox/text).
  syncEditorFieldsForCommittedRow = (committedRow: TableRowRecord): void => {
    if (!committedRow || !this.props.form) return;
    const columns = this.state.columns as unknown as EditorFieldColumn[] | undefined;
    if (!columns) return;
    columns.forEach((column: EditorFieldColumn) => {
      const editorFieldName: string | undefined = column?.editorFieldName;
      const dataValue: string | undefined = column?.dataValue;
      if (!editorFieldName || !dataValue) return;
      let cellValue: TableCellValue = committedRow[dataValue];
      if (cellValue === undefined) {
        const matchKey: string | undefined = Object.keys(committedRow).find(
          (key: string) => key.toLowerCase() === dataValue.toLowerCase()
        );
        if (matchKey) cellValue = committedRow[matchKey];
      }
      this.props.form?.setFieldValue(editorFieldName, cellValue);
    });
  };

  // Records which row subsequent action bindings read as "Current Row"
  // (getSelectedRowData / getSelectedIndex go through these hidden inputs).
  // Called from mousedown capture as well as click: some antd controls fire
  // their onChange during mousedown — the Select clear icon and Slider drag —
  // before any click reaches the row, so a click-only write left Current Row
  // pointing at the previously clicked row (or nothing on a fresh page).
  recordRowSelectionContext = (rowValue: any, rowIndex?: number): void => {
    const selectedIndexHiddenInput = this.getSelectedRowIndexHiddenInput();
    if (selectedIndexHiddenInput && rowIndex !== undefined) {
      selectedIndexHiddenInput.value = rowIndex.toString();
    }

    const selectedRowDataHiddenInput = this.getSelectedRowDataHiddenInput();
    if (selectedRowDataHiddenInput && rowValue !== undefined) {
      selectedRowDataHiddenInput.value = JSON.stringify(rowValue);
    }
  };

  // Generated `dataSource.selected.<field>` symbols (action params, If/Else conditions)
  // resolve through getSelectedRowData(), i.e. the selected-row-data hidden input.
  // That input is written by recordRowSelectionContext on the row's mousedown/click —
  // so committing an edit by CLICKING another row first records that OTHER row as
  // "selected", and an onRowEditFinished/onInsertRowFinished action reads it, writing the
  // committed values onto the wrong row (KOA-17691: the row below the edited one; and the
  // insert row's all-undefined mock record for the insert case). Publish the committed row
  // as "selected" right before firing so "selected" means the row that was just committed.
  publishCommittedRow = (committedRow: TableRowRecord): void => {
    const selectedRowDataHiddenInput = this.getSelectedRowDataHiddenInput();
    if (selectedRowDataHiddenInput) {
      selectedRowDataHiddenInput.value = JSON.stringify(committedRow);
    }
  };

  handleRowClick = (event: React.MouseEvent, rowValue: any, rowIndex?: number): void => {
    if (
      (this.props.onRowEditFinished || this.props.onInsertRowFinished) &&
      this.state.activeEditableRowIndex !== -1 &&
      rowIndex !== this.state.activeEditableRowIndex &&
      !this.props.editableAlwaysActive
    ) {
      const rowData = this.getRowDataFromElement(event?.target as HTMLElement);
      this.handleOnRowEditFinished(rowData);
    }

    this.recordRowSelectionContext(rowValue, rowIndex);
  };

  handleRowDoubleClick = (event: React.MouseEvent, rowValue: any, rowIndex?: number): void => {
    if (this.props.editableAlwaysActive) {
      return;
    }

    if (this.state.activeEditableRowIndex !== rowIndex && this.props.editable) {
      const isEditable = this.props.children[1]?.props?.editabilityCondition?.(rowValue) ?? true;

      if (!isEditable) {
        return;
      }

      const formValues = this.getFormValues(rowValue);
      this.props.form?.setFieldsValue(formValues);

      this.setState({ activeEditableRowIndex: rowIndex } as any);
    }

    if (this.props.onRowDoubleClick) {
      this.props.onRowDoubleClick();
    }
  };

  handleOnRowBlur = (event: React.FocusEvent, rowValue: any, rowIndex?: number): void => {};

  handleCellClick = (event: React.MouseEvent, columnIndex: number, rowData: any, rowIndex: number): void => {
    if (this.props.editableAlwaysActive) {
      this.setFields(event, true, rowData);
    }

    const selectedColumnIndexHiddenInput = this.getSelectedColumnIndexHiddenInput();
    if (selectedColumnIndexHiddenInput && columnIndex !== undefined) {
      selectedColumnIndexHiddenInput.value = columnIndex.toString();
    }
  };

  getColumnDataFromEvent = (
    event: any
  ): {
    dataValue: string | null;
    dataType: string | null;
    element: HTMLElement;
    columnIndex: string | null;
  } => {
    let dataValue = event?.target?.getAttribute("datavalue");
    let dataType = event?.target?.getAttribute("datatype");
    let element = event?.target;
    const columnIndex = event?.target?.getAttribute("columnindex");

    while (!dataValue && element && element.getAttribute("datavalue") === null) {
      element = element.parentElement;
      if (!element) break;

      dataValue = element?.getAttribute("datavalue");
      dataType = element?.getAttribute("datatype");
    }

    return { dataValue, dataType, element, columnIndex };
  };

  isBooleanElement = (element: HTMLElement): boolean => {
    return (
      element?.getAttribute("aria-checked") === "true" ||
      element?.getAttribute("aria-checked") === "false" ||
      element?.parentElement?.getAttribute("aria-checked") === "true" ||
      element?.parentElement?.getAttribute("aria-checked") === "false"
    );
  };

  isCheckboxElement = (element: HTMLElement): boolean => {
    return element.className === "ant-checkbox-inner" || element.className === "ant-checkbox-input";
  };

  getColumnValue = (
    event: any,
    dataType: string,
    element: HTMLElement
  ): {
    value: any;
    isDate: boolean;
    isBoolean: boolean;
    isSelect: boolean;
    selectedValue?: any;
  } => {
    let currentValue: any =
      (event.target as HTMLInputElement)?.value?.toString()?.trim() ||
      (document.activeElement as HTMLInputElement)?.checked ||
      (document.activeElement as HTMLInputElement)?.value;
    let selectedValue: any;
    const isDateType = currentValue && typeof currentValue === "string" && dataType === ValueType.Date;
    const isBooleanType = this.isBooleanElement(event?.target);
    let isSelect = false;

    if (event?.target?.className === "ant-select-item-option-content") {
      let selectedOptionElement = event.target?.parentElement;
      if (selectedOptionElement && selectedOptionElement.getAttribute("aria-selected") === "true") {
        currentValue = event?.target?.textContent;
      } else {
        // The picked option's value must not depend on a previously-selected
        // option existing — a first pick in an empty cell has no
        // aria-selected sibling, and requiring one made setFields write the
        // old (empty) value back into the row.
        selectedValue = selectedOptionElement?.getAttribute?.("data-value");
        const parentWithSelection = event.target?.parentElement?.parentElement?.querySelector('[aria-selected="true"]');
        if (parentWithSelection) {
          currentValue = parentWithSelection.getAttribute("data-value");
        }
      }
      isSelect = true;
    }

    if (isDateType) {
      currentValue = moment(currentValue, "DD-MM-YYYY HH:mm:ss").format("YYYY-MM-DD HH:mm:ss");
    }

    if (isBooleanType) {
      currentValue = !(
        event.target.parentElement?.getAttribute("aria-checked") === "true" ||
        event.target?.getAttribute("aria-checked") === "true"
      );
    }

    if (this.isCheckboxElement(event.target)) {
      const parent = event.target.parentElement;
      const input = parent?.querySelector("input");
      if (input) {
        currentValue = input.getAttribute("checked") === null;
      }
    }

    if (currentValue && typeof currentValue === "string") {
      if (dataType === ValueType.Number) {
        currentValue = BaseTable.parseLocalizedNumberString(currentValue);
      } else {
        currentValue = currentValue.replace(/[,]/g, "");
      }
    }

    return {
      value: currentValue,
      isDate: isDateType,
      isBoolean: isBooleanType || this.isCheckboxElement(event.target),
      isSelect,
      selectedValue
    };
  };

  setFields = (event: any, isClick: boolean, rowData: any): void => {
    const _rowData = rowData;
    if (!rowData) return;

    const isCurrentInsertRow = this.props.insertRowActive && this.isInsertRow(rowData);
    if (isCurrentInsertRow && !isClick) return;

    // Skip combobox chrome clicks (selector, arrow, search input). The
    // search input's value is intermediate (empty on open, partial while
    // typing) — reading it here writes that empty/partial value into the
    // form and hides what the user is typing. The actual value lands in
    // dataSource through the Combobox onChange handler when the user
    // picks an option (which keeps the .ant-select-item-option-content
    // path below working).
    if (isClick && BaseTable.isInsideAntSelectShell(event?.target as HTMLElement)) {
      return;
    }

    const { dataValue, dataType, element, columnIndex } = this.getColumnDataFromEvent(event);
    if (!dataValue) return;

    const dataSource = this.getDataSource();
    if (!dataSource) return;

    const selectedRow = rowData;
    let selectedValue = selectedRow[dataValue]?.toString();

    if (dataType === ValueType.Date && selectedValue) {
      selectedValue = moment(selectedValue).format("YYYY-MM-DD HH:mm:ss");
    }

    if (this.isBooleanElement(event.target) || this.isCheckboxElement(event.target)) {
      selectedValue = selectedValue === "true";
    }

    const {
      value: currentValue,
      isDate,
      isBoolean,
      isSelect,
      selectedValue: selectboxSelectedValue
    } = this.getColumnValue(event, dataType, element);

    if (isSelect && selectboxSelectedValue) {
      selectedValue = selectboxSelectedValue;
    }

    if (currentValue === selectedValue) {
      return;
    }

    let inputValue = currentValue;
    if (!isDate && !isBoolean && dataType !== ValueType.Number) {
      if (
        event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLTextAreaElement ||
        event.target instanceof HTMLSelectElement
      ) {
        inputValue = event.target.value;
      } else {
        inputValue = currentValue;
      }
    }

    const updatedRow = {
      ...selectedRow,
      [dataValue]: isSelect
        ? selectedValue
        : isDate || isBoolean
          ? currentValue
          : dataType === ValueType.Number
            ? parseFloat(currentValue)
            : inputValue
    };

    for (let i = 0; i < dataSource.length; i++) {
      if (dataSource[i].id === updatedRow.id) {
        dataSource[i] = updatedRow;
        break;
      }
    }

    // The row data passed by handleCellBlur's insert flow already carries the typed values,
    // so its shape no longer looks like the insert row — resolve the row from the DOM as well,
    // or this fallthrough would commit the insert row a second time behind the caller's back.
    const isDomRowInsertRow: boolean =
      !!this.props.insertRowActive && this.isInsertRow(this.getRowDataFromElement(event.target as HTMLElement));
    const isInsertRow =
      isCurrentInsertRow || isDomRowInsertRow || (this.props.insertRowActive && this.isInsertRow(updatedRow));
    const formValues = this.getFormValues(updatedRow, isInsertRow);
    this.props.form?.setFieldsValue(formValues);

    if (
      ((isBoolean && element.hasAttribute("columnIndex")) || (isDate && currentValue !== selectedValue) || isSelect) &&
      !isInsertRow
    ) {
      let rowData = this.getRowDataFromElement(event.target as HTMLElement);
      if (isSelect || isBoolean) {
        rowData = _rowData;
      }
      if (isBoolean) {
        // This runs in the cell's capture-phase click, BEFORE the editor's own
        // bubble-phase onChange commits the toggled value to the form store
        // (Switch/Checkbox wrapped in KuikaEditableCell forward the pick via the
        // Form.Item onChange during bubble). Firing the save synchronously here
        // would read the stale pre-toggle value, so a DisplayItemProp-bound Save
        // Record persists the old boolean (KOA-17691: false never became true).
        // Defer to a macrotask so the commit lands first, then read + save.
        const rowToSave = rowData;
        setTimeout(() => this.handleOnRowEditFinished(rowToSave), 0);
      } else {
        this.handleOnRowEditFinished(rowData);
      }
    }
  };

  handleCellBlur = (event: React.FocusEvent, rowIndex: number, columnIndex: number): void => {
    if (!this.props.editableAlwaysActive) {
      return;
    }

    const currentTarget = event.currentTarget as HTMLElement;
    const rowElement = this.findRowElement(currentTarget);
    if (!rowElement) return;

    const rowKey = rowElement.getAttribute("data-row-key");
    if (!rowKey) return;

    const dataSource = this.getDataSource();
    if (!dataSource) return;

    const rowData = this.resolveRowDataByRenderKey(dataSource, rowKey);
    if (!rowData) return;

    const isInsertRow = this.props.insertRowActive && this.isInsertRow(rowData);

    const relatedTarget = event.relatedTarget as HTMLElement;
    if (relatedTarget) {
      const clickedRowElement = this.findRowElement(relatedTarget);
      if (clickedRowElement) {
        const clickedRowKey = clickedRowElement.getAttribute("data-row-key");
        if (clickedRowKey) {
          const clickedRowData = this.resolveRowDataByRenderKey(dataSource, clickedRowKey);
          if (clickedRowData) {
            const clickedInsertRow = this.props.insertRowActive && this.isInsertRow(clickedRowData);
            if (isInsertRow && clickedInsertRow) {
              return;
            }
          }
        }
      }
    }

    const target = event.target as HTMLElement;
    const dataValue = currentTarget.getAttribute("datavalue");
    const dataType = currentTarget.getAttribute("datatype");

    if (!dataValue) return;

    let selectedColumnValue = rowData[dataValue]?.toString();
    let currentColumnValue = "";

    if (target.classList.contains("ant-select-selection-search-input")) {
      const activeDescendantId = target.getAttribute("aria-activedescendant");
      currentColumnValue = document.getElementById(activeDescendantId)?.innerText || "";
    } else {
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement
      ) {
        currentColumnValue = target.value?.toString() || "";
      } else {
        const inputElement = target.querySelector("input, textarea, select");
        if (
          inputElement instanceof HTMLInputElement ||
          inputElement instanceof HTMLTextAreaElement ||
          inputElement instanceof HTMLSelectElement
        ) {
          currentColumnValue = inputElement.value?.toString() || "";
        }
      }
      if (dataType === ValueType.Number) {
        currentColumnValue = BaseTable.parseLocalizedNumberString(currentColumnValue);
      } else {
        currentColumnValue = currentColumnValue.replace(/[,]/g, "");
      }
    }

    if (currentColumnValue === "Invalid date") {
      return;
    }

    let formattedDate = "";
    if (dataType === ValueType.Date) {
      currentColumnValue = moment(currentColumnValue, "DD-MM-YYYY HH:mm:ss").format("YYYY-MM-DD HH:mm:ss");
      formattedDate = moment(selectedColumnValue).format("YYYY-MM-DD HH:mm:ss");
    }

    if (currentColumnValue === selectedColumnValue || selectedColumnValue === "NaN") {
      return;
    }

    if (dataType === ValueType.Date && formattedDate) {
      if (selectedColumnValue !== formattedDate && formattedDate !== "Invalid date") {
        this.setFields(event, true, rowData);
        this.handleOnRowEditFinished(rowData);
      }
    } else {
      this.setFields(event, true, rowData);
      if (isInsertRow) {
        const updatedRowData = this.getCurrentFirstRowInputValues();
        this.setFields(event, true, updatedRowData);
        this.handleOnRowEditFinished(updatedRowData, true);
      } else {
        this.handleOnRowEditFinished(rowData);
      }
    }

    const selectedColumnIndexInput = this.getSelectedColumnIndexHiddenInput();
    if (selectedColumnIndexInput && columnIndex !== undefined) {
      selectedColumnIndexInput.value = columnIndex.toString();
    }
  };

  getCurrentFirstRowInputValues = (): any | null => {
    const dataSource = this.getDataSource();
    if (!dataSource || dataSource.length === 0) return null;

    const firstRowData = this.getInsertRowRecord() ?? dataSource[0];
    if (!firstRowData) return null;

    const tableElement = document.querySelector(`.${this.getClassName().trim()}`);
    if (!tableElement) return firstRowData;

    const rowElement = tableElement.querySelector(`tr[data-row-key="${this.getRowRenderKey(firstRowData)}"]`);
    if (!rowElement) return firstRowData;

    const result = {};

    Object.keys(firstRowData).forEach((fieldName) => {
      result[fieldName] = firstRowData[fieldName];
    });

    const inputElements = rowElement.querySelectorAll("input, textarea, select");
    inputElements.forEach((inputElement) => {
      const columnElement = this.findAttributeInTree(inputElement as HTMLElement, "datavalue");
      if (columnElement) {
        const fieldName = columnElement;
        let value = (inputElement as HTMLInputElement).value;

        if (inputElement.getAttribute("type") === "checkbox") {
          value = (inputElement as HTMLInputElement).checked.toString();
        } else if (inputElement.getAttribute("datatype") === ValueType.Number && value !== "") {
          const numericValue = BaseTable.parseLocalizedNumberString(value);
          value = parseFloat(numericValue).toString();
        }

        if (value === "") {
          value = undefined;
        }

        if (inputElement.getAttribute("datatype") !== ValueType.Number) {
          value = value?.replace?.(/[,]/g, "");
        }

        result[fieldName] = value;
      }
    });

    const selectElements = rowElement.querySelectorAll(".ant-select");
    selectElements.forEach((selectElement) => {
      const columnElement = this.findAttributeInTree(selectElement as HTMLElement, "datavalue");
      if (columnElement) {
        const fieldName = columnElement;

        const hiddenInput = selectElement.querySelector(".ant-select-selection-search-input");
        if (hiddenInput) {
          const ariaControls = (hiddenInput as HTMLElement).getAttribute("aria-controls");
          if (ariaControls) {
            const selectedOption = document.querySelector(`.ant-select-item-option-selected[data-value]`);
            if (selectedOption) {
              const value = selectedOption.getAttribute("data-value");

              if (selectElement.getAttribute("datatype") === ValueType.Number && !isNaN(Number(value))) {
                result[fieldName] = Number(value);
              } else {
                result[fieldName] = value;
              }
              return;
            }
          }

          const dataValue = selectElement.getAttribute("data-value");
          if (dataValue) {
            result[fieldName] = dataValue;
            return;
          }

          if (this.props.form) {
            const formKey = `Table_${this.props.id}_${fieldName}`;
            const formValue = this.props.form.getFieldValue(formKey);
            if (formValue !== undefined) {
              result[fieldName] = formValue;
              return;
            }
          }
        }

        const selectedValueElement = selectElement.querySelector(".ant-select-selection-item");
        if (selectedValueElement) {
          const valueAttribute =
            selectElement.getAttribute("data-selection-value") ||
            selectedValueElement.getAttribute("data-value") ||
            selectedValueElement.getAttribute("value");

          const displayValue = selectedValueElement.textContent;
          const value = valueAttribute || displayValue;

          if (selectElement.getAttribute("datatype") === ValueType.Number && !isNaN(Number(value))) {
            result[fieldName] = Number(value);
          } else {
            result[fieldName] = value;
          }
        }
      }
    });

    const switchElements = rowElement.querySelectorAll(".ant-switch");
    switchElements.forEach((switchElement) => {
      const columnElement = this.findAttributeInTree(switchElement as HTMLElement, "datavalue");
      if (columnElement) {
        const fieldName = columnElement;
        const isChecked = switchElement.classList.contains("ant-switch-checked");
        result[fieldName] = isChecked;
      }
    });

    const checkboxElements = rowElement.querySelectorAll(".ant-checkbox-wrapper");
    checkboxElements.forEach((checkboxElement) => {
      const columnElement = this.findAttributeInTree(checkboxElement as HTMLElement, "datavalue");
      if (columnElement) {
        const fieldName = columnElement;
        const isChecked = checkboxElement.querySelector(".ant-checkbox-checked") !== null;
        result[fieldName] = isChecked;
      }
    });

    return result;
  };

  getRowComponent = () => {
    const childList = React.Children.toArray(this.props.children);
    return childList[1];
  };

  getPagination = () => {
    if (this.props.pagination && this.props.pagination === true) {
      const paginationProps = {
        pageSize: this.state.pageSize,
        showSizeChanger: this.props.showSizeChanger,
        pageSizeOptions: this.getPageSizeOptions(),
        onShowSizeChange: (current: number, size: number) => this.onPageSizeChange(current, size),
        onChange: (current: number) => this.setState({ current } as any)
      };
      return paginationProps;
    }
    return false;
  };

  onPageSizeChange = (current: number, size: number) => {
    this.setState({ pageSize: size, current } as any);
  };

  getPageSizeOptions = () => {
    const options = this.props.options;
    if (options && options.length > 0) {
      const mappedOptions: number[] = [];
      options.forEach((option: any) => {
        const value = option.text?.trim() ?? option[this.props.datavaluefield];
        if (!isNaN(value) && value !== null && value !== undefined && value !== "") {
          mappedOptions.push(parseInt(value, 10));
        }
      });
      mappedOptions?.sort((a, b) => a - b);
      if (mappedOptions.length > 0) return mappedOptions;
    }

    return BaseTable.DEFAULT_PAGE_SIZE_OPTIONS;
  };

  shouldComponentUpdate = (nextProps: any, nextState: any) => {
    if (window.kuika?.isDesignTime) {
      // In design time, allow updates if children changed (e.g., dragging elements into columns)
      // Compare children reference - React creates new elements when structure changes
      if (nextProps.children !== this.props.children) {
        return true;
      }
      return false;
    }
    if (nextProps.mode === "carousel") {
      if (this.props.dataSource === undefined || this.props.dataSource !== nextProps.dataSource) return true;
      return false;
    }
    if (nextProps.mode === "google-maps") {
      if (this.props.dataSource === undefined || this.props.dataSource !== nextProps.dataSource) {
        if (_.isEqual(this.props.dataSource, nextProps.dataSource) === false) {
          return true;
        }
      }
      if (this.props.waypoints === undefined || this.props.waypoints !== nextProps.waypoints) {
        if (_.isEqual(this.props.waypoints, nextProps.waypoints) === false) {
          return true;
        }
      }
      if (this.refreshGoogleMapsBodyWrapper && this.state.value !== nextState.value) {
        const { value } = nextState;
        if (value != undefined && value.latitude != undefined && value.longitude != undefined) {
          if (this.props.autoCenter) {
            this.refreshGoogleMapsBodyWrapper({
              value: nextState.value,
              latitude: value.latitude,
              longitude: value.longitude
            });
          } else {
            this.refreshGoogleMapsBodyWrapper({ value: nextState.value });
          }
        }

        this.refreshGoogleMapsBodyWrapper({ value: nextState.value });
      }
      return false;
    }
    return !shallowequal(nextProps, this.props) || !shallowequal(nextState, this.state);
  };

  getRowProps = () => {
    const result: any = {};
    if (this.props.mode === "gallery") {
      result.desktopColumnsCount = (this.props as GalleryItemProps).desktopColumnsCount;
      result.tabletColumnsCount = (this.props as GalleryItemProps).tabletColumnsCount;
      result.phoneColumnsCount = (this.props as GalleryItemProps).phoneColumnsCount;
    } else if (this.props.mode === "google-maps") {
      result.value = this.state.value;
      result.map = this.state.map;
      result.maps = this.state.maps;
      result.isDataMarkerSelected = this.state.isDataMarkerSelected;
    }
    return result;
  };

  convertToCoordsFieldsFromLatField = (record: any): number | undefined => {
    if (this.props.dataLatField === undefined) return undefined;
    if (this.props.dataLatField !== undefined && record[toCamelCase(this.props.dataLatField)] !== undefined) {
      const latitude = parseFloat(record[toCamelCase(this.props.dataLatField)]);
      return latitude;
    }
  };

  convertToCoordsFieldsFromLngField = (record: any): number | undefined => {
    if (this.props.dataLngField === undefined) return undefined;
    if (this.props.dataLngField !== undefined && record[toCamelCase(this.props.dataLngField)] !== undefined) {
      const longitude = parseFloat(record[toCamelCase(this.props.dataLngField)]);
      return longitude;
    }
  };

  getRowLat = (rowData: any) => {
    const latitude = this.convertToCoordsFieldsFromLatField(rowData);
    return latitude;
  };

  getRowLng = (rowData: any) => {
    const longitude = this.convertToCoordsFieldsFromLngField(rowData);
    return longitude;
  };

  resetEditableRow = () => {
    if (this.state.activeEditableRowIndex != -1 && (this.props.onRowEditFinished || this.props.onInsertRowFinished)) {
      this.handleOnRowEditFinished();
    }
  };

  getDataSource = () => {
    const isDesignTime = window.kuika?.isDesignTime;
    if (this.props.insertRowActive && this.props.dataSource && !isDesignTime) {
      const rowKey = this.props.rowKey?.toString()?.toLowerCase() || "id";
      let mockDataSource = {
        [rowKey]: this.props.onInsertRowFinished ? undefined : this.randomGuid
      };

      let children = this.props.children[1].props.children;
      if (children?.length > 0) {
        children.forEach((element) => {
          if (
            element &&
            element.props &&
            element.props.children &&
            element.props.children[0] &&
            element.props.children[0].props &&
            element.props.children[0].props.value
          ) {
            var key = element.props.children[0].props.value as string;
            if (key) {
              key = key.replace("[datafield:", "").replace("]", "");
              if (key !== rowKey) {
                mockDataSource[key] = undefined;
              }
            }
          }
        });
      }
      if (this.props.insertRowPosition === "bottom") {
        let dataSource = this.props.dataSource;
        let newDataSource = [];
        if (this.props.pagination === true) {
          dataSource.forEach((element, index) => {
            newDataSource.push(element);
            if (index % 8 === 0 && index !== 0) {
              newDataSource.push(mockDataSource);
            } else if (index === dataSource.length - 1) {
              newDataSource.push(mockDataSource);
            }
          });
        } else {
          newDataSource = [...dataSource, mockDataSource];
        }
        return newDataSource;
      }
      return [mockDataSource, ...this.props.dataSource];
    }

    if (typeof this.props.dataSource === "string") {
      const rowKey = this.props.rowKey?.toString()?.toLowerCase() || "id";
      let mockDataSource = {
        [rowKey]: this.props.onInsertRowFinished ? undefined : this.randomGuid
      };
      return [mockDataSource];
    }
    if (window.kuika.dashboardState === 17) return this.props.dataSource.slice(0, 2);
    return this.props.dataSource;
  };

  getTableStyles = () => {
    if (window.kuika.dashboardState === 17) {
      const { borderWidth, borderColor, borderLeftWidth, borderRightWidth, borderTopWidth, borderBottomWidth } =
        this.props.style;
      if (borderWidth || borderColor || borderLeftWidth || borderRightWidth || borderTopWidth || borderBottomWidth) {
        this.props.style.borderStyle = "solid";
      }
    }

    if (this.props.mode === "carousel") {
      const { width, height, maxWidth, maxHeight } = this.props.style;
      delete this.props.style?.width;
      delete this.props.style?.height;
      delete this.props.style?.maxWidth;
      delete this.props.style?.maxHeight;
      return {
        width,
        height,
        maxWidth,
        maxHeight
      };
    }
  };

  setStartingIndex = (index: number) => {
    const totalSlides = this.props.dataSource?.length || 0;
    const validIndex = index < totalSlides ? index : index % totalSlides;
    if (this.props.mode === "carousel") {
      this.setState({ carouselStartingIndex: validIndex } as any);
      this.forceUpdate();
    }
  };

  static normalizeTurkishString = (str: any): string => {
    if (!str || typeof str !== "string") return "";
    const lowerCased = str.toLocaleLowerCase("tr-TR");
    const normalized = lowerCased
      .replace(/ı/g, "i")
      .replace(/ğ/g, "g")
      .replace(/ü/g, "u")
      .replace(/ş/g, "s")
      .replace(/ö/g, "o")
      .replace(/ç/g, "c");
    return normalized;
  };

  setSearchText = (searchText: string) => {
    this.setState({ searchText } as any);
  };

  setColumnSearchTexts = (key: string, value: string) => {
    const columnSearchTexts: { [key: string]: string } = {
      ...this.state.columnSearchTexts
    };
    columnSearchTexts[key] = value;
    this.setState({ columnSearchTexts } as any);
  };

  onHeaderSearchBlur = () => {
    if (this.props.onConfigChange) this.props.onConfigChange();
    this.requestSyncColumnWidths();
  };

  filterListBySearchText(rows: any, searchText: any) {
    const searchNormalized = BaseTable.normalizeTurkishString(searchText?.toString());
    let filteredList = rows;
    if (searchText && searchText !== "") {
      const visibleColumns = this.state.columns
        ?.filter(
          (c) =>
            c.visibility !== "hidden" &&
            !this.state.hiddenColumnList?.some((id) => id.toLowerCase() === c.id.toLowerCase())
        )
        ?.map((c: any) => c.dataValue)
        ?.filter(Boolean);

      filteredList = rows.filter((item: any) => {
        for (let key in item) {
          if (!visibleColumns?.some((col) => col.toLowerCase() === key.toLowerCase())) continue;

          const itemNormalized = BaseTable.normalizeTurkishString(item[key]?.toString());
          if (
            (typeof item[key] === "string" || typeof item[key] === "number") &&
            itemNormalized?.includes(searchNormalized)
          ) {
            return true;
          }
        }
        return false;
      });
    }

    const columnSearchTexts = this.state.columnSearchTexts;
    if (columnSearchTexts) {
      for (let key in columnSearchTexts) {
        const columnSearchText: any = columnSearchTexts[key];
        if (columnSearchText || columnSearchText === false) {
          const filteredListByColumnSearchText = filteredList.filter((item: any) => {
            const cellValue = item[key];

            const columnMeta = this.state.columns?.find(
              (c: any) => c?.dataValue && c.dataValue.toString().toLowerCase() === key.toString().toLowerCase()
            );
            const dateSearchFormat = (columnMeta as any)?.dateSearchFormat;
            const columnType = (columnMeta as any)?.dataType ?? ValueType.String;

            const searchDate = BaseTable.parseDateWithFormat(columnSearchText, dateSearchFormat, columnType);
            const isSearchDate = !!columnSearchText && searchDate.isValid();
            const isCellDate =
              BaseTable.getValueType(cellValue) === ValueType.Date ||
              (!!dateSearchFormat && typeof cellValue === "string");
            if (isSearchDate || isCellDate) {
              const cellDate = BaseTable.parseDateWithFormat(cellValue, dateSearchFormat, columnType);
              if (cellDate.isValid() && isSearchDate) {
                const comparisonUnit = BaseTable.getDateComparisonUnit(dateSearchFormat);
                if (cellDate.isSame(searchDate, comparisonUnit)) {
                  return true;
                }
                const normalizedSearch = BaseTable.normalizeTurkishString(columnSearchText.toString());
                const formattedCellDate = dateSearchFormat
                  ? cellDate.format(dateSearchFormat)
                  : cellDate.format("DD.MM.YYYY");
                const normalizedFormattedDate = BaseTable.normalizeTurkishString(formattedCellDate);
                if (normalizedFormattedDate.includes(normalizedSearch)) {
                  return true;
                }
              }
            }

            if (typeof cellValue === "string" || typeof cellValue === "number") {
              const itemNormalized = BaseTable.normalizeTurkishString(cellValue.toString());
              const columnSearchTextNormalized = BaseTable.normalizeTurkishString(columnSearchText.toString());
              if (itemNormalized.includes(columnSearchTextNormalized)) {
                return true;
              }
            }
            if (typeof item[key] === "boolean") {
              if (item[key] === true && (columnSearchText === "true" || columnSearchText === true)) {
                return true;
              }
              if (item[key] === false && (columnSearchText === "false" || columnSearchText === false)) {
                return true;
              }
            }
            return false;
          });
          filteredList = filteredListByColumnSearchText;
        }
      }
    }

    return filteredList;
  }

  handleCheckboxChange = (checkedValues) => {
    const { columns } = this.state;

    const allVisibleColumns = columns.filter((c) => c.visibility !== "hidden").map((c) => c.id);
    const newHiddenColumnList: any = allVisibleColumns.filter((id) => !checkedValues.includes(id));

    this.setState({ hiddenColumnList: newHiddenColumnList });
  };

  handleDragEnd = (result) => {
    if (!result.destination) {
      return;
    }

    const columns: any = this.reorder(this.state.columns, result.source.index, result.destination.index);

    const columnOrder = columns.map((col: any) => col.id);

    if (this.state.columnGroups?.length > 0) {
      const groupColumnIds = this.state.columnGroups.map((group) => group.children.map((child) => child.id)).flat();

      const allColumnIds = [...columnOrder];
      groupColumnIds.forEach((id) => {
        if (!allColumnIds.includes(id)) {
          allColumnIds.push(id);
        }
      });

      this.setState(
        {
          columns,
          columnOrder: allColumnIds as any
        },
        () => {
          this.handleColumnOrderChange(allColumnIds);
          this.requestSyncColumnWidths();
        }
      );
    } else {
      this.setState(
        {
          columns,
          columnOrder
        },
        () => {
          this.handleColumnOrderChange(columnOrder);
          this.requestSyncColumnWidths();
        }
      );
    }
  };

  reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
  };

  setResizingColumnIndex = (index: any, startX: number) => {
    this.resizingColumnIndexRef = React.createRef();
    (this.resizingColumnIndexRef as any).current = index;
    this.startX = startX;

    const table = document.getElementById(this.props.id);
    if (table) {
      table.classList.add("resizing-active");

      table.style.setProperty("--resizing-column-index", index.toString());

      const firstColumnCell: any = table.querySelector(`[columnindex="${index}"]`);
      if (firstColumnCell) {
        this.initialWidth = firstColumnCell.offsetWidth;

        const allResizeHandles = table.querySelectorAll(`[columnindex="${index}"] .resizeHandle`);
        allResizeHandles.forEach((resizeHandle: HTMLElement) => {
          resizeHandle.classList.add("resizing");
        });

        const allColumnCells = table.querySelectorAll(`[columnindex="${index}"]`);
        allColumnCells.forEach((cell: HTMLElement) => {
          cell.classList.add("resizing-column");
        });
      }
    }

    this.setState({ resizingColumnIndex: index });

    window.addEventListener("mousemove", this.resizeListener);

    window.addEventListener("mouseup", this.handleResizeEnd, { once: true });
  };

  resizeListener = (e: any) => {
    if (
      this.resizingColumnIndexRef.current !== undefined &&
      this.startX !== undefined &&
      this.initialWidth !== undefined
    ) {
      const table = document.getElementById(this.props.id);
      if (table) {
        const deltaX = e.clientX - this.startX;
        const newColumnWidth = this.initialWidth + deltaX;
        if (newColumnWidth > 50) {
          const allColumnCells = table.querySelectorAll(`[columnindex="${this.resizingColumnIndexRef.current}"]`);
          allColumnCells.forEach((cell: HTMLElement) => {
            cell.style.width = `${newColumnWidth}px`;
            cell.style.minWidth = `${newColumnWidth}px`;
            cell.style.maxWidth = `${newColumnWidth}px`;

            if (!cell.classList.contains("resizing-column")) {
              cell.classList.add("resizing-column");
            }
          });

          this.setState({
            columnWidths: {
              ...this.state.columnWidths,
              [this.resizingColumnIndexRef.current]: newColumnWidth
            }
          });
        }
      }
    }
  };

  handleResizeEnd = () => {
    const table = document.getElementById(this.props.id);
    if (table && this.resizingColumnIndexRef.current !== undefined) {
      table.classList.remove("resizing-active");

      table.style.removeProperty("--resizing-column-index");

      const allResizeHandles = table.querySelectorAll(
        `[columnindex="${this.resizingColumnIndexRef.current}"] .resizeHandle`
      );
      allResizeHandles.forEach((resizeHandle: HTMLElement) => {
        resizeHandle.classList.remove("resizing");
      });

      const allColumnCells = table.querySelectorAll(`[columnindex="${this.resizingColumnIndexRef.current}"]`);
      allColumnCells.forEach((cell: HTMLElement) => {
        cell.classList.remove("resizing-column");
        cell.style.removeProperty("width");
        cell.style.removeProperty("min-width");
        cell.style.removeProperty("max-width");
      });
    }

    window.removeEventListener("mousemove", this.resizeListener);
    this.setState({ resizingColumnIndex: undefined });
    if (this.props.onConfigChange) this.props.onConfigChange();
    this.requestSyncColumnWidths();
  };

  handleMouseUp = () => {
    if (this.state.resizingColumnIndex !== undefined) {
      const table = document.getElementById(this.props.id);
      if (table && this.resizingColumnIndexRef.current !== undefined) {
        table.classList.remove("resizing-active");

        table.style.removeProperty("--resizing-column-index");

        const allResizeHandles = table.querySelectorAll(
          `[columnindex="${this.resizingColumnIndexRef.current}"] .resizeHandle`
        );
        allResizeHandles.forEach((resizeHandle: HTMLElement) => {
          resizeHandle.classList.remove("resizing");
        });

        const allColumnCells = table.querySelectorAll(`[columnindex="${this.resizingColumnIndexRef.current}"]`);
        allColumnCells.forEach((cell: HTMLElement) => {
          cell.classList.remove("resizing-column");
          cell.style.removeProperty("width");
          cell.style.removeProperty("min-width");
          cell.style.removeProperty("max-width");
        });
      }

      window.removeEventListener("mousemove", this.resizeListener);
      this.setState({ resizingColumnIndex: undefined });
      if (this.props.onConfigChange) this.props.onConfigChange();
      this.requestSyncColumnWidths();
    }
  };

  handleColumnOrderChange = (newOrder) => {
    this.setState({ columnOrder: newOrder });
  };

  handleTableChange = (pagination, filters, sorter) => {
    if (sorter) {
      const sorterOptions = {
        field: sorter.column?.dataValue,
        order: sorter.order,
        columnId: sorter.column?.id
      };
      this.setState({ sorterOptions }, () => {
        this.requestSyncColumnWidths();
      });
    } else {
      this.setState({ sorterOptions: {} }, () => {
        this.requestSyncColumnWidths();
      });
    }
    if (filters) {
      Object.values(filters).forEach((filter: any) => {
        if (filter && filter[0] && filter[0].value) {
          const columns = this.state.columns;
          const column: any = columns.find((c) => c.id === filter[0].columnId);
          if (column) {
            filter[0].dataValue = column.dataValue;
            filter[0].columnId = column.id;

            filter[0].logicalOperator = filter[0].logicalOperator || "AND";

            BaseTable.normalizeFilterValue(filter[0], "type", StringFilterTypes.Contains);
            BaseTable.normalizeFilterValue(filter[0], "value");
            BaseTable.normalizeFilterValue(filter[0], "value2");

            if (filter[0].secondType) {
              BaseTable.normalizeFilterValue(filter[0], "secondType", StringFilterTypes.Contains);
              BaseTable.normalizeFilterValue(filter[0], "secondValue");
              BaseTable.normalizeFilterValue(filter[0], "secondValue2");
            }
          }
        }
      });
      this.setState({ filteringOptions: filters }, () => {
        this.requestSyncColumnWidths();
      });
    } else {
      this.setState({ filteringOptions: {} }, () => {
        this.requestSyncColumnWidths();
      });
    }
    if (this.props.onConfigChange) this.props.onConfigChange();
  };

  static normalizeFilterValue = (filter: any, field: string, defaultValue?: any) => {
    if (filter[field] === "") {
      filter[field] = undefined;
    }

    if (filter[field] === undefined && defaultValue !== undefined) {
      filter[field] = defaultValue;
    }
  };

  parseNumericValue = (value: any): number => {
    const num = Number(value);
    if (!Number.isNaN(num)) {
      return num;
    }
    if (typeof value === "string") {
      const normalized = value.replace(/\./g, "").replace(",", ".");
      return Number(normalized);
    }
    return num;
  };

  getFormattedValue(value: any, formatter: string) {
    if (formatter && value && value?.toString()?.trim() !== "") {
      const numericValue = this.parseNumericValue(value);
      switch (formatter) {
        case "money":
          if (Number.isNaN(numericValue)) {
            return "0.00";
          }

          return getCachedNumberFormat("tr-TR", {
            style: "currency",
            currency: "TRY",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          })
            .format(numericValue)
            .replace("₺", "");

        case "money-var-1":
          if (Number.isNaN(numericValue)) {
            return "0.00";
          }

          return getCachedNumberFormat("tr-TR", {
            style: "currency",
            currency: "TRY",
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
          })
            .format(numericValue)
            .replace("₺", "");

        case "money-tl":
          if (Number.isNaN(numericValue)) {
            return "₺0.00";
          }

          return getCachedNumberFormat("tr-TR", {
            style: "currency",
            currency: "TRY",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }).format(numericValue);

        case "money-tl-var-1":
          if (Number.isNaN(numericValue)) {
            return "₺0";
          }

          return getCachedNumberFormat("tr-TR", {
            style: "currency",
            currency: "TRY",
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
          }).format(numericValue);

        case "money-4-decimal":
          if (Number.isNaN(numericValue)) {
            return "0.0000";
          }

          return getCachedNumberFormat("tr-TR", {
            style: "currency",
            currency: "TRY",
            minimumFractionDigits: 4,
            maximumFractionDigits: 4
          })
            .format(numericValue)
            .replace("₺", "");

        case "money-eur":
          if (Number.isNaN(numericValue)) {
            return "€0.00";
          }
          return `€${getCachedNumberFormat("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }).format(numericValue)}`;

        case "money-usd":
          if (Number.isNaN(numericValue)) {
            return "$0.00";
          }
          return getCachedNumberFormat("en-US", {
            style: "currency",
            currency: "USD",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }).format(numericValue);

        case "money-eur-var-1":
          if (Number.isNaN(numericValue)) {
            return "€0";
          }
          return `€${getCachedNumberFormat("en-US", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
          }).format(numericValue)}`;

        case "money-usd-var-1":
          if (Number.isNaN(numericValue)) {
            return "$0";
          }
          return getCachedNumberFormat("en-US", {
            style: "currency",
            currency: "USD",
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
          }).format(numericValue);

        case "money-usd-4-decimal":
          if (Number.isNaN(numericValue)) {
            return "$0.0000";
          }
          return getCachedNumberFormat("en-US", {
            style: "currency",
            currency: "USD",
            minimumFractionDigits: 4,
            maximumFractionDigits: 4
          }).format(numericValue);

        case "money-eur-4-decimal":
          if (Number.isNaN(numericValue)) {
            return "€0.0000";
          }
          return `€${getCachedNumberFormat("en-US", {
            minimumFractionDigits: 4,
            maximumFractionDigits: 4
          }).format(numericValue)}`;

        case "money-tl-4-decimal":
          if (Number.isNaN(numericValue)) {
            return "₺0.0000";
          }
          return getCachedNumberFormat("tr-TR", {
            style: "currency",
            currency: "TRY",
            minimumFractionDigits: 4,
            maximumFractionDigits: 4
          }).format(numericValue);

        case "percent":
          return `${value}%`;

        case "fractional-2":
          return isNaN(numericValue) ? value : numericValue.toFixed(2);

        case "fractional-5":
          return isNaN(numericValue) ? value : numericValue.toFixed(5);

        case "round":
          return isNaN(numericValue) ? value : String(Math.round(numericValue));

        default:
          return value;
      }
    }
    return value?.toString();
  }

  getFilteredAndSortedDataSource = () => {
    let filteredList =
      this.props.searchable || this.props.globalSearch || this.state.searchText
        ? this.filterListBySearchText(this.getDataSource(), this.state.searchText)
        : this.getDataSource();

    if (this.state.filteringOptions) {
      const andFilters: any[] = [];
      const orFilters: any[] = [];

      Object.entries(this.state.filteringOptions).forEach(([c, filters]: any) => {
        if (filters && filters.length > 0) {
          const persistedFilter = filters[0];
          const { value, value2, type, columnId, logicalOperator, secondValue, secondValue2, secondType } =
            persistedFilter;

          const column = this.state.columns.find((col) => col.id === columnId);
          if (column) {
            const { dataValue, dataType }: any = column;
            const colorFn: CellBgColorFn | undefined = (column as any).calculatedCellBgColor;
            const filterMode = persistedFilter.filterMode ?? "condition";

            // Value/color filters carry their own OR semantics inside the selection
            // list, so across columns they are always AND'ed (matches "different
            // columns = AND"). Only condition filters honor the per-column AND/OR toggle.
            if (filterMode === "value" || filterMode === "color") {
              andFilters.push({ filterCriteria: persistedFilter, dataValue, dataType, colorFn });
              return;
            }

            const filterInfo = {
              filterCriteria: {
                value: value !== undefined ? value : null,
                value2: value2 !== undefined ? value2 : null,
                type: type || StringFilterTypes.Contains,
                logicalOperator,
                secondType,
                secondValue: secondValue !== undefined ? secondValue : null,
                secondValue2: secondValue2 !== undefined ? secondValue2 : null,
                dateSearchFormat: (column as any).dateSearchFormat
              },
              dataValue,
              dataType,
              colorFn: undefined as CellBgColorFn | undefined
            };

            if (logicalOperator === "OR") {
              orFilters.push(filterInfo);
            } else {
              andFilters.push(filterInfo);
            }
          }
        }
      });

      if (andFilters.length > 0) {
        filteredList = filteredList.filter((record) => {
          return andFilters.every((filter) => {
            return BaseTable.applyColumnFilter(
              filter.filterCriteria,
              record,
              filter.dataValue,
              filter.dataType,
              filter.colorFn
            );
          });
        });
      }

      if (orFilters.length > 0) {
        filteredList = filteredList.filter((record) => {
          return orFilters.some((filter) => {
            return BaseTable.applyColumnFilter(
              filter.filterCriteria,
              record,
              filter.dataValue,
              filter.dataType,
              filter.colorFn
            );
          });
        });
      }
    }

    if (this.state.sorterOptions && this.state.sorterOptions.field) {
      const { field, order } = this.state.sorterOptions;
      filteredList.sort((a, b) => {
        const result = BaseTable.compare(field, a, b, this.props.rowKey?.toString()?.toLowerCase());
        return order === "ascend" ? result : -result;
      });
    }

    return filteredList;
  };

  renderSummary = () => {
    const isDesignTime = window.kuika?.isDesignTime;

    const hasFooter = this.state.columns?.some(
      (column: any) => column.footerOptions !== FooterOptions.None && column.footerOptions
    );
    if (!hasFooter) return <></>;

    const calculateTotals = (column) => {
      const { footerOptions, dataType, dataValue, footerFormatter } = column;
      if (footerOptions === FooterOptions.None) return "";

      let filteredList = this.getFilteredAndSortedDataSource();

      const columnData = filteredList?.map((row) => row[dataValue]) || [];

      if (footerOptions === FooterOptions.Count) {
        return this.getFormattedValue(columnData.length, footerFormatter);
      }

      const numericData = columnData.map((val) => (val != null ? parseFloat(val) : NaN)).filter((val) => !isNaN(val));

      if (dataType !== ValueType.Number && numericData.length === 0) return "";

      switch (footerOptions) {
        case FooterOptions.Sum:
          return this.getFormattedValue(
            numericData.reduce((acc, val) => acc + val, 0),
            footerFormatter
          );
        case FooterOptions.Average:
          return this.getFormattedValue(
            numericData.length ? (numericData.reduce((acc, val) => acc + val, 0) / numericData.length).toFixed(2) : 0,
            footerFormatter
          );
        case FooterOptions.Min:
          return this.getFormattedValue(numericData.length ? Math.min(...numericData) : 0, footerFormatter);
        case FooterOptions.Max:
          return this.getFormattedValue(numericData.length ? Math.max(...numericData) : 0, footerFormatter);
        default:
          return "";
      }
    };

    const visibleColumns = this.state.columns?.filter(
      (col: any) =>
        col.visibility !== "hidden" &&
        !this.state.hiddenColumnList?.some((id) => id.toLowerCase() === col.id.toLowerCase())
    );
    const totals = visibleColumns?.map(calculateTotals);

    const convertFontWeightToNumber = (fontWeight: string): number => {
      const fontWeightMap = {
        Thin: 100,
        "Extra-Light": 200,
        Light: 300,
        Regular: 400,
        Medium: 500,
        "Semi-Bold": 600,
        Bold: 700,
        "Extra-Bold": 800,
        Black: 900
      };
      return fontWeightMap[fontWeight] || 400;
    };

    const renderSummaryCell = (column, index) => {
      const {
        footerOptions,
        footerFormatter,
        dataType,
        style,
        footerOptionLabel,
        footerFontSize,
        footerFontColor,
        footerTextAlign,
        footerFontWeight
      } = column;
      const hasFooterCalculation =
        dataType === ValueType.Number ||
        isDesignTime ||
        footerOptions === FooterOptions.Count ||
        (footerOptions && footerOptions !== FooterOptions.None && totals[index] !== "");

      if (!hasFooterCalculation) return <Table.Summary.Cell key={index} index={index} />;
      const designTimeValue = footerOptions !== FooterOptions.None ? this.getFormattedValue("20", footerFormatter) : "";
      const content = (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent:
              footerTextAlign === "middle"
                ? "center"
                : footerTextAlign === "left"
                  ? "flex-start"
                  : footerTextAlign === "right"
                    ? "flex-end"
                    : style?.textAlign === "-webkit-center"
                      ? "center"
                      : style?.textAlign === "-webkit-left"
                        ? "flex-start"
                        : style?.textAlign === "-webkit-right"
                          ? "flex-end"
                          : ""
          }}
        >
          {footerOptionLabel && (
            <span
              style={{
                fontSize: footerFontSize,
                color: footerFontColor,
                fontWeight: footerFontWeight ? convertFontWeightToNumber(footerFontWeight) : undefined
              }}
            >
              {footerOptionLabel}
            </span>
          )}
          <span
            style={{
              fontSize: footerFontSize,
              color: footerFontColor,
              fontWeight: footerFontWeight ? convertFontWeightToNumber(footerFontWeight) : undefined,
              marginLeft:
                footerTextAlign !== null && footerTextAlign !== undefined
                  ? footerTextAlign === "left"
                    ? footerOptionLabel
                      ? "8px"
                      : "0px"
                    : footerTextAlign === "right"
                      ? "auto"
                      : footerTextAlign === "middle"
                        ? footerOptionLabel
                          ? "8px"
                          : "0px"
                        : ""
                  : style?.textAlign === "-webkit-left"
                    ? footerOptionLabel
                      ? "8px"
                      : "0px"
                    : style?.textAlign === "-webkit-right"
                      ? "auto"
                      : style?.textAlign === "-webkit-center"
                        ? footerOptionLabel
                          ? "8px"
                          : "0px"
                        : ""
            }}
          >
            {isDesignTime ? designTimeValue : totals[index] || ""}
          </span>
        </div>
      );

      return (
        <Table.Summary.Cell
          key={index}
          index={index}
          align={
            footerTextAlign !== null && footerTextAlign !== undefined
              ? footerTextAlign === "middle"
                ? "center"
                : footerTextAlign
              : style?.textAlign === "-webkit-center"
                ? "center"
                : style?.textAlign === "-webkit-left"
                  ? "left"
                  : style?.textAlign === "-webkit-right"
                    ? "right"
                    : "left"
          }
        >
          {content}
        </Table.Summary.Cell>
      );
    };
    return (
      <Table.Summary>
        <Table.Summary.Row>
          {this.props.multiSelect && <Table.Summary.Cell index={0} />}
          {visibleColumns?.map(renderSummaryCell)}
        </Table.Summary.Row>
      </Table.Summary>
    );
  };

  onExport = async (): Promise<void> => {
    // KOA-15072: the Excel engine is imported statically (top of this file) so it
    // ships inside the initial <script> bundle. It used to be a lazy `import("exceljs")`
    // async chunk; on sub-path on-prem deployments that chunk 404'd and the SPA fallback
    // returned index.html in its place (`ChunkLoadError` / "Unexpected token '<'"), so the
    // rejected promise from this fire-and-forget handler was swallowed and the button
    // silently did nothing. The try/catch guarantees a failure can never be a silent no-op.
    try {
      await this.buildAndDownloadExcel();
    } catch (error) {
      console.error("Table export failed:", error);
    }
  };

  private buildAndDownloadExcel = async (): Promise<void> => {
    const workbook: ExcelJS.Workbook = this.buildExportWorkbook();

    workbook.xlsx
      .writeBuffer()
      .then((buffer) => {
        const blob = new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        });
        const fileName = this.props.exportFileName ? `${this.props.exportFileName}.xlsx` : "export.xlsx";
        saveAs(blob, fileName);
      })
      .catch((error) => {
        console.error("Error saving the file:", error);
      });
  };

  // A column can appear in the Excel export only if it can actually produce data: an explicit
  // dataValue binding, [datafield:] bindings among its children, or at least one row resolving
  // a non-empty value (covers the title/key/id fallback lookups). Action/button-only columns
  // fail all three and used to land in the Excel as blank columns that the export settings
  // popover could not even deselect, because it only lists data-bound columns. KOA-17950.
  private canColumnExportData = (column: ExportableColumn, records): boolean => {
    if (column.dataValue || column.hasDataBoundChildren) {
      return true;
    }
    if (!Array.isArray(records)) {
      return false;
    }
    return records.some((record, recordIndex) => {
      const resolution: IExportCellResolution = this.resolveExportCellValue(column, record, recordIndex);
      return resolution.rawValue !== undefined && resolution.rawValue !== null && resolution.rawValue !== "";
    });
  };

  protected buildExportWorkbook = (): ExcelJS.Workbook => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Sheet1");

    const colorToARGB = (color: string): string => {
      if (!color) return "";

      if (color.startsWith("#")) {
        const hex = color.replace("#", "");
        return hex.length === 6 ? `FF${hex}` : hex;
      }

      if (color.startsWith("rgb")) {
        const rgbMatch = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
        if (rgbMatch) {
          const r = parseInt(rgbMatch[1]).toString(16).padStart(2, "0");
          const g = parseInt(rgbMatch[2]).toString(16).padStart(2, "0");
          const b = parseInt(rgbMatch[3]).toString(16).padStart(2, "0");
          const a = rgbMatch[4]
            ? Math.round(parseFloat(rgbMatch[4]) * 255)
                .toString(16)
                .padStart(2, "0")
            : "FF";
          return a + r + g + b;
        }
      }

      return color.replace("#", "");
    };

    const visibleColumns = this.state.columns?.filter(
      (column: any) =>
        column.excludeFromExport !== true &&
        !this.state.excludedFromExportList?.includes(column.id) &&
        column.visibility !== "hidden" &&
        !this.state.hiddenColumnList?.some((id) => id.toLowerCase() === column.id.toLowerCase())
    );

    const sortedColumns = [
      ...visibleColumns.filter((col: any) => col.fixed === "left"),
      ...visibleColumns.filter((col: any) => !col.fixed),
      ...visibleColumns.filter((col: any) => col.fixed === "right")
    ];

    interface ColumnGroup {
      title: string;
      startIndex: number | null;
      endIndex: number | null;
      children: ExportableColumn[];
    }

    const columnGroups: { [key: string]: ColumnGroup } = {};
    this.state.columnGroups?.forEach((group) => {
      const visibleChildren = group.children.filter(
        (child: any) =>
          child.excludeFromExport !== true &&
          !this.state.excludedFromExportList?.includes(child.id) &&
          child.visibility !== "hidden" &&
          !this.state.hiddenColumnList?.some((id) => id.toLowerCase() === child.id.toLowerCase())
      );

      if (visibleChildren.length > 0) {
        columnGroups[group.title] = {
          title: group.title,
          startIndex: null,
          endIndex: null,
          children: visibleChildren
        };
      }
    });

    for (const group of Object.values(columnGroups)) {
      for (const child of group.children) {
        if (!sortedColumns.some((col: any) => col.id === child.id)) {
          sortedColumns.push(child);
        }
      }
    }

    const filteredList = this.getFilteredAndSortedDataSource();
    const exportableColumns = sortedColumns.filter((column: ExportableColumn) =>
      this.canColumnExportData(column, filteredList)
    );

    const columnDefinitions = exportableColumns.map(({ title }: any, colIndex: number) => ({
      header: title,
      key: `col${colIndex}`,
      width: 15
    }));

    worksheet.columns = columnDefinitions;

    const headerRow = worksheet.getRow(1);
    headerRow.font = { bold: true };
    headerRow.alignment = { vertical: "middle", horizontal: "center" };

    exportableColumns.forEach(({ style }: any, colIndex: number) => {
      if (style?.backgroundColor) {
        const cell = headerRow.getCell(colIndex + 1);
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: colorToARGB(style.backgroundColor) }
        };
      }
    });

    if (Object.keys(columnGroups).length > 0) {
      worksheet.spliceRows(1, 0, new Array(exportableColumns.length).fill(null));
      const groupHeaderRow = worksheet.getRow(1);

      let currentColIndex = 1;
      for (const col of exportableColumns) {
        for (const [groupTitle, group] of Object.entries(columnGroups)) {
          if (group.children.some((child: any) => child.id === col.id)) {
            if (group.startIndex === null) {
              group.startIndex = currentColIndex;
              group.endIndex = currentColIndex;
            } else {
              group.endIndex = currentColIndex;
            }
            break;
          }
        }
        currentColIndex++;
      }

      for (const [groupTitle, group] of Object.entries(columnGroups)) {
        if (group.startIndex !== null && group.endIndex !== null) {
          const startCell = groupHeaderRow.getCell(group.startIndex);
          startCell.value = groupTitle;

          if (group.endIndex > group.startIndex) {
            worksheet.mergeCells(1, group.startIndex, 1, group.endIndex);
          }
        }
      }

      groupHeaderRow.font = { bold: true };
      groupHeaderRow.alignment = { vertical: "middle", horizontal: "center" };

      const updatedHeaderRow = worksheet.getRow(2);
      updatedHeaderRow.font = { bold: true };
      updatedHeaderRow.alignment = { vertical: "middle", horizontal: "center" };

      exportableColumns.forEach(({ style }: any, colIndex: number) => {
        if (style?.backgroundColor) {
          const cell = updatedHeaderRow.getCell(colIndex + 1);
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: colorToARGB(style.backgroundColor) }
          };
        }
      });
    }

    const serializeValue = (value: any): string | number | boolean | Date | null => {
      if (value === null || value === undefined) {
        return null;
      }
      if (typeof value === "number") {
        return value;
      }
      if (typeof value === "boolean") {
        return value;
      }
      if (value instanceof Date) {
        return value;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          return value.map(serializeValue).join(", ");
        }
        try {
          return JSON.stringify(value);
        } catch {
          return String(value);
        }
      }
      return String(value);
    };

    filteredList.forEach((record, recordIndex) => {
      const rowData = {};
      let hasMultipleLabels = false;

      exportableColumns.forEach((column: ExportableColumn, colIndex: number) => {
        const { footerFormatter } = column;
        const resolution: IExportCellResolution = this.resolveExportCellValue(column, record, recordIndex);
        if (resolution.hasMultipleLabels) {
          hasMultipleLabels = true;
        }

        const rawValue = resolution.rawValue === undefined ? "" : resolution.rawValue;

        // A date-family formatter on the bound Label wins over the footer/serializer fallback so
        // the exported cell reads exactly like the rendered cell (KOA-17951).
        let value: string | number | boolean | Date | null;
        const dateFormattedValue: string | null = formatDateValueByFormatter(
          rawValue,
          resolution.formatter,
          resolution.customFormatter
        );
        if (dateFormattedValue !== null) {
          value = dateFormattedValue;
        } else if (footerFormatter) {
          value = this.getFormattedValue(rawValue, footerFormatter);
        } else {
          value = serializeValue(rawValue);
        }

        if (typeof value === "string" && value.includes("\n")) {
          hasMultipleLabels = true;
        }

        rowData[`col${colIndex}`] = value;
      });

      const row = worksheet.addRow(rowData);

      if (hasMultipleLabels) {
        row.height = 28;
      }

      exportableColumns.forEach(({ style, calculatedCellBgColor }: any, colIndex: number) => {
        const cell = row.getCell(colIndex + 1);
        const bgColor = calculatedCellBgColor || style?.backgroundColor;
        if (bgColor) {
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: colorToARGB(bgColor) }
          };
        }

        const cellValue = cell.value;
        if (typeof cellValue === "string" && cellValue.includes("\n")) {
          cell.alignment = {
            wrapText: true,
            vertical: "top",
            horizontal: "left"
          };
        }
      });
    });

    worksheet.columns.forEach((column) => {
      let maxLength = 0;
      column.eachCell({ includeEmpty: true }, (cell) => {
        const cellValue = cell.value ? cell.value.toString() : "";
        const cellLength = cellValue.length;
        if (cellLength > maxLength) {
          maxLength = cellLength;
        }
      });
      column.width = Math.max(10, Math.min(maxLength + 2, 50));
    });

    const leftFixedCount = exportableColumns.filter((col: any) => col.fixed === "left").length;
    if (leftFixedCount > 0) {
      const headerRowCount = Object.keys(columnGroups).length > 0 ? 2 : 1;
      worksheet.views = [
        {
          state: "frozen",
          xSplit: leftFixedCount,
          ySplit: headerRowCount,
          topLeftCell: `${String.fromCharCode(65 + leftFixedCount)}${headerRowCount + 1}`,
          activeCell: `A${headerRowCount + 1}`
        }
      ];
    }

    return workbook;
  };

  // Resolves the raw exported value of one cell the same way the rendered cell shows it: the
  // explicit dataValue binding first, then [datafield:] props extracted from the rendered tree
  // (joined when a cell holds several), then primitive rendered content, then title/key/id
  // lookups on the record. `hasMultipleLabels` mirrors the horizontal multi-label join so the
  // caller can raise the row height. The display formatter of the binding that produced the
  // value is reported back so the caller can format date cells like the screen (KOA-17951);
  // the value itself is left raw, which keeps `canColumnExportData`'s emptiness probe honest.
  private resolveExportCellValue = (column: ExportableColumn, record, recordIndex: number): IExportCellResolution => {
    const { dataValue, render } = column;
    const title: string | undefined = typeof column.title === "string" ? column.title : undefined;
    let rawValue;
    let hasMultipleLabels = false;
    let formatter: string | undefined;
    let customFormatter: string | undefined;

    if (dataValue) {
      rawValue = objectPath.get(record, dataValue);
      formatter = column.exportFormatter;
      customFormatter = column.exportCustomFormatter;
    } else if (render) {
      try {
        const dataBindedFields: DataBindedFieldFormatter[] = [];

        const extractDataFields = (obj) => {
          if (!obj) return;

          if (Array.isArray(obj)) {
            obj.forEach((child) => extractDataFields(child));
            return;
          }

          if (obj.props) {
            Object.keys(obj.props).forEach((key) => {
              if (
                obj.props[key]?.toString?.().includes("[datafield:") &&
                key !== "visExField1" &&
                key !== "visExField2" &&
                key !== "tooltip"
              ) {
                dataBindedFields.push({
                  binding: obj.props[key].toString(),
                  formatter: obj.props.formatter,
                  customFormatter: obj.props.customFormatter
                });
              }
            });

            if (Array.isArray(obj.props.children)) {
              obj.props.children.forEach((child) => extractDataFields(child));
            } else {
              extractDataFields(obj.props.children);
            }
          }
        };

        const renderedContent = render(null, record, recordIndex);
        const renderedNode = renderedContent as IRenderedNode;
        extractDataFields(renderedContent);

        if (dataBindedFields.length === 1) {
          const fieldName: string = dataBindedFields[0].binding.replace("[datafield:", "").replace("]", "");
          rawValue = record[fieldName];
          formatter = dataBindedFields[0].formatter;
          customFormatter = dataBindedFields[0].customFormatter;
        } else if (
          dataBindedFields.length > 1 &&
          dataBindedFields.every((field) => field.binding === dataBindedFields[0].binding)
        ) {
          const fieldName: string = dataBindedFields[0].binding.replace("[datafield:", "").replace("]", "");
          rawValue = record[fieldName];
          // Same field rendered several times (e.g. a hidden twin): any sibling that declares a
          // formatter defines how the value is displayed.
          const entryWithFormatter: DataBindedFieldFormatter | undefined = dataBindedFields.find(
            (field: DataBindedFieldFormatter) => field.formatter
          );
          formatter = entryWithFormatter?.formatter;
          customFormatter = entryWithFormatter?.customFormatter;
        } else if (dataBindedFields.length > 1) {
          // Distinct fields joined into one cell: each is formatted with its own formatter here,
          // because the joined string can no longer be formatted per field afterwards.
          const values = dataBindedFields
            .map((field) => {
              const fieldName: string = field.binding.replace("[datafield:", "").replace("]", "");
              const fieldValue = record[fieldName] !== undefined ? record[fieldName] : "";
              const formattedFieldValue: string | null = formatDateValueByFormatter(
                fieldValue,
                field.formatter,
                field.customFormatter
              );
              return formattedFieldValue !== null ? formattedFieldValue : fieldValue;
            })
            .filter(Boolean);

          let hasVerticalDirection = false;
          try {
            const parentProps = renderedNode?.props;
            if (parentProps?.direction === "vertical") {
              hasVerticalDirection = true;
            }
            const checkForVerticalDirection = (obj) => {
              if (!obj) return false;
              if (obj.props?.direction === "vertical") return true;
              if (Array.isArray(obj.props?.children)) {
                return obj.props.children.some((child) => checkForVerticalDirection(child));
              }
              if (obj.props?.children) {
                return checkForVerticalDirection(obj.props.children);
              }
              return false;
            };
            if (!hasVerticalDirection) {
              hasVerticalDirection = checkForVerticalDirection(renderedContent);
            }
          } catch (e) {
            hasVerticalDirection = false;
          }

          rawValue = hasVerticalDirection ? values.join("\n") : values.join(" ");
          if (!hasVerticalDirection) {
            hasMultipleLabels = true;
          }
        } else if (
          typeof renderedContent === "string" ||
          typeof renderedContent === "number" ||
          typeof renderedContent === "boolean"
        ) {
          rawValue = renderedContent;
        } else if (renderedNode?.props?.value !== undefined) {
          rawValue = renderedNode.props.value;
        } else if (
          renderedNode?.props?.children &&
          (typeof renderedNode.props.children === "string" ||
            typeof renderedNode.props.children === "number" ||
            typeof renderedNode.props.children === "boolean")
        ) {
          rawValue = renderedNode.props.children;
        } else if (title && record[title] !== undefined) {
          rawValue = record[title];
        } else if (title && record[title.toLowerCase()] !== undefined) {
          rawValue = record[title.toLowerCase()];
        }
      } catch (e) {
        console.error("Error rendering cell for export:", e);
      }
    } else if (title && record[title] !== undefined) {
      rawValue = record[title];
    } else if (title && record[title.toLowerCase()] !== undefined) {
      rawValue = record[title.toLowerCase()];
    }

    if (rawValue === undefined) {
      if (column.key && record[column.key] !== undefined) {
        rawValue = record[column.key];
      } else if (column.id && record[column.id] !== undefined) {
        rawValue = record[column.id];
      } else if (column.id && record[column.id?.toLowerCase()] !== undefined) {
        rawValue = record[column.id?.toLowerCase()];
      }
    }

    return { rawValue, hasMultipleLabels, formatter, customFormatter };
  };

  parseColor = (color: string): { r: number; g: number; b: number; a?: number } | null => {
    if (!color) return null;

    color = color.trim();

    if (color.startsWith("#")) {
      const hex = color.substring(1);
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      return { r, g, b, a: 1 };
    }

    const rgbaMatch = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
    if (rgbaMatch) {
      return {
        r: parseInt(rgbaMatch[1]),
        g: parseInt(rgbaMatch[2]),
        b: parseInt(rgbaMatch[3]),
        a: rgbaMatch[4] ? parseFloat(rgbaMatch[4]) : 1
      };
    }

    return null;
  };

  calculateLuminance = (r: number, g: number, b: number): number => {
    const rsRGB = r / 255;
    const gsRGB = g / 255;
    const bsRGB = b / 255;

    const rLinear = rsRGB <= 0.03928 ? rsRGB / 12.92 : ((rsRGB + 0.055) / 1.055) ** 2.4;
    const gLinear = gsRGB <= 0.03928 ? gsRGB / 12.92 : ((gsRGB + 0.055) / 1.055) ** 2.4;
    const bLinear = bsRGB <= 0.03928 ? bsRGB / 12.92 : ((bsRGB + 0.055) / 1.055) ** 2.4;

    return 0.2126 * rLinear + 0.7152 * gLinear + 0.0722 * bLinear;
  };

  getContrastColor = (bgColor: string): string => {
    if (!bgColor || bgColor === "transparent") {
      return "#000000";
    }

    const rgb = this.parseColor(bgColor);
    if (!rgb) {
      return "#000000";
    }

    if (rgb.a !== undefined && rgb.a === 0) {
      return "#000000";
    }

    const luminance = this.calculateLuminance(rgb.r, rgb.g, rgb.b);
    return luminance > 0.5 ? "#000000" : "#ffffff";
  };

  getPopoverStyle = () => {
    const tableBgColor = this.props.style?.backgroundColor || this.props.style?.background;
    const hasTableBgColor = tableBgColor && tableBgColor !== "transparent";
    const contrastColor = hasTableBgColor ? this.getContrastColor(tableBgColor) : "#000000";

    if (!hasTableBgColor) return null;

    return (
      <style>
        {`
          .kuika-export-popover,
          .kuika-column-chooser-popover {
            box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.5) !important;
            border: 1px solid ${contrastColor} !important;
            border-radius: 8px !important;
          }
          .kuika-export-popover .ant-popover-content,
          .kuika-column-chooser-popover .ant-popover-content,
          .kuika-export-popover .kuika-export-column-selector,
          .kuika-column-chooser-popover .kuika_table__column_chooser__checkbox_group {
            background-color: ${tableBgColor} !important;
            border-radius: 8px !important;
          }
          .kuika-export-popover .ant-checkbox-wrapper,
          .kuika-column-chooser-popover .ant-checkbox-wrapper {
            color: ${contrastColor} !important;
          }
          .kuika-export-popover .ant-checkbox-wrapper span,
          .kuika-column-chooser-popover .ant-checkbox-wrapper span {
            color: ${contrastColor} !important;
          }
          .kuika-export-popover .ant-checkbox-inner,
          .kuika-column-chooser-popover .ant-checkbox-inner {
            border-color: ${contrastColor} !important;
            background-color: transparent !important;
          }
          .kuika-export-popover .ant-checkbox-checked .ant-checkbox-inner,
          .kuika-column-chooser-popover .ant-checkbox-checked .ant-checkbox-inner {
            background-color: ${contrastColor} !important;
            border-color: ${contrastColor} !important;
          }
          .kuika-export-popover .ant-checkbox-checked .ant-checkbox-inner::after,
          .kuika-column-chooser-popover .ant-checkbox-checked .ant-checkbox-inner::after {
            border-color: ${tableBgColor} !important;
          }
          .kuika-export-popover .kuika-export-column-item,
          .kuika-column-chooser-popover .draggable-item {
            background-color: transparent !important;
            color: ${contrastColor} !important;
          }
          .kuika-export-popover .kuika-export-column-item:hover,
          .kuika-column-chooser-popover .draggable-item:hover {
            background-color: ${contrastColor}20 !important;
          }
          .kuika-export-popover .kuika-export-column-selector-title,
          .kuika-export-popover .kuika-export-column-label,
          .kuika-column-chooser-popover .kuika-export-column-label {
            color: ${contrastColor} !important;
          }
          .kuika-export-popover .ant-popover-title,
          .kuika-column-chooser-popover .ant-popover-title {
            background-color: ${tableBgColor} !important;
            color: ${contrastColor} !important;
            border-bottom-color: ${contrastColor}40 !important;
          }
          .kuika-export-popover .ant-popover-inner,
          .kuika-column-chooser-popover .ant-popover-inner {
            background-color: ${tableBgColor} !important;
          }
          .kuika-export-popover .ant-popover-arrow::before,
          .kuika-column-chooser-popover .ant-popover-arrow::before {
            background-color: ${tableBgColor} !important;
          }
          .kuika-export-popover .drag-handle,
          .kuika-column-chooser-popover .drag-handle {
            color: ${contrastColor} !important;
          }
        `}
      </style>
    );
  };

  render = (): ReactNode => {
    const isDesignTime = window.kuika?.isDesignTime;
    const defaultEmptyProps = {
      xl: 24 / (this.props as GalleryItemProps).desktopColumnsCount,
      md: 24 / (this.props as GalleryItemProps).tabletColumnsCount,
      sm: 24 / (this.props as GalleryItemProps).phoneColumnsCount,
      xs: 24 / (this.props as GalleryItemProps).phoneColumnsCount,

      style: {
        padding: "5px 5px"
      }
    };
    let defaultTemp = [];

    if (this.props.kuikacomponentname == "GalleryView" && !this.props.children) {
      let a = Array(24).fill(1);
      a.forEach((element, index) => {
        defaultTemp.push(
          <Col {...defaultEmptyProps} key={index}>
            <div className="kuika_emptygalleryviewPlaceholder" />
          </Col>
        );
      });

      return <Row>{defaultTemp}</Row>;
    }
    if (this.props.mode == "carousel" && this.props.kuikacomponentname === "Carousel" && !this.props.children) {
      return <img src={CarouselPlaceholder} style={{ width: "100%" }} />;
    }
    if (this.props.mode == "carousel" && this.props.kuikacomponentname === "HorizontalGrid" && !this.props.children) {
      return <img src={HorizontalGridPlaceholder} style={{ width: "100%" }} />;
    }
    if (this.props.mode == "flex-grid" && !this.props.children && window.kuika.isDesignTime) {
      return <img src={FlexGridPlaceholder} style={{ width: "100%" }} />;
    }
    if (
      this.props.hideTableOnEmpty == true &&
      (this.props.dataSource == undefined || this.props.dataSource.length < 1)
    ) {
      return <></>;
    }

    const filteredList =
      this.props.searchable || this.props.globalSearch || this.state.searchText
        ? this.filterListBySearchText(this.getDataSource(), this.state.searchText)
        : this.getDataSource();
    const language = localStorage.getItem("ml");

    let noDataFoundMessageText = "No data found";
    if (language === "tr_TR") {
      noDataFoundMessageText = "Veri bulunamadı";
    } else if (language === "fr_FR") {
      noDataFoundMessageText = "Aucune donnée trouvée";
    } else if (language === "de_DE") {
      noDataFoundMessageText = "Keine Daten gefunden";
    } else if (language === "es_ES") {
      noDataFoundMessageText = "No se encontraron datos";
    }

    const hasFixedColumns = this.state.columns?.some((col: any) => col.fixed === "left" || col.fixed === "right");
    const hasVisibleFixedColumns = this.state.columns?.some(
      (col: any) =>
        (col.fixed === "left" || col.fixed === "right") &&
        col.visibility !== "hidden" &&
        !this.state.hiddenColumnList?.some((id) => id.toLowerCase() === col.id.toLowerCase())
    );
    // The row-number column always renders first, so it has to travel with the pinned-left
    // block: left unpinned it would scroll out from under the pinned columns and leave them
    // floating over the scrolling content.
    const hasFixedLeftColumn: boolean = this.state.columns?.some((col: any) => col.fixed === "left") === true;

    return (
      <>
        {this.getPopoverStyle()}
        <div className={`kuika_table__wrapper${this.getClassName()} ${this.props.className ?? ""}`.trim()}>
          {(this.props.globalSearch || this.props.columnChooser || this.props.export) && (
            <div
              style={{
                display: "flex",
                justifyContent:
                  this.props.style?.textAlign === "-webkit-center"
                    ? "center"
                    : this.props.style?.textAlign === "-webkit-left"
                      ? "flex-start"
                      : "flex-end",
                alignItems: "center",
                paddingBottom: 12,
                backgroundColor: this.props.style?.backgroundColor
              }}
            >
              {this.props.globalSearch && (
                <Input
                  placeholder={language === "tr_TR" ? "Ara..." : "Search..."}
                  style={{
                    width: 240,
                    backgroundColor: "transparent",
                    color: this.getContrastColor(this.props.style?.backgroundColor)
                  }}
                  className="kuika-table-search-input"
                  value={this.state.searchText}
                  onChange={(e) => this.setSearchText(e.target.value)}
                  readOnly={isDesignTime}
                  allowClear
                />
              )}

              {this.props.export && (
                <div className="kuika-table-export-wrapper">
                  <div className="kuika-export-button-group">
                    <Button
                      type="primary"
                      className="kuika-export-button"
                      icon={<DownloadOutlined />}
                      onClick={() => this.onExport()}
                      style={
                        this.props.exportButtonBgColor
                          ? {
                              backgroundColor: this.props.exportButtonBgColor,
                              borderColor: this.props.exportButtonBgColor
                            }
                          : undefined
                      }
                    >
                      {language === "tr_TR" ? "Dışa Aktar" : "Export"}
                    </Button>
                    <Popover
                      content={
                        <div className="kuika-export-column-selector">
                          <div className="kuika-export-column-selector-header">
                            <span className="kuika-export-column-selector-title">
                              {language === "tr_TR" ? "Kolonları Seç" : "Select Columns"}
                            </span>
                          </div>
                          <div className="kuika-export-column-list">
                            {this.state.columns
                              ?.filter(
                                (c: ExportableColumn) =>
                                  c.visibility !== "hidden" && (!!c.dataValue || c.hasDataBoundChildren === true)
                              )
                              .map((c: any, index) => (
                                <div key={c.id} className="kuika-export-column-item">
                                  <Checkbox
                                    checked={!this.state.excludedFromExportList?.includes(c.id)}
                                    onChange={(e) => {
                                      const excludedFromExportList = e.target.checked
                                        ? (this.state.excludedFromExportList || []).filter((id) => id !== c.id)
                                        : [...(this.state.excludedFromExportList || []), c.id];
                                      this.setState({
                                        excludedFromExportList
                                      } as any);
                                    }}
                                  >
                                    <span className="kuika-export-column-label">{c.title}</span>
                                  </Checkbox>
                                </div>
                              ))}
                          </div>
                        </div>
                      }
                      title={language === "tr_TR" ? "Dışa Aktarılacak Kolonlar" : "Columns to Export"}
                      trigger="click"
                      placement="bottomLeft"
                      overlayClassName="kuika-export-popover"
                    >
                      <div
                        className="kuika-export-settings-wrapper"
                        style={
                          this.props.exportButtonBgColor
                            ? {
                                backgroundColor: this.props.exportButtonBgColor,
                                borderColor: this.props.exportButtonBgColor
                              }
                            : undefined
                        }
                      >
                        <SettingOutlined className="kuika-export-settings-icon" />
                      </div>
                    </Popover>
                  </div>
                </div>
              )}

              {this.props.columnChooser && (
                <div style={{ marginLeft: 12 }}>
                  <Popover
                    content={
                      <DragDropContext onDragEnd={this.handleDragEnd}>
                        <Droppable droppableId="columnChooser">
                          {(provided) => (
                            <div
                              {...provided.droppableProps}
                              ref={provided.innerRef}
                              className="kuika_table__column_chooser__checkbox_group"
                            >
                              {this.state.columns
                                ?.filter((c) => c.visibility !== "hidden")
                                .map((c: any, index) => (
                                  <Draggable key={c.id} draggableId={c.id} index={index}>
                                    {(provided) => (
                                      <div
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        {...provided.dragHandleProps}
                                        className="draggable-item"
                                      >
                                        <MenuOutlined className="drag-handle" />
                                        <Checkbox
                                          checked={!this.state.hiddenColumnList.includes(c.id)}
                                          onChange={(e) => {
                                            if (
                                              this.state.hiddenColumnList.length ===
                                                this.state.columns.filter((c) => c.visibility !== "hidden").length -
                                                  1 &&
                                              !e.target.checked
                                            ) {
                                              return;
                                            }
                                            const hiddenColumnList: any = e.target.checked
                                              ? this.state.hiddenColumnList.filter((id) => id !== c.id)
                                              : [...this.state.hiddenColumnList, c.id];
                                            this.setState({ hiddenColumnList }, () => {
                                              if (this.props.onConfigChange) this.props.onConfigChange();
                                              this.requestSyncColumnWidths();
                                            });
                                          }}
                                        >
                                          {c.title}
                                        </Checkbox>
                                      </div>
                                    )}
                                  </Draggable>
                                ))}
                              {provided.placeholder}
                            </div>
                          )}
                        </Droppable>
                      </DragDropContext>
                    }
                    title={
                      language === "tr_TR"
                        ? "Kolon Seçici"
                        : language === "fr_FR"
                          ? "Sélectionneur de colonnes"
                          : "Column Chooser"
                    }
                    trigger="click"
                    placement="bottomLeft"
                    overlayClassName="kuika-column-chooser-popover"
                  >
                    <UnorderedListOutlined
                      style={{
                        pointerEvents: isDesignTime ? "none" : "auto"
                      }}
                    />
                  </Popover>
                </div>
              )}
            </div>
          )}
          <Table
            id={this.props.id}
            style={{ ...this.getTableStyles() }}
            showHeader={this.props.showHeader}
            pagination={this.getPagination()}
            dataSource={filteredList}
            onChange={this.handleTableChange}
            components={{
              table: this.getViewWrapper(),
              header: {
                wrapper: this.state.headerRenderer,
                row: TableHeaderRow,
                cell: TableHeaderCell
              },
              body: {
                wrapper: this.getBodyWrapper(),
                row: this.state.rowRenderer,
                cell: this.getBodyCell()
              }
            }}
            summary={this.renderSummary}
            locale={{
              emptyText: (
                <Empty
                  image={
                    this.props.noDataFoundImage ? (
                      <Image value={this.props.noDataFoundImage} />
                    ) : this.props.mode === "table" || this.props.showNoDataFound ? (
                      <img
                        src={EmptyDataSvg}
                        style={{
                          width: "100%"
                        }}
                      />
                    ) : null
                  }
                  description={
                    this.props.mode === "table" || this.props.showNoDataFound
                      ? this.props.nodatafoundmessage || noDataFoundMessageText
                      : ""
                  }
                />
              )
            }}
            onRow={(rowValue: any, rowIndex?: number) => ({
              ...this.getRowProps(),
              onMouseLeave: (e: any) => {
                this.handleOnRowBlur(e, rowValue, rowIndex);
              },
              onDoubleClick: (e: any) => {
                this.handleRowDoubleClick(e, rowValue, rowIndex);
              },
              onTouchStart: (e: any) => {
                this.timeOutId = setTimeout(() => {
                  this.handleRowDoubleClick(e, rowValue, rowIndex);
                }, 500);
              },
              onTouchEnd: (e: any) => {
                clearTimeout(this.timeOutId);
              },
              onClickCapture: (e: any) => {
                this.handleRowClick(e, rowValue, rowIndex);
              },
              onMouseDownCapture: () => {
                this.recordRowSelectionContext(rowValue, rowIndex);
              },
              editMode: this.props.editableAlwaysActive ? true : this.state.activeEditableRowIndex == rowIndex,
              style: this.getRowStyle(rowValue, rowIndex),
              hoverFontColor: this.props.rowHoverFontColor,
              transformedOnMobileResolution: this.props.transformedOnMobileResolution,
              hideHeaderOnMobileResolution: this.props.hideHeaderOnMobileResolution,
              hoverBgColor: this.props.rowHoverBgColor,
              selectedBgColor: this.props.selectedRowColor,
              isSelected: this.state.selectedRowKeys?.some(
                (k) =>
                  k?.toString()?.toLowerCase() === rowValue[this.state.actualRowKey || "id"]?.toString()?.toLowerCase()
              ),
              tableClassName: this.getClassName().trim(),
              showTableHeader: this.props.showHeader,
              desktopColumnsCount: (this.props as GalleryItemProps).desktopColumnsCount,
              tabletColumnsCount: (this.props as GalleryItemProps).tabletColumnsCount,
              phoneColumnsCount: (this.props as GalleryItemProps).phoneColumnsCount,
              mode: this.props.mode,
              rowData: rowValue,
              rowIndex,
              isInsertRow: this.isInsertRow(rowValue),
              isInsertRowActive: this.state.activeEditableRowIndex === 0,
              isEditableAlwaysActive: this.props.editableAlwaysActive,
              handleAddRow: () => {
                if (!this.props.onRowEditFinished && !this.props.onInsertRowFinished) {
                  return;
                }
                if (this.props.editableAlwaysActive && this.props.insertRowActive) {
                  // Without row data handleOnRowEditFinished cannot resolve the insert row in
                  // editableAlwaysActive mode (there is no active row index to fall back to).
                  this.commitInsertRow();
                } else {
                  this.handleOnRowEditFinished();
                }
              },
              handleCancelEditing: () => {
                this.setState({ activeEditableRowIndex: -1 } as any);
              },
              onClick: () => this.resetEditableRow,
              rowComponent: this.getRowComponent(),
              lat: this.getRowLat(rowValue),
              lng: this.getRowLng(rowValue),
              dataLatField: this.props?.dataLatField,
              dataLngField: this.props?.dataLngField,
              waypointLatField: this.props?.waypointLatField,
              waypointLngField: this.props?.waypointLngField,
              marker: {
                lat: this.getRowLat(rowValue),
                lng: this.getRowLng(rowValue)
              },
              rowStyle: this.getRowStyle(rowValue, rowIndex),
              value: this.state.value,
              isDataMarkerSelected: this.state.isDataMarkerSelected,
              onClickDesign: (designClicked: boolean) => {
                if (this.props.onClickDesign) {
                  this.props.onClickDesign(designClicked);
                }
              },
              openDrawer: (content?: any) => {
                if (content !== undefined) {
                  if (this.refreshGoogleMapsBodyWrapper) {
                    this.refreshGoogleMapsBodyWrapper({
                      activeContent: content
                    });
                  }
                }
              },
              designClicked: (this.props.designClicked as any) === "" ? true : this.props.designClicked,
              markerDetailView: this.props.markerDetailView,
              position: this.props.markerDetailPosition,
              height: this.props.markerDetailHeight,
              width: this.props.markerDetailWidth,
              selectedLocationIcon: this.props.selectedLocationIcon,
              selectedLocationColor: this.props.selectedLocationColor
            })}
            onHeaderRow={() => ({
              style: this.state.headerStyle,
              mode: this.props.mode,
              onClick: this.resetEditableRow
            })}
            columns={BaseTable.orderColumnsByFixedEdge([
              ...(this.props.showRowNumbers ? [this.getRowNumberColumn(hasFixedLeftColumn)] : []),
              ...(this.state.columnGroups
                ?.map((group, groupIndex) => {
                  const visibleChildren = group.children.filter(
                    (child) => !this.state.hiddenColumnList?.some((id) => id.toLowerCase() === child.id.toLowerCase())
                  );

                  if (visibleChildren.length === 0) {
                    return null;
                  }

                  const groupFixedEdge: FixedEdge = BaseTable.resolveGroupFixedEdge(visibleChildren);

                  return {
                    title: group.title,
                    children: visibleChildren?.map((child, childIndex) => {
                      (child as any).sortOrder =
                        this.state.sorterOptions?.field === (child as any).dataValue &&
                        this.state.sorterOptions?.columnId === child.id
                          ? this.state.sorterOptions?.order
                          : undefined;
                      return this.createColumnConfig(child, `group-${groupIndex}-${childIndex}`, true);
                    }),
                    style: {
                      ...group.style,
                      borderTop: `1px solid ${this.getGroupBorderColor()}`,
                      borderBottom: `1px solid ${this.getGroupBorderColor()}`,
                      borderRight: `1px solid ${this.getGroupBorderColor()}`,
                      borderLeft: `1px solid ${this.getGroupBorderColor()}`,
                      backgroundColor: group.groupBackgroundColor || group.style?.backgroundColor,
                      color: group.groupFontColor || group.style?.color,
                      fontSize: group.groupFontSize ? `${group.groupFontSize}px` : undefined,
                      fontWeight: group.groupFontWeight ? BaseTable.mapFontWeight(group.groupFontWeight) : undefined
                    },
                    hasGrouping: true,
                    fixed: groupFixedEdge,
                    onHeaderCell: () => ({
                      style: {
                        ...group.style,
                        borderTop: `1px solid ${this.getGroupBorderColor()}`,
                        borderBottom: `1px solid ${this.getGroupBorderColor()}`,
                        borderRight: `1px solid ${this.getGroupBorderColor()}`,
                        borderLeft: `1px solid ${this.getGroupBorderColor()}`,
                        backgroundColor: group.groupBackgroundColor || group.style?.backgroundColor,
                        color: group.groupFontColor || group.style?.color,
                        fontSize: group.groupFontSize ? `${group.groupFontSize}px` : undefined,
                        fontWeight: group.groupFontWeight ? BaseTable.mapFontWeight(group.groupFontWeight) : undefined
                      },
                      hasGrouping: true,
                      isGroupCell: true,
                      // The rendered-column identity the pinned-offset CSS targets.
                      index: `group-${groupIndex}`,
                      groupBackgroundColor: group.groupBackgroundColor,
                      groupFontColor: group.groupFontColor,
                      groupFontSize: group.groupFontSize,
                      groupFontWeight: group.groupFontWeight,
                      fixed: groupFixedEdge,
                      pinnedCellShadow:
                        groupFixedEdge && this.isHeaderInBodyTable()
                          ? BaseTable.buildStickyBorderShadow(
                              { borderWidth: 1, borderColor: this.getGroupBorderColor() },
                              true
                            )
                          : undefined
                    })
                  };
                })
                .filter(Boolean) || []),
              ...this.state.columns
                ?.filter(
                  (c: any) =>
                    c.visibility !== "hidden" &&
                    !this.state.hiddenColumnList?.some((id) => id.toLowerCase() === c.id.toLowerCase()) &&
                    !c.isGroup
                )
                .map((column: any, columnIndex: number) => {
                  const { columns } = this.state;
                  column.sortOrder =
                    this.state.sorterOptions?.field === column.dataValue &&
                    this.state.sorterOptions?.columnId === column.id
                      ? this.state.sorterOptions?.order
                      : undefined;
                  if (this.props.tableWidthMode === "scroll" || this.props.tableWidthMode === "horizontal scroll") {
                    column.width = column.style.width ?? "min-content";
                    column.style.width = column.width;
                    column.style.minWidth = column.style.width;
                  }
                  if (this.state.columnWidths && this.state.columnWidths[columnIndex] !== undefined) {
                    column.width = this.state.columnWidths[columnIndex];
                    column.style.width = column.width;
                  }
                  return this.createColumnConfig(column, columnIndex, false);
                })
            ])}
            loading={this.props.loading}
            size={this.props.size}
            bordered={false}
            scroll={
              this.hasHorizontalOverflowMode()
                ? {
                    x: "max-content",
                    y:
                      this.props.fixedHeader && hasFixedColumns
                        ? (() => {
                            const containerHeight = this.props.style?.height || this.props.style?.maxHeight;
                            if (containerHeight) {
                              const heightValue =
                                typeof containerHeight === "string"
                                  ? parseInt(containerHeight.replace(/px|%|em|rem/g, ""), 10)
                                  : containerHeight;
                              return Math.max(200, heightValue - (this.props.pagination ? 80 : 40));
                            }
                            return 400;
                          })()
                        : undefined
                  }
                : this.props.fixedHeader && hasFixedColumns
                  ? {
                      y: (() => {
                        const containerHeight = this.props.style?.height || this.props.style?.maxHeight;
                        if (containerHeight) {
                          const heightValue =
                            typeof containerHeight === "string"
                              ? parseInt(containerHeight.replace(/px|%|em|rem/g, ""), 10)
                              : containerHeight;
                          return Math.max(200, heightValue - (this.props.pagination ? 80 : 40));
                        }
                        return 400;
                      })()
                    }
                  : undefined
            }
            rowKey={this.getRowRenderKey}
            rowSelection={
              this.props.multiSelect
                ? {
                    type: "checkbox",
                    selectedRowKeys: this.getSelectedRowKeysForAntd(),
                    onSelect: (record, selected, selectedRows) => {
                      const { selectedRowKeys } = this.state;
                      const actualRowKey = this.state.actualRowKey || "id";
                      const recordValue = record[actualRowKey];

                      const updatedRowKeys = selected
                        ? [...selectedRowKeys, recordValue]
                        : selectedRowKeys.filter(
                            (key) => key?.toString()?.toLowerCase() !== recordValue?.toString()?.toLowerCase()
                          );

                      const updatedRowKeysString = updatedRowKeys.length > 0 ? updatedRowKeys.join(",") : "";

                      this.persistNestedSelection(updatedRowKeysString);
                      this.setState(
                        {
                          selectedRowKeys: updatedRowKeys,
                          selectedRowKeysString: updatedRowKeysString
                        },
                        () => {
                          this.props.onSelectedValuesChange?.();
                        }
                      );
                    },
                    onSelectAll: (selected, selectedRows, changeRows) => {
                      const { multiSelectScope, dataSource, onSelectedValuesChange } = this.props;
                      const actualRowKey = this.state.actualRowKey || "id";

                      const getRecordValue = (row) => {
                        return row[actualRowKey];
                      };

                      const updateState = (keys, keysString) => {
                        this.persistNestedSelection(keysString);
                        this.setState(
                          {
                            selectedRowKeys: keys,
                            selectedRowKeysString: keysString
                          },
                          () => {
                            if (onSelectedValuesChange) {
                              onSelectedValuesChange();
                            }
                          }
                        );
                      };

                      const generateKeysString = (rows) =>
                        rows.length > 0 ? rows.map((row) => getRecordValue(row)).join(",") : "";

                      if (multiSelectScope === "all") {
                        const allRowKeys = dataSource.map((row) => getRecordValue(row));
                        const allRowKeysString = generateKeysString(dataSource);

                        if (selectedRows.length - 1 !== dataSource.length && selected) {
                          updateState(allRowKeys, allRowKeysString);
                        } else {
                          updateState([], "");
                        }
                      } else {
                        const selectedRowKeys = selectedRows
                          ?.filter((row) => row !== undefined)
                          ?.map((row) => getRecordValue(row));
                        const selectedRowKeysString = generateKeysString(selectedRows);

                        updateState(selectedRowKeys, selectedRowKeysString);
                      }
                    }
                  }
                : undefined
            }
          />

          {this.shouldPinHorizontalScrollbar() && (
            <div
              className="kuika-table-pinned-hscroll"
              ref={this.handlePinnedScrollbarRef}
              onScroll={this.handlePinnedScrollbarScroll}
            >
              <div className="kuika-table-pinned-hscroll-spacer" ref={this.handlePinnedScrollbarSpacerRef} />
            </div>
          )}
          <input type="hidden" id={this.state.selectedIndexGuid} className="selected-index-guid"></input>
          <input type="hidden" id={this.state.selectedColumnIndexGuid} className="selected-column-index-guid"></input>
          <input type="hidden" id={this.state.selectedRowDataGuid} className="selected-row-data-guid"></input>
        </div>
      </>
    );
  };

  createColumnConfig = (column: any, columnIndex: any, isGroup: boolean) => {
    const { columns } = this.state;
    const hasGrouping = this.state.columnGroups?.length > 0 || columns?.some((col) => col.group);

    const isHidden = this.state.hiddenColumnList?.some((id) => id.toLowerCase() === column.id.toLowerCase());
    const fixed = isHidden ? undefined : column.fixed;

    // Computed once per column rather than per rendered cell. The value is published as the
    // cell's --kuika-pinned-shadow custom property and only applied while that edge is
    // scrolled away from — see buildStickyBorderShadow and getPinnedCellBorderCss.
    const pinnedCellShadow: string | undefined = fixed
      ? BaseTable.composePinnedCellShadow(
          column.style?.boxShadow,
          BaseTable.buildStickyBorderShadow(column.style, !BaseTable.rowStylePaintsSeparators(this.state.rowStyle))
        )
      : undefined;
    const pinnedHeaderCellShadow: string | undefined =
      fixed && this.isHeaderInBodyTable()
        ? BaseTable.composePinnedCellShadow(
            this.state.headerStyle?.boxShadow,
            BaseTable.buildStickyBorderShadow(this.state.headerStyle, true)
          )
        : undefined;

    // No `left`/`right` is handed to the cells: applyPinnedColumnOffsets publishes the sticky
    // offsets as CSS keyed by column identity instead. Computing them per cell from a width
    // cache keyed by rendered column index is what desynced pinned columns and grouped header
    // titles once groups, hidden columns or a row-number column shifted those indices
    // (KOA-17746).
    return {
      ...column,
      fixed,
      onCell: (rowData: any, rowIndex?: number): any => ({
        onCellClick: (_e: any, _columnIndex: number, _rowData: any, _rowIndex: number) => {
          this.handleCellClick(_e, _columnIndex || columnIndex, _rowData || rowData, _rowIndex || rowIndex);
        },
        onCellBlur: (_e: any) => {
          this.handleCellBlur(_e, rowIndex, columnIndex);
        },
        style: column.style,
        calculatedCellBgColor: column.calculatedCellBgColor,
        calculatedCellFontColor: column.calculatedCellFontColor,
        alwaysVisibleOnMobileResolution: column.alwaysVisibleOnMobileResolution,
        rowIndex,
        rowData,
        columnIndex,
        title: column.title,
        columns: columns?.filter((x: any) => x.visible === true),
        mode: this.props.mode,
        isEditable: column.isEditableColumn,
        isEditMode:
          (column.isEditableColumn && this.state.activeEditableRowIndex == rowIndex) ||
          (this.props.editableAlwaysActive && column.isEditableColumn),
        editableAlwaysActive: this.props.editableAlwaysActive,
        dataValue: column.dataValue,
        dataType: column.dataType ?? ValueType.String,
        setResizingColumnIndex: this.setResizingColumnIndex,
        resizeListener: this.resizeListener,
        index: columnIndex,
        resizableColumns: this.props.resizableColumns,
        tableId: this.props.id,
        fixed: fixed || undefined,
        pinnedCellShadow,
        [BaseTable.EXPLICIT_WIDTH_ATTRIBUTE]: BaseTable.hasExplicitColumnWidth(column) || undefined
      }),
      onHeaderCell: (): any => ({
        style: this.state.headerStyle,
        columnStyle: column.style,
        headerStyle: this.state.headerStyle,
        title: column.title,
        secondTitle: column.secondTitle,
        mode: this.props.mode,
        textDirection: this.props.children[0]?.props?.textDirection,
        columnSearchText: this.state.columnSearchTexts,
        setColumnSearchText: this.setColumnSearchTexts,
        onHeaderSearchBlur: this.onHeaderSearchBlur,
        searchable: this.props.searchable,
        type: column.dataType ?? ValueType.String,
        dataValue: column.dataValue,
        index: columnIndex,
        resizeListener: this.resizeListener,
        setResizingColumnIndex: this.setResizingColumnIndex,
        resizableColumns: this.props.resizableColumns,
        hidden: this.state.hiddenColumnList.includes(column.id),
        isSearchHidden: column.hideSearch,
        isFilteringHidden: column.hideFiltering,
        isSortingHidden: column.hideSorting,
        tableFiltering: this.props.filtering,
        tableSorting: this.props.sorter,
        hasGrouping,
        shouldSearchOnChange: this.props.shouldSearchOnChange,
        searchDelay: this.props.searchDelay,
        isGroupCell: isGroup,
        titleBgColor: column.titleBgColor,
        placeholder: column.placeholder,
        fixed: fixed || undefined,
        pinnedCellShadow: pinnedHeaderCellShadow,
        filteredValue: column.filteredValue,
        groupBackgroundColor: undefined,
        groupFontColor: undefined,
        groupFontSize: undefined,
        groupFontWeight: undefined,
        isFirstColumn: columnIndex === 0,
        isLastColumn: columnIndex === columns.length - 1,
        dateSearchFormat: column.dateSearchFormat,
        tableBgColor: this.props.style?.backgroundColor,
        getContrastTextColor: this.getContrastColor
      })
    };
  };

  private static readonly ROW_NUMBER_COLUMN_KEY: string = "__kuika_row_number__";

  private getRowNumberColumn = (pinLeft: boolean) => {
    const { current = 1, pageSize = 10 } = this.state;
    const paginationEnabled = this.props.pagination === true;
    const offset = paginationEnabled ? (current - 1) * pageSize : 0;

    return {
      title: "#",
      dataIndex: BaseTable.ROW_NUMBER_COLUMN_KEY,
      key: BaseTable.ROW_NUMBER_COLUMN_KEY,
      width: 50,
      fixed: pinLeft ? ("left" as const) : undefined,
      sorter: false,
      render: (_: any, _record: any, rowIndex: number) => offset + rowIndex + 1,
      // `index` is the column identity the width sync pairs header and body cells by
      // (BaseTable.getColumnKey). Without it this column is invisible to the sync and, in the
      // auto-layout split header, collapses to the width of the "#" glyph while the body keeps
      // its column width — pushing every following header column out of line (KOA-17746).
      onHeaderCell: (): any => ({
        style: { ...this.state.headerStyle, textAlign: "center", padding: "8px 4px" },
        isRowNumberColumn: true,
        index: BaseTable.ROW_NUMBER_COLUMN_KEY
      }),
      onCell: (): any => ({
        style: { textAlign: "center", padding: "8px 4px" },
        isRowNumberColumn: true,
        index: BaseTable.ROW_NUMBER_COLUMN_KEY,
        [BaseTable.EXPLICIT_WIDTH_ATTRIBUTE]: true
      })
    };
  };

  requestSyncColumnWidths = () => {
    if (this.syncWidthsTimerId !== undefined) {
      clearTimeout(this.syncWidthsTimerId);
    }
    this.syncWidthsTimerId = setTimeout(() => {
      this.syncWidthsTimerId = undefined;
      this.syncColumnWidths();
      this.applyPinnedColumnOffsets();
      this.refreshPinnedEdgeState();
    }, 100);
  };

  // Measures the pinned columns of the rendered row and publishes their sticky offsets as
  // CSS. See computePinnedOffsets for why antd cannot supply them here.
  applyPinnedColumnOffsets = (): void => {
    const className: string = this.getClassName().trim();
    if (!className) {
      return;
    }
    const wrapper: HTMLElement | null = this.getWrapperElement();
    const row: HTMLElement | null = wrapper?.querySelector<HTMLElement>("tbody tr:not(.ant-table-measure-row)") ?? null;
    if (!row) {
      this.pinnedOffsetStyle.setDynamicCss("");
      return;
    }

    const cells: HTMLTableCellElement[] = BaseTable.getLeafCells(
      Array.from(row.querySelectorAll("td")) as HTMLTableCellElement[]
    );
    const measurements: PinnedCellMeasurement[] = cells.map((cell: HTMLTableCellElement) => {
      const cn: string = cell.className || "";
      let edge: FixedEdge;
      if (cn.includes("ant-table-cell-fix-left")) {
        edge = "left";
      } else if (cn.includes("ant-table-cell-fix-right")) {
        edge = "right";
      }
      return { key: BaseTable.getColumnKey(cell), edge, width: cell.getBoundingClientRect().width };
    });

    const leafOffsets: PinnedColumnOffset[] = BaseTable.computePinnedOffsets(measurements);
    const groupOffsets: PinnedColumnOffset[] = BaseTable.computeGroupPinnedOffsets(
      this.getGroupChildKeys(),
      leafOffsets
    );
    this.pinnedOffsetStyle.setDynamicCss(BaseTable.buildPinnedOffsetCss(className, [...leafOffsets, ...groupOffsets]));
  };

  // The rendered-column keys of each header group's visible children, in render order —
  // the same `group-<groupIndex>-<childIndex>` identities createColumnConfig stamps on the
  // cells.
  private getGroupChildKeys = (): GroupChildKeys[] => {
    return (this.state.columnGroups ?? []).map((group, groupIndex: number) => {
      const visibleChildren = group.children.filter(
        (child) => !this.state.hiddenColumnList?.some((id) => id.toLowerCase() === child.id.toLowerCase())
      );
      return {
        key: `group-${groupIndex}`,
        childKeys: visibleChildren.map((_child, childIndex: number) => `group-${groupIndex}-${childIndex}`)
      };
    });
  };

  // antd recomputes which edge is currently scrolled away from (its `ping` classes, which
  // gate the pinned-cell borders below) only when the body fires a scroll event. It
  // evaluates once at mount — before the column widths are applied — so a table that only
  // overflows afterwards stays "not pinged" and its right-pinned column renders without
  // borders until the user scrolls. A synthetic scroll event re-runs that evaluation.
  refreshPinnedEdgeState = (): void => {
    const wrapper: HTMLElement | null = this.getWrapperElement();
    const scrollContainer: HTMLElement | null = wrapper ? this.getHorizontalScrollContainer(wrapper) : null;
    scrollContainer?.dispatchEvent(new Event("scroll"));
  };

  // The leaf cells of one row: every rendered column, minus the grouped header cells
  // (colspan > 1) that merely span their children and antd's scrollbar filler / no-data
  // placeholder.
  private static getLeafCells(cells: HTMLTableCellElement[]): HTMLTableCellElement[] {
    return BaseTable.filterScrollbarCells(cells).filter(
      (cell) => parseInt(cell.getAttribute("colspan") || "1", 10) <= 1
    );
  }

  // The rendered-column identity of a cell, shared by its header cell and all of its body
  // cells: `group-<groupIndex>-<childIndex>` for a grouped column, the rendered index for a
  // plain one, and a stable token for the columns antd itself injects (selection checkbox,
  // expand icon) which carry no index of their own.
  //
  // Header and body cells are paired through this key rather than through their position in
  // the DOM: a grouped `<thead>` is two-dimensional — an ungrouped column renders in the
  // *first* header row with rowspan=2 while grouped leaves render in the second — so DOM
  // order across the header rows is not visual column order. Pairing by position applied each
  // measured width to the wrong header column as soon as a table mixed grouped and ungrouped
  // columns, which drifted the header away from the body and made the group titles overlap
  // (KOA-17746).
  private static getColumnKey(cell: HTMLTableCellElement): string | null {
    const rendered: string | null = cell.getAttribute("columnindex") ?? cell.getAttribute("index");
    if (rendered !== null) {
      return rendered;
    }
    const className: string = cell.className || "";
    if (className.includes("ant-table-selection-column")) {
      return "ant-table-selection-column";
    }
    if (className.includes("ant-table-row-expand-icon-cell")) {
      return "ant-table-row-expand-icon-cell";
    }
    return null;
  }

  private static filterScrollbarCells(cells: HTMLTableCellElement[]): HTMLTableCellElement[] {
    return cells.filter((cell) => {
      const cn = cell.className || "";
      return (
        !cn.includes("ant-table-cell-scrollbar") && !cn.includes("scrollbar") && !cn.includes("ant-table-placeholder")
      );
    });
  }

  // True when any header cell's rendered width no longer matches its body cell (>1px).
  // A fixed header keeps the column headings and the scrollable body in two separate
  // <table>s, so when they diverge the headings visibly overlap. `headerCells` and
  // `bodyCells` are expected to be the same length and in matching column order; callers
  // must read this in the live (tableLayout="fixed") state, before relaxing the layout.
  static columnsMisaligned(headerCells: HTMLTableCellElement[], bodyCells: HTMLTableCellElement[]): boolean {
    const count: number = Math.min(headerCells.length, bodyCells.length);
    for (let i = 0; i < count; i++) {
      const headerWidth: number = headerCells[i].getBoundingClientRect().width;
      const bodyWidth: number = bodyCells[i].getBoundingClientRect().width;
      if (Math.abs(headerWidth - bodyWidth) > 1) {
        return true;
      }
    }
    return false;
  }

  syncColumnWidths = () => {
    const hasVisibleFixedColumns = this.state.columns?.some(
      (col: any) =>
        (col.fixed === "left" || col.fixed === "right") &&
        col.visibility !== "hidden" &&
        !this.state.hiddenColumnList?.some((id) => id.toLowerCase() === col.id.toLowerCase())
    );

    if (!this.props.fixedHeader || !hasVisibleFixedColumns) {
      return;
    }

    const tableWrapper = document.getElementById(this.props.id || `kuika_${(this.props as any)?.kuikaid}`);
    if (!tableWrapper) return;

    const headerTable = tableWrapper.querySelector(".ant-table-header table") as HTMLTableElement;
    const bodyTable = tableWrapper.querySelector(".ant-table-body table") as HTMLTableElement;

    if (!headerTable || !bodyTable) return;

    try {
      const allHeaderCells = Array.from(headerTable.querySelectorAll("thead tr th")) as HTMLTableCellElement[];
      const headerCellsByColumn = new Map<string, HTMLTableCellElement>();
      BaseTable.getLeafCells(allHeaderCells).forEach((cell: HTMLTableCellElement) => {
        const columnKey: string | null = BaseTable.getColumnKey(cell);
        if (columnKey !== null && !headerCellsByColumn.has(columnKey)) {
          headerCellsByColumn.set(columnKey, cell);
        }
      });

      const bodyRows = Array.from(bodyTable.querySelectorAll("tbody tr")) as HTMLTableRowElement[];

      if (headerCellsByColumn.size === 0 || bodyRows.length === 0) return;

      // Prefer the second row (the first can be a measurement/editable ghost row),
      // but fall back to the first so a single-row table still syncs instead of
      // throwing and leaving header/body widths unaligned.
      const firstBodyRow = bodyRows[1] ?? bodyRows[0];
      const allBodyCells = Array.from(firstBodyRow.querySelectorAll("td")) as HTMLTableCellElement[];

      // Body cells drive the order (a body row IS visual column order) and each one pulls in
      // its header counterpart by column key, so headerCells[i] and bodyCells[i] are always
      // the same column.
      const headerCells: HTMLTableCellElement[] = [];
      const bodyCells: HTMLTableCellElement[] = [];
      BaseTable.getLeafCells(allBodyCells).forEach((bodyCell: HTMLTableCellElement) => {
        const columnKey: string | null = BaseTable.getColumnKey(bodyCell);
        const headerCell: HTMLTableCellElement | undefined =
          columnKey === null ? undefined : headerCellsByColumn.get(columnKey);
        if (headerCell) {
          headerCells.push(headerCell);
          bodyCells.push(bodyCell);
        }
      });

      if (bodyCells.length === 0) return;

      // Read whether the currently-rendered header/body columns are already misaligned.
      // This must happen before the tableLayout="auto" toggle below, which relaxes both
      // tables to their inline/content widths and would mask the divergence. Used to
      // defeat the cache short-circuit when an external re-render has desynced the
      // columns (KOA-17505).
      const fixedColumnsMisaligned: boolean = BaseTable.columnsMisaligned(headerCells, bodyCells);

      headerTable.style.tableLayout = "auto";
      bodyTable.style.tableLayout = "auto";

      // Batch all reads
      const cellWidths: number[] = [];
      bodyCells.forEach((cell, index) => {
        const width = cell.getBoundingClientRect().width;
        cellWidths[index] = BaseTable.resolveSyncedColumnWidth(width, BaseTable.cellHasExplicitWidth(cell));
      });

      // Build column key → width mapping and check for changes
      const newWidths = new Map<string, number>();
      bodyCells.forEach((cell, index) => {
        const columnKey: string | null = BaseTable.getColumnKey(cell);
        if (columnKey !== null && cellWidths[index]) {
          newWidths.set(columnKey, cellWidths[index]);
        }
      });

      // Skip the re-apply only when the measured widths are unchanged AND the rendered
      // fixed header/body columns are still aligned. An external re-render (e.g. opening
      // a Modal, Drawer or Float Panel over the table) makes antd rebuild the table and
      // reset its <colgroup>, desyncing the separate fixed header and body tables even
      // though the body cells keep their inline widths — so a pure "measured == cache"
      // check would short-circuit and leave the columns overlapping (KOA-17505). The
      // misalignment is read before the tableLayout="auto" toggle above relaxes it.
      if (
        !fixedColumnsMisaligned &&
        newWidths.size === this.cachedColumnWidths.size &&
        ![...newWidths].some(([k, v]) => this.cachedColumnWidths.get(k) !== v)
      ) {
        headerTable.style.tableLayout = "fixed";
        bodyTable.style.tableLayout = "fixed";
        return;
      }

      this.cachedColumnWidths = newWidths;

      // Batch all writes
      headerCells.forEach((headerCell, index) => {
        if (cellWidths[index]) {
          headerCell.style.width = `${cellWidths[index]}px`;
          headerCell.style.minWidth = `${cellWidths[index]}px`;
          headerCell.style.maxWidth = `${cellWidths[index]}px`;
        }
      });

      bodyCells.forEach((bodyCell, index) => {
        if (cellWidths[index]) {
          bodyCell.style.width = `${cellWidths[index]}px`;
          bodyCell.style.minWidth = `${cellWidths[index]}px`;
          bodyCell.style.maxWidth = `${cellWidths[index]}px`;
        }
      });

      // Every other row gets the same widths, looked up by column key so a row whose cell
      // set differs (an editable/insert row, a colspan placeholder) cannot shift them.
      bodyRows.forEach((row, rowIndex) => {
        if (rowIndex === 1) return;

        const allCells = Array.from(row.querySelectorAll("td")) as HTMLTableCellElement[];
        BaseTable.getLeafCells(allCells).forEach((cell: HTMLTableCellElement) => {
          const columnKey: string | null = BaseTable.getColumnKey(cell);
          const width: number | undefined = columnKey === null ? undefined : newWidths.get(columnKey);
          if (width) {
            cell.style.width = `${width}px`;
            cell.style.minWidth = `${width}px`;
            cell.style.maxWidth = `${width}px`;
          }
        });
      });

      const headerColgroup = headerTable.querySelector("colgroup");
      const bodyColgroup = bodyTable.querySelector("colgroup");

      if (headerColgroup && bodyColgroup) {
        const headerCols = Array.from(headerColgroup.querySelectorAll("col"));
        const bodyCols = Array.from(bodyColgroup.querySelectorAll("col"));

        // colgroup <col>s are in visual column order (leaf-only). Use body row tds in
        // DOM order — which is also visual column order — to read each column's natural
        // width. Indexing into `allHeaderCells` was wrong when grouped headers existed,
        // because allHeaderCells interleaves the group-header row and the leaf-header
        // row, so headerCols[i] did not correspond to allHeaderCells[i]. That caused
        // each leaf column's colgroup width to be taken from the wrong column,
        // shifting parent group headers (with colspan) relative to their children.
        const visualBodyCells = BaseTable.filterScrollbarCells(allBodyCells);
        const colCount = Math.min(headerCols.length, bodyCols.length, visualBodyCells.length);
        for (let i = 0; i < colCount; i++) {
          const visualCell = visualBodyCells[i];
          const width = BaseTable.resolveSyncedColumnWidth(
            visualCell.getBoundingClientRect().width,
            BaseTable.cellHasExplicitWidth(visualCell)
          );
          headerCols[i].style.width = `${width}px`;
          bodyCols[i].style.width = `${width}px`;
        }
      }

      headerTable.style.tableLayout = "fixed";
      bodyTable.style.tableLayout = "fixed";

      const totalWidth = cellWidths.reduce((sum, width) => sum + width, 0);
      if (totalWidth > 0) {
        headerTable.style.minWidth = `${totalWidth}px`;
        bodyTable.style.minWidth = `${totalWidth}px`;
      }
    } catch (error) {
      console.warn("Error syncing column widths:", error);
    }
  };
}
